import { publishOrderDataChange } from "../utils/orderDataSync.js";
import { confirmedTractorAssignment, tractorAssignmentChallenge, tractorAssignmentConfirmation, trailerChangeConfirmation } from "../lib/tractorAssignmentsApi.js";
import { driverTourStartedChallenge, driverTourStartedConfirmation, startedTourRestoreChallenge, startedTourRestoreConfirmation } from "../lib/driverAssignmentsApi.js";
import { createDeliveryGroupCapacitySplitChallengeHandler } from "../utils/deliveryGroupCapacitySplit.js";
import { COMPARTMENT_SHARING_STALE_CODE, createCompartmentSharingChallengeHandler } from "../utils/compartmentSharingChallenge.js";
import { useEffect, useRef, useState } from "react";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import {
  buildPlanningAlertCaptureContext,
  extractPlanningValidationErrors,
  buildStatusNoticeAlertCaptureContext,
} from "../lib/dispatchAlertCaptureContext.js";
import { useToast } from "../components/ResultsPrimitives.jsx";
import { withDispatchGeotabCheck } from "../components/resultsPageChrome/DispatchGeotabCheck.jsx";
import { TRUCK_OPTIMIZATION_PROGRESS_STEPS, createToastProgress, createToastProgressReporter } from "../utils/toastProgress.js";
import { invalidatePageDataCacheEntries, loadPageData, writePageDataCacheEntry } from "./usePageData.js";
import { invalidateShellDataCache } from "./useShellData.js";
import { useOptimizationJobTracker } from "./useOptimizationJobTracker.js";
import { clearAppStatusNotice, dispatchAppStatusNotice, getAppStatusNoticeSnapshot } from "../utils/appStatusNotice.js";
import { launchOptimizationViaApi } from "../utils/optimizationLaunch.js";
import { dispatchPlanningStateChange } from "../utils/planningCrossViewSync.js";
import { PLANNING_UPDATE_BUSY_MESSAGE, tryBeginPlanningMutationActivity } from "../utils/planningMutationActivity.js";
import { isCoherentPlanningResult, isDeliveryMovePayloadSynced, isPortalOnlyPlanningMutationResult, requiresSavedPlanningViewForMutation } from "../utils/planningMutationPayloadSync.js";
import {
  buildPlanningRecoveryFeedback,
  recoverPlanningMutation,
  resolvePlanningResponseCorrelationId,
  resolveUnansweredPlanningRequest,
  shouldRecoverPlanningMutation,
} from "../utils/planningMutationRecovery.js";
import {
  buildPlanningPerformanceCorrelationHeaders,
  createPerformanceCorrelationId,
  getPerformanceTabId,
} from "../utils/sourceChangesRefreshPolicy.js";
import {
  DISPATCH_REASSIGNMENT_MODE_COMPLETE,
  DISPATCH_REASSIGNMENT_MODE_FAST,
  buildDispatchProtectAfterReassignmentMutationFields,
  buildDispatchReassignmentMutationModeFields,
  normalizeDispatchProtectAfterReassignment,
  normalizeDispatchReassignmentMode,
} from "../utils/dispatchReassignmentMode.js";
import {
  buildEndOfDayLoadTargetSuspensionDialog,
  END_OF_DAY_LOAD_TARGET_SUSPENSION_CODE,
} from "../utils/endOfDayLoadTargetSuspension.js";
import {
  buildPortalPublicationInvalidationToast,
  buildPortalPublicationMutationBody,
  buildPortalPublicationMutationUrl,
  buildPortalPublicationSelectionSummary,
  normalizePortalPublicationInvalidatedTruckIds,
} from "../lib/portalPublicationsApi.js";
import {
  getAssignmentDisplayedWorkloadMinutes,
  getAssignmentMaxWorkMinutes,
  getAssignmentOvertimeState,
  getTargetedReactivateOvertimeState,
  getTruckCompatibilityTimeWindowMode,
  isAssignmentOvertimeAuthorized,
  getDeliveryHighlightKey,
  getLoadingHighlightKey,
  getLoadingOrderHighlightKey,
  syncClientSelectionState,
} from "../utils/resultsFleetData.js";
import { formatDurationShort } from "../utils/routeModelsData.js";
import { formatPlanningDiagnosticActionLabel } from "../utils/planningLastDiagnostics.js";
import { buildOrderEditFeedback, normalizeOrderEditEvidence, GANTT_ORDER_EDIT_ACTION } from "../utils/planningOrderEditDiagnostics.js";
import { createMutationTransportDiagnostic } from "../utils/mutationTransportDiagnostics.js";
import { planningRevisionForScope, planningRevisionTimeline, recordPlanningRevisionResponse, recordPlanningRevisionIgnored } from "../utils/planningRevisionDiagnostics.js";
import { createOperationFeedbackCapture } from "../utils/operationFeedbackCapture.js";
import {
  buildDispatchDayTransitionConfirmationContent,
  addPendingPlanningMutation,
  buildClearTruckPreloadTimeConfirmationContent,
  buildDeliveryMoveConfirmationContent,
  buildTruckBulkReassignmentConfirmationContent,
  buildTruckDompeurMixedLoadConfirmationContent,
  buildTruckDispatchDisabledConfirmationContent,
  buildOrderProtectionConfirmationContent,
  buildTruckOvertimeConfirmationContent,
  buildTruckOrderProtectionConfirmationContent,
  buildTruckPreloadTimeConfirmationContent,
  buildTruckProtectionConfirmationContent,
  buildTruckRouteOptimizationConfirmationContent,
  formatTruckPreloadTimeLabel,
  normalizeTruckPreloadTimeValue,
  resolveDeliveryMoveRejectedMessage,
  resolvePlanningMutationOperatorErrorMessage,
  resolveDeliverySuspendRejectedMessage,
  buildDeliveryReactivateConfirmationContent,
  buildDeliveryReleaseConfirmationContent,
  buildDeliveryUnconfirmConfirmationContent,
  buildDeliveryFreezeConfirmationContent,
  buildDeliveryUnfreezeConfirmationContent,
  buildErrorSuspendConfirmationContent,
  buildErrorSuspendGroupConfirmationContent,
  buildDeliveryReassignmentToast,
  buildDeliverySuspendConfirmationContent,
  buildPlanningResetConfirmationContent,
  captureLastReassignmentDiagnosticPayload,
  clearPendingPlanningMutation,
  hasPendingPlanningMutation,
  preserveOperationalFreshnessForProtectionRemoval,
  revealAssignmentCard,
  resolveDeliveryGroupLinkedLoadingTransition,
  resolveDeliveryLinkedLoadingTransition,
  resolveLoadingActivationTransition,
  resolvePlanningMutationHighlightKeys,
  syncOvertimeWarningTruckIds,
  syncResultsAfterPlanningMutation,
} from "../utils/resultsWorkspaceData.js";

export const PLANNING_MUTATION_INVALIDATED_API_URLS = [
  "/api/dashboard",
  "/api/dashboard/home",
  "/api/data-manager/state",
];
const RESULTS_LATEST_API_URL = "/api/results/latest";
export const PLANNING_PERFORMANCE_ACTION_HEADER = "X-TransIA-Performance-Action";
const PLANNING_PERFORMANCE_ACTION_NAME_PATTERN = /^[a-z][a-z0-9_:-]{0,79}$/;
const RUNTIME_REVISION_GUARDED_REASSIGNMENT_ACTIONS = new Set([
  "evening_standard_transfer",
  "preloaded_previous_day_assignment",
  "remove_preloaded_previous_day_assignment",
  "optimize_truck_routes",
  "recalculate_truck_route",
  "create_truck_incident",
  "delete_truck_incident",
  "move_delivery",
  "move_delivery_group",
  "move_order",
  "reactivate",
  "reassign_truck_orders",
  "suspend",
  "suspend_delivery_group",
  "update_truck_incident",
  "update_truck_driver_name",
  "update_truck_tractor",
]);
export const END_OF_DAY_LOAD_ASSIGNMENT_API_URL = "/api/planning/end-of-day-loads/assign";
export const END_OF_DAY_LOAD_REMOVAL_API_URL = "/api/planning/end-of-day-loads/remove";
export const EVENING_STANDARD_TRANSFER_API_URL = "/api/planning/delivery-groups/transfer-evening";
export const PRELOADED_PREVIOUS_DAY_API_URL = "/api/planning/orders/preloaded-previous-day";
export const PRELOADED_PREVIOUS_DAY_REMOVAL_API_URL = "/api/planning/orders/preloaded-previous-day/remove";
export const TRUCK_INCIDENTS_API_URL = "/api/planning/truck-incidents";
const REACTIVATE_OVERTIME_WARNING_THRESHOLD = 0.75;
const TIME_WINDOW_OVERRIDE_REASON_FIELD_KEY = "timeWindowOverrideReason";
const DOMPEUR_CONFIRM_FIELD_KEY = "confirmDompeurOverride";
const OVERTIME_CONFIRM_FIELD_KEY = "allowOvertime";
const TRUCK_PRELOAD_TIME_FIELD_KEY = "truckPreloadTime";
const TRUCK_START_TIME_FIELD_KEY = "truckStartTime";
const TRUCK_TIME_SETTINGS_START_FIELD_KEY = "startTime";
const TRUCK_TIME_SETTINGS_MAX_FIELD_KEY = "maxWorkHours";
const TRUCK_TIME_SETTINGS_CAPACITY_FIELD_KEY = "capacityTons";
const SOREL_TRACY_ROUTE_ALTERNATIVE_ID = "sorel_tracy_saint_ignace";
const TRANSIENT_ACTIVATION_HIGHLIGHT_DURATION_MS = 4600;
const CROSS_PERIOD_ACTIVATION_HIGHLIGHT_TRANSPORT_DURATION_MS = 8000;
export const CLIENT_COHESION_JOIN_CONFIRMATION_REQUIRED_CODE = "client_cohesion_join_confirmation_required";
export const DELIVERY_CLIENT_REPEAT_CONFIRMATION_REQUIRED_CODE = "delivery_client_repeat_confirmation_required";
export const DELIVERY_CLIENT_REPEAT_CONFIRMATION_STALE_CODE = "delivery_client_repeat_confirmation_stale";
const ROUTE_ALTERNATIVE_APPLY_CONFIRMATION = {
  eyebrow: "Traverse Sorel-Tracy",
  title: "Appliquer la traverse ?",
  description: "Le tracé du segment sera remplacé par l'alternative Sorel-Tracy dans le planning courant.",
  impactItems: [
    "Seuls la route, la durée et les km de ce trajet changent.",
  ],
  confirmLabel: "Appliquer la traverse",
};
const ROUTE_ALTERNATIVE_REMOVE_CONFIRMATION = {
  eyebrow: "Traverse Sorel-Tracy",
  title: "Retirer la traverse ?",
  description: "Le trajet reprendra sa route actuelle.",
  impactItems: [
    "Le Gantt sera mis à jour, sans relancer « Optimiser ».",
  ],
  confirmLabel: "Retirer la traverse",
};

function formatDurationWarningLabel(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return "0 min";
  }

  return formatDurationShort(minutes) || `${Math.round(minutes)} min`;
}

function readTruckTimeSettingsOptimizationQuality(payload) {
  const direct = payload?.truck_optimization_quality;
  if (direct && typeof direct === "object" && !Array.isArray(direct)) {
    return direct;
  }
  const nested = payload?._planning_diagnostics?.optimization_quality;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested;
  }
  return null;
}

function buildTruckTimeSettingsDurationConflictDetail(payload, {
  protectedOrderCount = 0,
} = {}) {
  const quality = readTruckTimeSettingsOptimizationQuality(payload);
  if (!quality) {
    return "";
  }

  const candidateDurationMinutes = Number(
    quality?.candidate?.duration_minutes
    ?? quality?.baseline?.duration_minutes,
  );
  const maximumDurationMinutes = Number(quality?.maximum_duration_minutes);
  const reportedOverageMinutes = Number(quality?.duration_overage_minutes);
  if (
    !Number.isFinite(candidateDurationMinutes)
    || !Number.isFinite(maximumDurationMinutes)
    || candidateDurationMinutes <= maximumDurationMinutes
  ) {
    return "";
  }

  const durationOverageMinutes = Number.isFinite(reportedOverageMinutes) && reportedOverageMinutes > 0
    ? reportedOverageMinutes
    : candidateDurationMinutes - maximumDurationMinutes;
  const candidateOrderCount = new Set(
    (Array.isArray(quality?.candidate?.order_ids) ? quality.candidate.order_ids : [])
      .map((orderId) => normalizeInlineText(String(orderId ?? "")))
      .filter(Boolean),
  ).size;
  const requiredMaxWorkHours = Number(quality?.required_max_work_hours);
  const maxWorkTimeFlexMinutes = Number(quality?.max_work_time_flex_minutes);
  const hasLimitBreakdown = (
    Number.isFinite(requiredMaxWorkHours)
    && requiredMaxWorkHours > 0
    && Number.isFinite(maxWorkTimeFlexMinutes)
    && maxWorkTimeFlexMinutes > 0
  );
  const limitBreakdown = hasLimitBreakdown
    ? ` (${formatDurationWarningLabel(requiredMaxWorkHours * 60)} + ${formatDurationWarningLabel(maxWorkTimeFlexMinutes)} de tolérance)`
    : "";
  const candidateOrdersLabel = candidateOrderCount > 0
    ? ` en conservant les ${candidateOrderCount} commandes du camion`
    : "";
  const normalizedProtectedOrderCount = Number.isFinite(Number(protectedOrderCount))
    ? Math.max(0, Math.trunc(Number(protectedOrderCount)))
    : 0;
  const protectedOrdersDetail = normalizedProtectedOrderCount === 1
    ? " Une de ces commandes est protégée et reste figée pendant ce recalcul ciblé."
    : normalizedProtectedOrderCount > 1
      ? ` ${normalizedProtectedOrderCount} de ces commandes sont protégées et restent figées pendant ce recalcul ciblé.`
      : "";
  const usesRetainedRoute = normalizeInlineText(quality?.duration_basis).toLowerCase() === "retained_route";
  const tourLabel = usesRetainedRoute ? "La tournée actuelle" : "La tournée recalculée";
  const durationVerb = usesRetainedRoute ? "dure" : "durerait";

  return (
    `${tourLabel}${candidateOrdersLabel} ${durationVerb} ${formatDurationWarningLabel(candidateDurationMinutes)}. `
    + `Elle dépasse de ${formatDurationWarningLabel(durationOverageMinutes)} la limite applicable de `
    + `${formatDurationWarningLabel(maximumDurationMinutes)}${limitBreakdown}.`
    + protectedOrdersDetail
  );
}

function buildTruckTimeSettingsCapacityConflictDetail(payload, {
  protectedOrderCount = 0,
} = {}) {
  const quality = readTruckTimeSettingsOptimizationQuality(payload);
  if (!quality) {
    return "";
  }

  const candidatePeakTons = Number(
    quality?.candidate_peak_trip_load_tons
    ?? quality?.candidate?.peak_trip_load_tons,
  );
  const requiredCapacityTons = Number(quality?.required_capacity_tons);
  const reportedOverageTons = Number(quality?.capacity_overage_tons);
  if (
    !Number.isFinite(candidatePeakTons)
    || !Number.isFinite(requiredCapacityTons)
    || requiredCapacityTons < 0
    || candidatePeakTons <= requiredCapacityTons
  ) {
    return "";
  }

  const capacityOverageTons = Number.isFinite(reportedOverageTons) && reportedOverageTons > 0
    ? reportedOverageTons
    : candidatePeakTons - requiredCapacityTons;
  const candidateOrderCount = new Set(
    (Array.isArray(quality?.candidate?.order_ids) ? quality.candidate.order_ids : [])
      .map((orderId) => normalizeInlineText(String(orderId ?? "")))
      .filter(Boolean),
  ).size;
  const candidateOrdersLabel = candidateOrderCount > 0
    ? ` en conservant les ${candidateOrderCount} commandes du camion`
    : "";
  const normalizedProtectedOrderCount = Number.isFinite(Number(protectedOrderCount))
    ? Math.max(0, Math.trunc(Number(protectedOrderCount)))
    : 0;
  const protectedOrdersDetail = normalizedProtectedOrderCount === 1
    ? " Une de ces commandes est protégée et reste figée pendant ce recalcul ciblé."
    : normalizedProtectedOrderCount > 1
      ? ` ${normalizedProtectedOrderCount} de ces commandes sont protégées et restent figées pendant ce recalcul ciblé.`
      : "";

  return (
    `Le chargement maximal actuel${candidateOrdersLabel} atteint ${formatTruckCapacityTonsLabel(candidatePeakTons)}. `
    + `Il dépasse de ${formatTruckCapacityTonsLabel(capacityOverageTons)} la nouvelle capacité de `
    + `${formatTruckCapacityTonsLabel(requiredCapacityTons)}.`
    + protectedOrdersDetail
  );
}

function buildTruckTimeSettingsTimeWindowConflictDetail(payload) {
  const quality = readTruckTimeSettingsOptimizationQuality(payload);
  const diagnostic = quality?.blocking_time_window_diagnostic;
  if (!diagnostic || typeof diagnostic !== "object" || Array.isArray(diagnostic)) {
    return "";
  }
  if (diagnostic.is_violated === false) {
    return "";
  }
  return normalizeInlineText(diagnostic.detail);
}

function getCurrentDispatchDateFromLocation() {
  if (typeof window === "undefined") {
    return "";
  }

  const searchParams = new URLSearchParams(window.location.search || "");
  return normalizeInlineText(searchParams.get("dispatch_date"));
}

function normalizePlanningDispatchPeriod(value) {
  const normalizedValue = normalizeInlineText(value).toLowerCase();
  return normalizedValue === "evening" || normalizedValue === "soir" ? "evening" : "day";
}

export function resolveTransientActivationHighlightDuration({ actionName = "" } = {}) {
  const normalizedActionName = normalizeInlineText(actionName);
  return normalizedActionName === "evening_standard_transfer"
    ? CROSS_PERIOD_ACTIVATION_HIGHLIGHT_TRANSPORT_DURATION_MS
    : TRANSIENT_ACTIVATION_HIGHLIGHT_DURATION_MS;
}

function buildPlanningPortalPublicationInvalidationToast(actionName, payload, resultPayload = null) {
  if (actionName === "unpublish_driver_portal_routes") {
    return null;
  }
  return buildPortalPublicationInvalidationToast([
    ...normalizePortalPublicationInvalidatedTruckIds(payload?.publication_invalidated_truck_ids),
    ...normalizePortalPublicationInvalidatedTruckIds(resultPayload?.publication_invalidated_truck_ids),
  ]);
}

function getCurrentDispatchPeriodFromLocation() {
  if (typeof window === "undefined") {
    return "day";
  }

  const searchParams = new URLSearchParams(window.location.search || "");
  return normalizePlanningDispatchPeriod(searchParams.get("dispatch_period"));
}

function appendCurrentDispatchDate(url) {
  if (typeof url !== "string" || !url) {
    return url;
  }

  const dispatchDate = getCurrentDispatchDateFromLocation();
  const dispatchPeriod = getCurrentDispatchPeriodFromLocation();
  if (!dispatchDate && dispatchPeriod === "day") {
    return url;
  }

  if (typeof window === "undefined") {
    const queryItems = [];
    if (dispatchDate) {
      queryItems.push(`dispatch_date=${encodeURIComponent(dispatchDate)}`);
    }
    if (dispatchPeriod === "evening") {
      queryItems.push("dispatch_period=evening");
    }
    const separator = url.includes("?") ? "&" : "?";
    return queryItems.length ? `${url}${separator}${queryItems.join("&")}` : url;
  }

  const resolvedUrl = new URL(url, window.location.origin);
  if (dispatchDate && !resolvedUrl.searchParams.get("dispatch_date")) {
    resolvedUrl.searchParams.set("dispatch_date", dispatchDate);
  }
  if (dispatchPeriod === "evening" && !resolvedUrl.searchParams.get("dispatch_period")) {
    resolvedUrl.searchParams.set("dispatch_period", dispatchPeriod);
  }

  return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
}

function appendPlanningDispatchPeriod(url, dispatchPeriod = "") {
  if (typeof url !== "string" || !url || !normalizeInlineText(dispatchPeriod)) {
    return url;
  }

  const normalizedDispatchPeriod = normalizePlanningDispatchPeriod(dispatchPeriod);
  if (typeof window === "undefined") {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}dispatch_period=${normalizedDispatchPeriod}`;
  }

  const resolvedUrl = new URL(url, window.location.origin);
  resolvedUrl.searchParams.set("dispatch_period", normalizedDispatchPeriod);
  return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
}

export function buildDispatchSnapshotListUrl(dispatchDate = "") {
  const scopedDispatchDate = normalizeInlineText(dispatchDate) || getCurrentDispatchDateFromLocation();
  if (!scopedDispatchDate) {
    return "";
  }
  return `/api/planning/snapshots?dispatch_date=${encodeURIComponent(scopedDispatchDate)}`;
}

export function buildDispatchSnapshotCreateUrl(dispatchDate = "") {
  return buildDispatchSnapshotListUrl(dispatchDate);
}

export function buildDispatchSnapshotRestoreUrl(snapshotId = "", dispatchDate = "") {
  const safeSnapshotId = normalizeInlineText(snapshotId);
  const scopedDispatchDate = normalizeInlineText(dispatchDate) || getCurrentDispatchDateFromLocation();
  if (!safeSnapshotId || !scopedDispatchDate) {
    return "";
  }
  return `/api/planning/snapshots/${encodeURIComponent(safeSnapshotId)}/restore?dispatch_date=${encodeURIComponent(scopedDispatchDate)}`;
}

function appendDispatchSnapshotRestoreConfirmationParams(mutationUrl = "", options = {}) {
  if (!mutationUrl) {
    return "";
  }
  const confirmationParams = [];
  if (options?.confirmRelatedDispatches === true) {
    confirmationParams.push("confirm_related_dispatches=true");
  }
  if (options?.confirmRemovedEndOfDayLoads === true) {
    confirmationParams.push("confirm_removed_end_of_day_loads=true");
  }
  if (options?.confirmAddressRevalidation === true) {
    confirmationParams.push("confirm_address_revalidation=true");
  }
  if (options?.confirmStartedTours === true) {
    confirmationParams.push("confirm_started_tours=true");
  }
  if (!confirmationParams.length) {
    return mutationUrl;
  }
  return `${mutationUrl}${mutationUrl.includes("?") ? "&" : "?"}${confirmationParams.join("&")}`;
}

export function buildDispatchSnapshotDeleteUrl(snapshotId = "", dispatchDate = "") {
  const safeSnapshotId = normalizeInlineText(snapshotId);
  const scopedDispatchDate = normalizeInlineText(dispatchDate) || getCurrentDispatchDateFromLocation();
  if (!safeSnapshotId || !scopedDispatchDate) {
    return "";
  }
  return `/api/planning/snapshots/${encodeURIComponent(safeSnapshotId)}?dispatch_date=${encodeURIComponent(scopedDispatchDate)}`;
}

function getScopedResultsLatestApiUrl(dispatchPeriod = "") {
  return appendPlanningDispatchPeriod(
    appendCurrentDispatchDate(RESULTS_LATEST_API_URL),
    dispatchPeriod,
  );
}

function getForcedResultsLatestApiUrl(dispatchPeriod = "") {
  const url = getScopedResultsLatestApiUrl(dispatchPeriod);
  const refreshToken = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  if (typeof window === "undefined") {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}_planning_refresh=${encodeURIComponent(refreshToken)}`;
  }

  const resolvedUrl = new URL(url, window.location.origin);
  resolvedUrl.searchParams.set("_planning_refresh", refreshToken);
  return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
}

function rerenderWorkspace(workspaceStore, onWorkspaceRender) {
  const state = workspaceStore.getState();
  if (!state) {
    return;
  }

  syncClientSelectionState(state);
  onWorkspaceRender?.();
}

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizePlanningIdentifierList(value, objectKeys = []) {
  const seenValues = new Set();
  return (Array.isArray(value) ? value : [])
    .map((item) => {
      if (typeof item === "string" || typeof item === "number") {
        return normalizeInlineText(String(item));
      }
      if (!item || typeof item !== "object" || Array.isArray(item)) {
        return "";
      }
      for (const objectKey of objectKeys) {
        const resolvedValue = normalizeInlineText(item?.[objectKey]);
        if (resolvedValue) {
          return resolvedValue;
        }
      }
      return "";
    })
    .filter((item) => {
      if (!item || seenValues.has(item)) {
        return false;
      }
      seenValues.add(item);
      return true;
    });
}

function hasSamePlanningIdentifierSet(leftValues, rightValues) {
  if (leftValues.length !== rightValues.length) {
    return false;
  }
  const rightValueSet = new Set(rightValues);
  return leftValues.every((value) => rightValueSet.has(value));
}

function normalizeOptionalPlanningNumber(value) {
  if (value == null || normalizeInlineText(String(value)) === "") {
    return null;
  }
  const normalizedValue = Number(value);
  return Number.isFinite(normalizedValue) ? normalizedValue : null;
}

function normalizeDeliveryClientRepeatClientKey(value) {
  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }

  const normalizedKey = value.map((item) => {
    if (typeof item !== "string" && typeof item !== "number") {
      return "";
    }
    return normalizeInlineText(String(item));
  });
  return normalizedKey.some((item) => !item) ? null : normalizedKey;
}

function normalizeDeliveryClientRepeatConflict(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const clientKey = normalizeDeliveryClientRepeatClientKey(value.client_key);
  const clientLabel = normalizeInlineText(value.client_label);
  const beforeVisitCount = Number(value.before_visit_count);
  const afterVisitCount = Number(value.after_visit_count);
  if (
    !clientKey
    || !clientLabel
    || !Number.isInteger(beforeVisitCount)
    || beforeVisitCount < 0
    || !Number.isInteger(afterVisitCount)
    || afterVisitCount <= beforeVisitCount
  ) {
    return null;
  }

  return {
    afterVisitCount,
    beforeVisitCount,
    clientKey,
    clientLabel,
    existingOrderIds: normalizePlanningIdentifierList(value.existing_order_ids, ["order_id", "id"]),
    involvedMovedOrderIds: normalizePlanningIdentifierList(
      value.involved_moved_order_ids,
      ["order_id", "id"],
    ),
  };
}

export function resolveDeliveryClientRepeatConfirmationChallenge({
  payload = null,
  status: responseStatus = 0,
} = {}) {
  if (Number(responseStatus) !== 409 || !payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }

  const challengeCode = normalizeInlineText(payload.code || payload.reason_code).toLowerCase();
  const details = payload.error_details && typeof payload.error_details === "object" && !Array.isArray(payload.error_details)
    ? payload.error_details
    : null;
  if (challengeCode !== DELIVERY_CLIENT_REPEAT_CONFIRMATION_REQUIRED_CODE || !details) {
    return null;
  }

  const confirmationId = normalizeInlineText(details.confirmation_id);
  const runtimeRevision = normalizeInlineText(details.runtime_revision);
  const rebuildMode = normalizeDispatchReassignmentMode(details.rebuild_mode, "");
  const targetTruckId = normalizeInlineText(details.target_truck_id);
  const movedDeliveryIds = normalizePlanningIdentifierList(
    details.moved_delivery_ids,
    ["delivery_id", "id"],
  );
  const rawConflicts = Array.isArray(details.conflicts) ? details.conflicts : [];
  const conflicts = rawConflicts.map(normalizeDeliveryClientRepeatConflict);
  if (
    !confirmationId
    || !runtimeRevision
    || !rebuildMode
    || !targetTruckId
    || movedDeliveryIds.length === 0
    || conflicts.length === 0
    || conflicts.some((conflict) => !conflict)
  ) {
    return null;
  }

  return {
    confirmationId,
    conflicts,
    movedDeliveryIds,
    movedOrderIds: normalizePlanningIdentifierList(details.moved_order_ids, ["order_id", "id"]),
    rebuildMode,
    runtimeRevision,
    sourceTruckIds: normalizePlanningIdentifierList(details.source_truck_ids, ["truck_id", "id"]),
    targetTruckId,
  };
}

export function buildDeliveryClientRepeatConfirmationRequestBody({
  challenge = null,
  expectedDeliveryIds = [],
  initialBody = null,
  targetTruckId = "",
} = {}) {
  if (
    !challenge
    || !initialBody
    || typeof initialBody !== "object"
    || Array.isArray(initialBody)
  ) {
    return null;
  }

  const initialRebuildMode = normalizeDispatchReassignmentMode(initialBody.rebuild_mode, "");
  const initialDeliveryIds = normalizePlanningIdentifierList(
    initialBody.delivery_ids,
    ["delivery_id", "id"],
  );
  const explicitExpectedDeliveryIds = normalizePlanningIdentifierList(
    expectedDeliveryIds,
    ["delivery_id", "id"],
  );
  const clientCohesionExpectedDeliveryIds = normalizePlanningIdentifierList(
    initialBody.client_cohesion_expected_delivery_ids,
    ["delivery_id", "id"],
  );
  const clientCohesionDecision = normalizeInlineText(initialBody.client_cohesion_decision).toLowerCase();
  const scopedExpectedDeliveryIds = clientCohesionDecision === "join" && clientCohesionExpectedDeliveryIds.length > 0
    ? clientCohesionExpectedDeliveryIds
    : explicitExpectedDeliveryIds.length > 0
      ? explicitExpectedDeliveryIds
      : initialDeliveryIds;
  const initialTargetTruckId = normalizeInlineText(initialBody.target_truck_id);
  const explicitTargetTruckId = normalizeInlineText(targetTruckId);
  const challengeTargetTruckId = normalizeInlineText(challenge.targetTruckId);
  const challengeDeliveryIds = normalizePlanningIdentifierList(
    challenge.movedDeliveryIds,
    ["delivery_id", "id"],
  );
  const expectedClientKeys = (Array.isArray(challenge.conflicts) ? challenge.conflicts : [])
    .map((conflict) => normalizeDeliveryClientRepeatClientKey(conflict?.clientKey));
  if (
    !initialRebuildMode
    || initialRebuildMode !== challenge.rebuildMode
    || !normalizeInlineText(challenge.confirmationId)
    || !normalizeInlineText(challenge.runtimeRevision)
    || !challengeTargetTruckId
    || scopedExpectedDeliveryIds.length === 0
    || challengeDeliveryIds.length === 0
    || !hasSamePlanningIdentifierSet(scopedExpectedDeliveryIds, challengeDeliveryIds)
    || (initialTargetTruckId && initialTargetTruckId !== challengeTargetTruckId)
    || (explicitTargetTruckId && explicitTargetTruckId !== challengeTargetTruckId)
    || expectedClientKeys.length === 0
    || expectedClientKeys.some((clientKey) => !clientKey)
  ) {
    return null;
  }

  return {
    ...initialBody,
    confirm_delivery_client_repeat: true,
    delivery_client_repeat_confirmation_id: challenge.confirmationId,
    delivery_client_repeat_expected_client_keys: expectedClientKeys.map((clientKey) => [...clientKey]),
    delivery_client_repeat_expected_delivery_ids: [...challengeDeliveryIds],
    delivery_client_repeat_expected_rebuild_mode: initialRebuildMode,
    expected_runtime_revision: challenge.runtimeRevision,
    rebuild_mode: initialRebuildMode,
  };
}

export function buildDeliveryClientRepeatConfirmationContent(challenge = null) {
  const conflicts = Array.isArray(challenge?.conflicts) ? challenge.conflicts.filter(Boolean) : [];
  if (conflicts.length === 0) {
    return null;
  }

  const movedOrderIds = normalizePlanningIdentifierList(challenge?.movedOrderIds, ["order_id", "id"]);
  const movedDeliveryIds = normalizePlanningIdentifierList(
    challenge?.movedDeliveryIds,
    ["delivery_id", "id"],
  );
  const movedCount = movedOrderIds.length || movedDeliveryIds.length;
  const targetTruckLabel = normalizeInlineText(challenge?.targetTruckLabel)
    || normalizeInlineText(challenge?.targetTruckId);
  if (movedCount === 0 || !targetTruckLabel) {
    return null;
  }

  const movedSummary = movedCount === 1
    ? (movedOrderIds[0] || movedDeliveryIds[0])
    : `${movedCount} commandes`;
  const singleConflict = conflicts.length === 1 ? conflicts[0] : null;
  return {
    cancelLabel: "Annuler",
    confirmLabel: "Autoriser la multi-visite",
    contentDensity: "compact",
    description: movedCount === 1
      ? `La commande ${movedSummary} créerait un passage client supplémentaire sur ${targetTruckLabel}.`
      : `La réaffectation de ${movedCount} commandes créerait des passages client supplémentaires sur ${targetTruckLabel}.`,
    eyebrow: "Visites client",
    impactItems: [],
    summaryItems: [
      {
        label: movedCount === 1 ? "Commande" : "Commandes",
        value: movedSummary,
      },
      {
        label: "Camion cible",
        value: targetTruckLabel,
      },
      ...conflicts.map((conflict) => ({
        label: conflict.clientLabel,
        tone: "warning",
        value: `${conflict.beforeVisitCount} → ${conflict.afterVisitCount} visites`,
      })),
    ],
    summaryLayout: "stack",
    title: singleConflict
      ? `Autoriser une ${singleConflict.afterVisitCount}e visite chez ${singleConflict.clientLabel} ?`
      : "Autoriser plusieurs visites client ?",
    tone: "warning",
    warningMessage: "Cette autorisation vaut uniquement pour cette réaffectation. Un futur clic sur Optimiser pourra regrouper la visite ou refuser la solution si les contraintes l'empêchent.",
  };
}

export function createDeliveryClientRepeatMutationChallengeHandler({
  dismissConfirmation = null,
  expectedDeliveryIds = [],
  retryMutation = null,
  setConfirmationDialog = null,
  targetTruckId = "",
  targetTruckLabel = "",
} = {}) {
  return ({ payload = null, requestBody = null, response = null } = {}) => {
    if (typeof retryMutation !== "function" || typeof setConfirmationDialog !== "function") {
      return false;
    }

    const parsedChallenge = resolveDeliveryClientRepeatConfirmationChallenge({
      payload,
      status: response?.status,
    });
    const challenge = parsedChallenge
      ? {
          ...parsedChallenge,
          targetTruckLabel: normalizeInlineText(targetTruckLabel) || parsedChallenge.targetTruckId,
        }
      : null;
    const retryBody = buildDeliveryClientRepeatConfirmationRequestBody({
      challenge,
      expectedDeliveryIds,
      initialBody: requestBody,
      targetTruckId,
    });
    const confirmationContent = buildDeliveryClientRepeatConfirmationContent(challenge);
    if (!challenge || !retryBody || !confirmationContent) {
      return false;
    }

    let retrySubmitted = false;
    const closeConfirmation = typeof dismissConfirmation === "function"
      ? dismissConfirmation
      : () => setConfirmationDialog(null);
    setConfirmationDialog({
      ...confirmationContent,
      isOpen: true,
      onCancel: closeConfirmation,
      onClose: closeConfirmation,
      onConfirm: () => {
        if (retrySubmitted) {
          return;
        }
        retrySubmitted = true;
        closeConfirmation();
        retryMutation({
          requestBody: retryBody,
          requestExpectedRuntimeRevision: challenge.runtimeRevision,
        });
      },
      onDismiss: closeConfirmation,
    });
    return true;
  };
}

export function resolveClientCohesionJoinConfirmationChallenge({
  payload = null,
  status: responseStatus = 0,
} = {}) {
  if (Number(responseStatus) !== 409 || !payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }

  const challengeCode = normalizeInlineText(payload.code || payload.reason_code).toLowerCase();
  const details = payload.error_details && typeof payload.error_details === "object" && !Array.isArray(payload.error_details)
    ? payload.error_details
    : null;
  if (challengeCode !== CLIENT_COHESION_JOIN_CONFIRMATION_REQUIRED_CODE || !details) {
    return null;
  }

  const selectedDeliveryIds = normalizePlanningIdentifierList(
    details.selected_delivery_ids,
    ["delivery_id", "id"],
  );
  const separatedDeliveryIds = normalizePlanningIdentifierList(
    details.separated_delivery_ids,
    ["delivery_id", "id"],
  );
  const expectedDeliveryIds = normalizePlanningIdentifierList(
    details.expected_delivery_ids,
    ["delivery_id", "id"],
  );
  const rebuildMode = normalizeDispatchReassignmentMode(details.rebuild_mode, "");
  const runtimeRevision = normalizeInlineText(details.runtime_revision);
  const confirmationId = normalizeInlineText(details.confirmation_id);
  if (
    selectedDeliveryIds.length === 0
    || expectedDeliveryIds.length === 0
    || !rebuildMode
    || !runtimeRevision
    || !confirmationId
  ) {
    return null;
  }

  const joinBlockReason = normalizeInlineText(details.join_block_reason);
  return {
    canJoin: details.can_join === true && !joinBlockReason,
    clientLabel: normalizeInlineText(details.client_label) || "Ce client",
    confirmationId,
    expectedDeliveryIds,
    joinBlockReason,
    rebuildMode,
    runtimeRevision,
    selectedDeliveryIds,
    selectedOrderIds: normalizePlanningIdentifierList(details.selected_order_ids, ["order_id", "id"]),
    separatedDeliveryIds,
    separatedOrderIds: normalizePlanningIdentifierList(details.separated_order_ids, ["order_id", "id"]),
    sourceTruckIds: normalizePlanningIdentifierList(details.source_truck_ids, ["truck_id", "id"]),
    targetCapacityTons: normalizeOptionalPlanningNumber(details.target_capacity_tons),
    targetTruckId: normalizeInlineText(details.target_truck_id),
    totalQuantityTons: normalizeOptionalPlanningNumber(details.total_quantity_tons),
  };
}

export function buildClientCohesionDecisionRequestBody({
  challenge = null,
  decision = "",
  initialBody = null,
} = {}) {
  const normalizedDecision = normalizeInlineText(decision).toLowerCase();
  if (
    !initialBody
    || typeof initialBody !== "object"
    || Array.isArray(initialBody)
    || !challenge
    || !["join", "keep_split"].includes(normalizedDecision)
    || (normalizedDecision === "join" && challenge.canJoin !== true)
  ) {
    return null;
  }

  const initialDeliveryIds = normalizePlanningIdentifierList(initialBody.delivery_ids, ["delivery_id", "id"]);
  const initialRebuildMode = normalizeDispatchReassignmentMode(initialBody.rebuild_mode, "");
  if (
    !initialRebuildMode
    || initialRebuildMode !== challenge.rebuildMode
    || !hasSamePlanningIdentifierSet(initialDeliveryIds, challenge.selectedDeliveryIds || [])
    || !Array.isArray(challenge.expectedDeliveryIds)
    || challenge.expectedDeliveryIds.length === 0
    || !normalizeInlineText(challenge.runtimeRevision)
    || !normalizeInlineText(challenge.confirmationId)
  ) {
    return null;
  }

  return {
    ...initialBody,
    client_cohesion_confirmation_id: challenge.confirmationId,
    client_cohesion_decision: normalizedDecision,
    client_cohesion_expected_delivery_ids: [...challenge.expectedDeliveryIds],
    client_cohesion_expected_rebuild_mode: initialRebuildMode,
    expected_runtime_revision: challenge.runtimeRevision,
    rebuild_mode: initialRebuildMode,
  };
}

function resolveClientCohesionOrderCounts(challenge = null) {
  const selectedOrderCount = challenge?.selectedOrderIds?.length
    || challenge?.selectedDeliveryIds?.length
    || 0;
  const separatedOrderCount = challenge?.separatedOrderIds?.length
    || challenge?.separatedDeliveryIds?.length
    || 0;
  return {
    selectedOrderCount,
    separatedOrderCount,
    totalOrderCount: selectedOrderCount + separatedOrderCount,
  };
}

export function resolveClientCohesionSourceTruckLabels(state, challenge = null) {
  return (Array.isArray(challenge?.sourceTruckIds) ? challenge.sourceTruckIds : [])
    .map((truckId) => resolveTruckDisplayName(state, truckId, truckId))
    .filter(Boolean);
}

export function buildClientCohesionJoinConfirmationContent(challenge = null) {
  if (!challenge) {
    return null;
  }
  const { selectedOrderCount, separatedOrderCount, totalOrderCount } = resolveClientCohesionOrderCounts(challenge);
  if (selectedOrderCount === 0 || separatedOrderCount === 0 || totalOrderCount === 0) {
    return null;
  }

  const targetTruckLabel = challenge.targetTruckLabel || challenge.targetTruckId || "le camion cible";
  const sourceTruckLabels = normalizePlanningIdentifierList(
    challenge.sourceTruckLabels,
    ["truck_name", "truck_label", "label", "name"],
  );
  const sourceTruckLabel = sourceTruckLabels.length
    ? sourceTruckLabels.join(", ")
    : challenge.sourceTruckIds?.length
      ? challenge.sourceTruckIds.join(", ")
      : "Camions distincts";
  const capacitySummaryItems = challenge.targetCapacityTons != null
    ? [{
        label: "Capacité cible",
        tone: challenge.canJoin ? "neutral" : "warning",
        value: `${challenge.targetCapacityTons} t`,
      }]
    : [];
  const quantitySummaryItems = challenge.totalQuantityTons != null
    ? [{
        label: "Quantité totale",
        tone: challenge.canJoin ? "neutral" : "warning",
        value: `${challenge.totalQuantityTons} t`,
      }]
    : [];

  return {
    cancelLabel: challenge.canJoin
      ? `Non, réaffecter seulement les ${selectedOrderCount}`
      : "Annuler",
    confirmLabel: challenge.canJoin
      ? `Oui, réaffecter les ${totalOrderCount}`
      : `Réaffecter seulement les ${selectedOrderCount}`,
    description: challenge.canJoin
      ? `${challenge.clientLabel} possède ${separatedOrderCount} commande${separatedOrderCount > 1 ? "s" : ""} séparée${separatedOrderCount > 1 ? "s" : ""} sur un autre camion. Vous pouvez les réunir sur ${targetTruckLabel} ou déplacer seulement la sélection.`
      : `${challenge.clientLabel} possède ${separatedOrderCount} commande${separatedOrderCount > 1 ? "s" : ""} séparée${separatedOrderCount > 1 ? "s" : ""}. La sélection peut rester séparée et être déplacée seule vers ${targetTruckLabel}.`,
    eyebrow: "Cohésion client",
    contentDensity: "compact",
    impactItems: challenge.canJoin
      ? [
          "Oui réunit toutes les commandes concernées sur la cible.",
          "Non conserve la séparation actuelle et déplace uniquement la sélection.",
        ]
      : ["La commande séparée et son camion actuel ne seront pas reconstruits."],
    summaryItems: [
      { label: "Sélection", value: String(selectedOrderCount) },
      { label: "Séparées", value: String(separatedOrderCount), tone: "warning" },
      { label: "Sources", nowrap: true, value: sourceTruckLabel },
      ...quantitySummaryItems,
      ...capacitySummaryItems,
    ],
    summaryDensity: "compact",
    summaryLayout: "cohesion",
    title: challenge.canJoin
      ? `Regrouper les commandes de ${challenge.clientLabel} ?`
      : `Regroupement impossible pour ${challenge.clientLabel}`,
    tone: "warning",
    warningMessage: challenge.canJoin
      ? "Le choix sera revérifié sous verrou avant toute sauvegarde."
      : challenge.joinBlockReason || "La reconstruction cumulative ne permet pas de réunir toutes les commandes sur la cible.",
  };
}

export function buildPlanningPerformanceActionHeaders(actionName, correlation = {}) {
  const safeActionName = normalizeInlineText(actionName);
  if (!PLANNING_PERFORMANCE_ACTION_NAME_PATTERN.test(safeActionName)) {
    return {};
  }
  return {
    [PLANNING_PERFORMANCE_ACTION_HEADER]: safeActionName,
    ...buildPlanningPerformanceCorrelationHeaders(correlation),
  };
}

export function buildRuntimeRevisionGuardedPlanningMutationBody({
  actionName = "",
  body = null,
  expectedRuntimeRevision = "",
  resultsData = null,
} = {}) {
  const safeActionName = normalizeInlineText(actionName);
  const runtimeRevision = normalizeInlineText(expectedRuntimeRevision)
    || normalizeInlineText(resultsData?.runtime_revision);
  if (
    !runtimeRevision
    || !RUNTIME_REVISION_GUARDED_REASSIGNMENT_ACTIONS.has(safeActionName)
  ) {
    return body;
  }

  return {
    ...(body && typeof body === "object" && !Array.isArray(body) ? body : {}),
    expected_runtime_revision: runtimeRevision,
    ...(safeActionName === "evening_standard_transfer" ? {
      expected_runtime_revision_period: normalizePlanningDispatchPeriod(
        expectedRuntimeRevision ? (body?.expected_runtime_revision_period || resultsData?.dispatch_period) : resultsData?.dispatch_period,
      ),
    } : {}),
  };
}

export function buildTruckIncidentMutationUrl({
  dispatchDate = "",
  dispatchPeriod = "day",
  incidentId = "",
} = {}) {
  const safeIncidentId = normalizeInlineText(incidentId);
  const path = safeIncidentId
    ? `${TRUCK_INCIDENTS_API_URL}/${encodeURIComponent(safeIncidentId)}`
    : TRUCK_INCIDENTS_API_URL;
  const searchParams = new URLSearchParams();
  const safeDispatchDate = normalizeInlineText(dispatchDate);
  if (safeDispatchDate) {
    searchParams.set("dispatch_date", safeDispatchDate);
  }
  searchParams.set("dispatch_period", normalizePlanningDispatchPeriod(dispatchPeriod));
  return `${path}?${searchParams.toString()}`;
}

export function buildSourceChangesVerificationEventMetadata(actionName, body = null) {
  if (normalizeInlineText(actionName) !== "segment_reorder") {
    return {};
  }

  return {
    sourceChangesVerificationRequired: Array.isArray(body?.clearTimePriorityOrderUpdates)
      && body.clearTimePriorityOrderUpdates.length > 0,
  };
}

function resolvePlanningMutationContentValue(value, context = {}) {
  if (typeof value === "function") {
    return normalizeInlineText(value(context));
  }
  return normalizeInlineText(value);
}

const DISPATCH_DAY_LABEL_FORMATTER = new Intl.DateTimeFormat("fr-CA", { day: "numeric", month: "long", timeZone: "UTC" });

// « 14 octobre » pour une date AAAA-MM-JJ ; la valeur telle quelle sinon.
function formatDispatchDayLabel(value) {
  const text = normalizeInlineText(value);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? DISPATCH_DAY_LABEL_FORMATTER.format(new Date(text)) : text;
}

// Un bris qui repousse un chargement après la fermeture de la meunerie l'envoie dans Suspendues.
export function withSuspendedOrdersNote(message, payload) {
  const count = Array.isArray(payload?.suspended_order_ids) ? payload.suspended_order_ids.length : 0;
  return count > 0 ? `${message} ${count} commande${count > 1 ? "s" : ""} dans Suspendues.` : message;
}

// Un déplacement peut faire gagner ou perdre « Chargé la veille » à une autre commande du camion : le serveur la nomme.
export function withCollateralPreloadNote(message, payload) {
  const warnings = (Array.isArray(payload?.warnings) ? payload.warnings : [])
    .filter((warning) => warning?.reason_code === "collateral_preload_change");
  if (warnings.length === 0) return message;
  const orderIds = [...new Set(warnings.flatMap((warning) => warning.order_ids || []))];
  return `${message} « Chargé la veille » change aussi pour ${orderIds.join(", ") || "d'autres commandes"}.`;
}

export function resolveEndOfDayLoadRemovalFeedback(payload = {}) {
  const outcome = normalizeInlineText(payload?.end_of_day_load_removal_outcome);
  const serverMessage = normalizeInlineText(payload?.end_of_day_load_removal_message)
    || normalizeInlineText(payload?.message);

  if (outcome === "restored_to_initial_truck") {
    return {
      message: serverMessage || "Le Chargé en PM est retourné sur sa remorque initiale.",
      title: "Commande retournée",
    };
  }

  if (outcome === "moved_to_suspended") {
    return {
      message: serverMessage || "Le Chargé en PM a été transféré dans Suspendues.",
      title: "Commande suspendue",
    };
  }

  return {
    message: serverMessage || "Le Chargé en PM a été retiré.",
    title: "Chargé en PM retiré",
  };
}

function normalizeRouteAlternativeSegmentIndexes(routeSegmentIndex, routeSegmentIndexes = []) {
  const candidates = Array.isArray(routeSegmentIndexes) ? [...routeSegmentIndexes] : [];
  candidates.push(routeSegmentIndex);
  const indexes = [];
  const seen = new Set();

  candidates.forEach((value) => {
    const parsedIndex = Number.parseInt(String(value ?? ""), 10);
    if (!Number.isInteger(parsedIndex) || parsedIndex <= 0 || seen.has(parsedIndex)) {
      return;
    }

    seen.add(parsedIndex);
    indexes.push(parsedIndex);
  });

  return indexes;
}

export function normalizeSegmentReorderTimePriorityResetUpdates(updates = []) {
  if (!Array.isArray(updates) || updates.length === 0) {
    return [];
  }

  const seenKeys = new Set();

  return updates.reduce((items, item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      return items;
    }

    const orderId = normalizeInlineText(item.orderId);
    const stepType = normalizeInlineText(item.stepType);
    const payload = item.payload && typeof item.payload === "object" && !Array.isArray(item.payload)
      ? item.payload
      : null;
    if (!orderId || !payload) {
      return items;
    }

    const dedupeKey = `${orderId}:${stepType || "order"}`;
    if (seenKeys.has(dedupeKey)) {
      return items;
    }

    seenKeys.add(dedupeKey);
    items.push({
      clearsPriority: item.clearsPriority === true,
      clearsTime: item.clearsTime === true,
      orderId,
      payload,
      ...(stepType ? { stepType } : {}),
    });
    return items;
  }, []);
}

export function buildSegmentReorderConfirmationDialogConfig({
  draggedStepCount = 1,
  isLoadScope = false,
  resetUpdates = [],
} = {}) {
  const safeDraggedStepCount = Number.isInteger(draggedStepCount) && draggedStepCount > 1 ? draggedStepCount : 1;
  const safeResetUpdates = normalizeSegmentReorderTimePriorityResetUpdates(resetUpdates);
  const subjectLabel = safeDraggedStepCount > 1
    ? "Ce groupe possède une heure et/ou une priorité."
    : "Ce segment possède une heure et/ou une priorité.";

  return {
    eyebrow: "Réordonner",
    title: safeDraggedStepCount > 1
      ? "Confirmer le déplacement du groupe ?"
      : "Confirmer le déplacement du segment ?",
    description: subjectLabel,
    impactItems: [
      safeDraggedStepCount > 1
        ? "Le groupe change de place sur ce camion."
        : "La commande change de place sur ce camion.",
      "Si vous confirmez le déplacement, l'heure et la priorité seront supprimées de la commande.",
    ],
    summaryItems: [
      { label: "Portée", value: isLoadScope ? "Chargements" : "Livraisons" },
      { label: "Commandes", value: String(safeResetUpdates.length || 1) },
    ],
    confirmLabel: "Confirmer",
    cancelLabel: "Annuler",
    tone: "warning",
    warningMessage: "« Annuler » ne change rien.",
  };
}

function formatSegmentReorderCommandReference(clientLabel = "", orderLabel = "") {
  const safeClientLabel = normalizeInlineText(clientLabel);
  const safeOrderLabel = normalizeInlineText(orderLabel);

  if (!safeClientLabel) {
    return safeOrderLabel;
  }

  if (!safeOrderLabel) {
    return safeClientLabel;
  }

  return `${safeClientLabel} (${safeOrderLabel})`;
}

function prefixSegmentReorderSuccessMessage(message = "", truckLabel = "") {
  const safeMessage = normalizeInlineText(message);
  const safeTruckLabel = normalizeInlineText(truckLabel);

  if (!safeMessage) {
    return "";
  }

  return safeTruckLabel ? `Camion ${safeTruckLabel} : ${safeMessage}` : safeMessage;
}

export function buildSegmentReorderSuccessMessage({
  draggedLabel = "",
  draggedOrderLabel = "",
  draggedStepCount = 1,
  isLoadScope = false,
  placement = "before",
  targetLabel = "",
  targetOrderLabel = "",
  truckLabel = "",
} = {}) {
  // Chargements à la suite : le bloc glissé est chargé avant la cible, puis les livraisons.
  if (placement === "join") {
    return prefixSegmentReorderSuccessMessage(
      `${draggedLabel || "Ce chargement"} puis ${targetLabel || "la cible"} : chargés à la suite, livraisons après.`,
      truckLabel,
    );
  }
  if (placement === "separate") {
    return prefixSegmentReorderSuccessMessage("Chargements séparés : chacun est livré après son chargement.", truckLabel);
  }
  const draggedReference = formatSegmentReorderCommandReference(draggedLabel, draggedOrderLabel);
  const targetReference = formatSegmentReorderCommandReference(targetLabel, targetOrderLabel);
  const placementLabel = placement === "after" ? "après" : "avant";
  const safeDraggedStepCount = Number.isInteger(draggedStepCount) && draggedStepCount > 1
    ? draggedStepCount
    : 1;

  if (draggedReference && targetReference) {
    const baseMessage = safeDraggedStepCount > 1
      ? `Le groupe ${draggedReference} a été réordonné ${placementLabel} ${targetReference}.`
      : `La commande ${draggedReference} a été réordonnée ${placementLabel} ${targetReference}.`;

    return prefixSegmentReorderSuccessMessage(baseMessage, truckLabel);
  }

  return prefixSegmentReorderSuccessMessage(
    isLoadScope
      ? "Le nouvel ordre local des chargements est prêt."
      : "Le nouvel ordre local des livraisons est prêt.",
    truckLabel,
  );
}

export function buildSegmentReorderMutationRequest(payload = {}) {
  const safeDispatchPeriod = normalizePlanningDispatchPeriod(payload.dispatchPeriod);
  const safeDraggedSegmentId = normalizeInlineText(payload.draggedSegmentId);
  const safeDraggedStepId = normalizeInlineText(payload.draggedStepId);
  const safeDraggedLabel = normalizeInlineText(payload.draggedLabel);
  const safeDraggedOrderLabel = normalizeInlineText(payload.draggedOrderLabel);
  const safeTargetLabel = normalizeInlineText(payload.targetLabel);
  const safeTargetOrderLabel = normalizeInlineText(payload.targetOrderLabel);
  const safeTruckLabel = normalizeInlineText(payload.truckLabel);
  const safeDraggedStepIds = Array.isArray(payload.draggedStepIds)
    ? payload.draggedStepIds
        .map((id) => normalizeInlineText(id))
        .filter((id) => id && id !== safeDraggedStepId)
    : [];
  const safeTargetSegmentId = normalizeInlineText(payload.targetSegmentId);
  const safeTargetStepId = normalizeInlineText(payload.targetStepId);
  const safePlacement = ["after", "join", "separate"].includes(normalizeInlineText(payload.placement))
    ? normalizeInlineText(payload.placement)
    : "before";
  const safeTruckId = normalizeInlineText(payload.truckId);
  const safeScope = normalizeInlineText(payload.scope);
  const safeParentLoadingStepId = normalizeInlineText(payload.parentLoadingStepId);
  const safeDeliveryBlockId = normalizeInlineText(payload.deliveryBlockId);
  const safeSourceGroupKey = normalizeInlineText(payload.sourceGroupKey);
  const safeResetUpdates = normalizeSegmentReorderTimePriorityResetUpdates(payload.timePriorityResetUpdates);
  const safeHighlightedStepKeys = Array.isArray(payload.highlightedStepKeys)
    ? payload.highlightedStepKeys.filter((stepKey) => typeof stepKey === "string" && stepKey.trim())
    : [];
  if (!safeDraggedStepId || !safeTargetStepId || !safeTruckId) {
    return null;
  }

  const isLoadScope = safeScope === "loads";
  const scopeLabel = isLoadScope ? "chargements" : "livraisons";
  const draggedStepCount = safeDraggedStepIds.length + 1;
  const successMessage = buildSegmentReorderSuccessMessage({
    draggedLabel: safeDraggedLabel,
    draggedOrderLabel: safeDraggedOrderLabel,
    draggedStepCount,
    isLoadScope,
    placement: safePlacement,
    targetLabel: safeTargetLabel,
    targetOrderLabel: safeTargetOrderLabel,
    truckLabel: safeTruckLabel,
  });
  const body = {
    draggedSegmentId: safeDraggedSegmentId || safeDraggedStepId,
    draggedStepId: safeDraggedStepId,
    deliveryBlockId: safeDeliveryBlockId || null,
    parentLoadingStepId: safeParentLoadingStepId || null,
    placement: safePlacement,
    scope: safeScope,
    targetSegmentId: safeTargetSegmentId || safeTargetStepId,
    targetStepId: safeTargetStepId,
    truckId: safeTruckId,
  };
  Object.assign(body, buildDispatchReassignmentMutationModeFields(payload.rebuildMode));
  if (safeDraggedStepIds.length > 0) {
    body.draggedStepIds = [safeDraggedStepId, ...safeDraggedStepIds];
  }
  if (safeSourceGroupKey) {
    body.sourceGroupKey = safeSourceGroupKey;
  }
  if (safeResetUpdates.length > 0) {
    body.clearTimePriorityOrderUpdates = safeResetUpdates.map((item) => ({
      orderId: item.orderId,
      payload: item.payload,
    }));
  }

  return {
    body,
    confirmationDialog: safeResetUpdates.length > 0
      ? buildSegmentReorderConfirmationDialogConfig({
          draggedStepCount,
          isLoadScope,
          resetUpdates: safeResetUpdates,
        })
      : null,
    dispatchPeriod: safeDispatchPeriod,
    highlightedStepKeys: safeHighlightedStepKeys,
    isLoadScope,
    loadingStepId: safeParentLoadingStepId || safeDraggedStepId,
    scopeLabel,
    successMessage,
    truckId: safeTruckId,
  };
}

export function buildDeliveryMoveRequestBody({
  allowOvertime = false,
  allowReturnToOptimized = false,
  confirmDompeurOverride = false,
  confirmTimeWindowOverride = false,
  forcedPreloadedPreviousDayOrderIds = [],
  protectAfterReassignment = true,
  rebuildMode = DISPATCH_REASSIGNMENT_MODE_COMPLETE,
  targetTruckId = "",
  timeWindowOverrideReason = "",
} = {}) {
  const safeTargetTruckId = normalizeInlineText(targetTruckId);
  if (!safeTargetTruckId) {
    return null;
  }
  const resolvedForcedPreloadedPreviousDayOrderIds = (
    Array.isArray(forcedPreloadedPreviousDayOrderIds)
      ? forcedPreloadedPreviousDayOrderIds
      : []
  )
    .map((orderId) => normalizeInlineText(orderId))
    .filter(Boolean)
    .filter((orderId, index, orderIds) => orderIds.indexOf(orderId) === index);
  const safeTimeWindowOverrideReason = normalizeInlineText(timeWindowOverrideReason);

  return {
    target_truck_id: safeTargetTruckId,
    ...(allowReturnToOptimized ? { allow_return_to_optimized: true } : {}),
    ...buildDispatchReassignmentMutationModeFields(rebuildMode, { allowOvertime }),
    ...(resolvedForcedPreloadedPreviousDayOrderIds.length === 0
      ? buildDispatchProtectAfterReassignmentMutationFields(protectAfterReassignment)
      : {}),
    ...(resolvedForcedPreloadedPreviousDayOrderIds.length > 0
      ? { forced_preloaded_previous_day_order_ids: resolvedForcedPreloadedPreviousDayOrderIds }
      : {}),
    ...(safeTimeWindowOverrideReason ? { time_window_override_reason: safeTimeWindowOverrideReason } : {}),
    ...(confirmTimeWindowOverride ? { confirm_time_window_override: true } : {}),
    ...(confirmDompeurOverride ? { confirm_dompeur_override: true } : {}),
  };
}

export function buildDeliveryReactivationRequestBody({
  allowOvertime = false,
  confirmDompeurOverride = false,
  confirmTimeWindowOverride = false,
  protectAfterReassignment = true,
  rebuildMode = DISPATCH_REASSIGNMENT_MODE_COMPLETE,
  targetTruckId = "",
  timeWindowOverrideReason = "",
} = {}) {
  const safeTargetTruckId = normalizeInlineText(targetTruckId);
  const safeTimeWindowOverrideReason = normalizeInlineText(timeWindowOverrideReason);
  return {
    ...buildDispatchReassignmentMutationModeFields(rebuildMode, { allowOvertime }),
    ...(safeTargetTruckId
      ? buildDispatchProtectAfterReassignmentMutationFields(protectAfterReassignment)
      : {}),
    ...(safeTargetTruckId ? { target_truck_id: safeTargetTruckId } : {}),
    ...(safeTimeWindowOverrideReason ? { time_window_override_reason: safeTimeWindowOverrideReason } : {}),
    ...(confirmTimeWindowOverride ? { confirm_time_window_override: true } : {}),
    ...(confirmDompeurOverride ? { confirm_dompeur_override: true } : {}),
  };
}

export function buildTruckBulkReassignmentRequestBody({
  protectAfterReassignment = true,
  targetTruckId = "",
} = {}) {
  const safeTargetTruckId = normalizeInlineText(targetTruckId);
  if (!safeTargetTruckId) {
    return null;
  }
  return {
    target_truck_id: safeTargetTruckId,
    ...buildDispatchProtectAfterReassignmentMutationFields(protectAfterReassignment),
  };
}

export function buildDeliveryGroupMoveRequestBody({
  deliveryIds = [],
  groupStepIds = [],
  sourceGroupKey = "",
  sourceTruckId = "",
  targetTruckId = "",
  forcedPreloadedPreviousDayOrderIds = [],
  allowReturnToOptimized = false,
  allowOvertime = false,
  protectAfterReassignment = true,
  rebuildMode = "",
  timeWindowOverrideReason = "",
  confirmTimeWindowOverride = false,
  confirmDompeurOverride = false,
} = {}) {
  const resolvedDeliveryIds = (Array.isArray(deliveryIds) ? deliveryIds : [])
    .map((deliveryId) => normalizeInlineText(deliveryId))
    .filter(Boolean);
  const resolvedGroupStepIds = (Array.isArray(groupStepIds) ? groupStepIds : [])
    .map((stepId) => normalizeInlineText(stepId))
    .filter(Boolean);
  const resolvedForcedPreloadedPreviousDayOrderIds = (Array.isArray(forcedPreloadedPreviousDayOrderIds)
    ? forcedPreloadedPreviousDayOrderIds
    : []
  )
    .map((orderId) => normalizeInlineText(orderId))
    .filter(Boolean)
    .filter((orderId, index, orderIds) => orderIds.indexOf(orderId) === index);
  const safeTargetTruckId = normalizeInlineText(targetTruckId);
  if (resolvedDeliveryIds.length === 0 || (resolvedDeliveryIds.length < 2 && resolvedForcedPreloadedPreviousDayOrderIds.length === 0) || !safeTargetTruckId) {
    return null;
  }

  const body = {
    delivery_ids: resolvedDeliveryIds,
    target_truck_id: safeTargetTruckId,
  };
  const safeSourceTruckId = normalizeInlineText(sourceTruckId);
  const safeSourceGroupKey = normalizeInlineText(sourceGroupKey);
  if (safeSourceTruckId) {
    body.source_truck_id = safeSourceTruckId;
  }
  if (safeSourceGroupKey) {
    body.source_group_key = safeSourceGroupKey;
  }
  if (resolvedGroupStepIds.length > 0) {
    body.group_step_ids = resolvedGroupStepIds;
  }
  if (resolvedForcedPreloadedPreviousDayOrderIds.length > 0) {
    body.forced_preloaded_previous_day_order_ids = resolvedForcedPreloadedPreviousDayOrderIds;
  }
  if (allowReturnToOptimized) {
    body.allow_return_to_optimized = true;
  }
  if (resolvedForcedPreloadedPreviousDayOrderIds.length === 0) {
    Object.assign(body, buildDispatchProtectAfterReassignmentMutationFields(protectAfterReassignment));
  }
  if (rebuildMode) {
    Object.assign(body, buildDispatchReassignmentMutationModeFields(rebuildMode, { allowOvertime }));
  } else if (allowOvertime) {
    body.allow_overtime = true;
  }
  const safeTimeWindowOverrideReason = normalizeInlineText(timeWindowOverrideReason);
  if (safeTimeWindowOverrideReason) {
    body.time_window_override_reason = safeTimeWindowOverrideReason;
  }
  if (confirmTimeWindowOverride) {
    body.confirm_time_window_override = true;
  }
  if (confirmDompeurOverride) {
    body.confirm_dompeur_override = true;
  }
  return body;
}

export function buildEndOfDayLoadAssignmentRequestBody({
  deliveryMode = "",
  deliveryId = "",
  deliveryIds = [],
  dispatchDate = "",
  groupStepIds = [],
  markerDispatchDate = "",
  markerKey = "",
  sourceGroupKey = "",
  sourceDispatchDate = "",
  sourceTruckId = "",
  scope = "delivery",
  targetPeriod = "",
  targetDispatchDate = "",
  targetTruckId = "",
  allowReturnToOptimized = false,
  allowOvertime = false,
  timeWindowOverrideReason = "",
  confirmTimeWindowOverride = false,
} = {}) {
  const resolvedDeliveryIds = (Array.isArray(deliveryIds) ? deliveryIds : [])
    .map((candidateDeliveryId) => normalizeInlineText(candidateDeliveryId))
    .filter(Boolean);
  const safeDeliveryId = normalizeInlineText(deliveryId);
  if (safeDeliveryId && !resolvedDeliveryIds.includes(safeDeliveryId)) {
    resolvedDeliveryIds.unshift(safeDeliveryId);
  }

  const safeTargetTruckId = normalizeInlineText(targetTruckId);
  const safeTargetDispatchDate = normalizeInlineText(targetDispatchDate);
  const safeScope = normalizeInlineText(scope).toLowerCase() === "group" ? "group" : "delivery";
  const safeDeliveryMode = normalizeInlineText(deliveryMode);
  const safeMarkerDispatchDate = normalizeInlineText(markerDispatchDate);
  const safeMarkerKey = normalizeInlineText(markerKey);
  const safeSourceDispatchDate = normalizeInlineText(sourceDispatchDate);
  const safeTargetPeriod = normalizeInlineText(targetPeriod);
  const resolvedScope = safeScope === "group" && resolvedDeliveryIds.length < 2
    ? "delivery"
    : safeScope;
  if (!safeTargetTruckId || !safeTargetDispatchDate || resolvedDeliveryIds.length === 0) {
    return null;
  }

  const body = {
    delivery_ids: resolvedDeliveryIds,
    scope: resolvedScope,
    target_dispatch_date: safeTargetDispatchDate,
    target_truck_id: safeTargetTruckId,
  };
  const safeDispatchDate = normalizeInlineText(dispatchDate);
  const safeSourceTruckId = normalizeInlineText(sourceTruckId);
  const safeSourceGroupKey = normalizeInlineText(sourceGroupKey);
  const resolvedGroupStepIds = (Array.isArray(groupStepIds) ? groupStepIds : [])
    .map((stepId) => normalizeInlineText(stepId))
    .filter(Boolean);

  if (resolvedDeliveryIds.length === 1) {
    body.delivery_id = resolvedDeliveryIds[0];
  }
  if (safeDeliveryMode) {
    body.delivery_mode = safeDeliveryMode;
  }
  if (safeDispatchDate) {
    body.dispatch_date = safeDispatchDate;
  }
  if (safeMarkerKey) {
    body.marker_key = safeMarkerKey;
  }
  if (safeMarkerDispatchDate) {
    body.marker_dispatch_date = safeMarkerDispatchDate;
  }
  if (safeSourceDispatchDate) {
    body.source_dispatch_date = safeSourceDispatchDate;
  }
  if (safeSourceTruckId) {
    body.source_truck_id = safeSourceTruckId;
  }
  if (safeSourceGroupKey) {
    body.source_group_key = safeSourceGroupKey;
  }
  if (safeTargetPeriod) {
    body.target_period = safeTargetPeriod;
  }
  if (resolvedGroupStepIds.length > 0) {
    body.group_step_ids = resolvedGroupStepIds;
  }
  if (allowReturnToOptimized) {
    body.allow_return_to_optimized = true;
  }
  if (allowOvertime) {
    body.allow_overtime = true;
  }
  const safeTimeWindowOverrideReason = normalizeInlineText(timeWindowOverrideReason);
  if (safeTimeWindowOverrideReason) {
    body.time_window_override_reason = safeTimeWindowOverrideReason;
  }
  if (confirmTimeWindowOverride) {
    body.confirm_time_window_override = true;
  }
  return body;
}

export function buildEveningStandardTransferRequestBody({
  confirmDompeurOverride = false,
  destinationKind = "truck",
  deliveryId = "",
  deliveryIds = [],
  dispatchDate = "",
  groupStepIds = [],
  protectAfterReassignment = true,
  rebuildMode = DISPATCH_REASSIGNMENT_MODE_COMPLETE,
  scope = "delivery",
  sourceGroupKey = "",
  sourceTruckId = "",
  targetPeriod = "",
  targetTruckId = "",
} = {}) {
  const resolvedDeliveryIds = (Array.isArray(deliveryIds) ? deliveryIds : [])
    .map((candidateDeliveryId) => normalizeInlineText(candidateDeliveryId))
    .filter(Boolean)
    .filter((candidateDeliveryId, index, allDeliveryIds) => allDeliveryIds.indexOf(candidateDeliveryId) === index);
  const safeDeliveryId = normalizeInlineText(deliveryId);
  if (safeDeliveryId && !resolvedDeliveryIds.includes(safeDeliveryId)) {
    resolvedDeliveryIds.unshift(safeDeliveryId);
  }

  const safeDispatchDate = normalizeInlineText(dispatchDate);
  const safeTargetPeriod = normalizeInlineText(targetPeriod).toLowerCase();
  const safeTargetTruckId = normalizeInlineText(targetTruckId);
  const safeScope = normalizeInlineText(scope).toLowerCase() === "group" ? "group" : "delivery";
  const resolvedScope = safeScope === "group" && resolvedDeliveryIds.length < 2
    ? "delivery"
    : safeScope;
  const isSuspendedDestination = destinationKind === "suspended";
  if (!safeDispatchDate || (!isSuspendedDestination && !safeTargetTruckId) || resolvedDeliveryIds.length === 0) {
    return null;
  }

  const body = {
    delivery_ids: resolvedDeliveryIds,
    dispatch_date: safeDispatchDate,
    ...buildDispatchProtectAfterReassignmentMutationFields(protectAfterReassignment),
    ...buildDispatchReassignmentMutationModeFields(rebuildMode),
    scope: resolvedScope,
    ...(isSuspendedDestination ? { destination_kind: "suspended" } : { target_truck_id: safeTargetTruckId }),
  };
  const safeSourceTruckId = normalizeInlineText(sourceTruckId);
  const safeSourceGroupKey = normalizeInlineText(sourceGroupKey);
  const resolvedGroupStepIds = (Array.isArray(groupStepIds) ? groupStepIds : [])
    .map((stepId) => normalizeInlineText(stepId))
    .filter(Boolean);
  if (resolvedDeliveryIds.length === 1) {
    body.delivery_id = resolvedDeliveryIds[0];
  }
  if (safeSourceTruckId) {
    body.source_truck_id = safeSourceTruckId;
  }
  if (safeSourceGroupKey) {
    body.source_group_key = safeSourceGroupKey;
  }
  if (safeTargetPeriod === "day" || safeTargetPeriod === "evening") {
    body.target_period = safeTargetPeriod;
  }
  if (resolvedGroupStepIds.length > 0) {
    body.group_step_ids = resolvedGroupStepIds;
  }
  if (confirmDompeurOverride) {
    body.confirm_dompeur_override = true;
  }
  return body;
}

export function buildPreloadedPreviousDayRequestBody({
  groupStepIds = [],
  orderIds = [],
  rebuildMode = DISPATCH_REASSIGNMENT_MODE_COMPLETE,
  scope = "order",
  sourceGroupKey = "",
  sourceTruckId = "",
} = {}) {
  const resolvedOrderIds = (Array.isArray(orderIds) ? orderIds : [])
    .map((orderId) => normalizeInlineText(orderId))
    .filter(Boolean)
    .filter((orderId, index, allOrderIds) => allOrderIds.indexOf(orderId) === index);
  const safeScope = normalizeInlineText(scope).toLowerCase() === "group" ? "group" : "order";
  const safeSourceTruckId = normalizeInlineText(sourceTruckId);
  if (!safeSourceTruckId || resolvedOrderIds.length === 0 || (safeScope === "order" && resolvedOrderIds.length !== 1)) {
    return null;
  }
  if (safeScope === "group" && resolvedOrderIds.length < 2) {
    return null;
  }

  const body = {
    order_ids: resolvedOrderIds,
    scope: safeScope,
    source_truck_id: safeSourceTruckId,
    ...buildDispatchReassignmentMutationModeFields(rebuildMode),
  };
  const safeSourceGroupKey = normalizeInlineText(sourceGroupKey);
  const resolvedGroupStepIds = (Array.isArray(groupStepIds) ? groupStepIds : [])
    .map((stepId) => normalizeInlineText(stepId))
    .filter(Boolean);
  if (safeScope === "group" && safeSourceGroupKey) {
    body.source_group_key = safeSourceGroupKey;
  }
  if (safeScope === "group" && resolvedGroupStepIds.length > 0) {
    body.group_step_ids = resolvedGroupStepIds;
  }
  return body;
}

export function buildPreloadedPreviousDayRemovalRequestBody(payload = {}) {
  return buildPreloadedPreviousDayRequestBody(payload);
}

export function buildEndOfDayLoadRemovalRequestBody({
  dispatchDate = "",
  isSourcePreload = false,
  markerDispatchDate = "",
  markerKey = "",
  removalMode = "",
  sourceDispatchDate = "",
  targetDispatchDate = "",
} = {}) {
  const safeMarkerKey = normalizeInlineText(markerKey);
  if (!safeMarkerKey) {
    return null;
  }

  const body = {
    marker_key: safeMarkerKey,
  };
  const safeDispatchDate = normalizeInlineText(dispatchDate);
  const safeMarkerDispatchDate = normalizeInlineText(markerDispatchDate);
  const safeRemovalMode = normalizeInlineText(removalMode);
  const safeSourceDispatchDate = normalizeInlineText(sourceDispatchDate);
  const safeTargetDispatchDate = normalizeInlineText(targetDispatchDate);
  if (safeDispatchDate) {
    body.dispatch_date = safeDispatchDate;
  }
  if (safeMarkerDispatchDate) {
    body.marker_dispatch_date = safeMarkerDispatchDate;
  }
  if (safeSourceDispatchDate) {
    body.source_dispatch_date = safeSourceDispatchDate;
  }
  if (safeTargetDispatchDate) {
    body.target_dispatch_date = safeTargetDispatchDate;
  }
  if (safeRemovalMode) {
    body.removal_mode = safeRemovalMode;
  }
  if (isSourcePreload) {
    body.is_source_preload = true;
  }
  return body;
}

function getTimeWindowStepLabel(stepType) {
  if (stepType === "loading") {
    return "Chargement";
  }

  if (stepType === "delivery") {
    return "Déchargement";
  }

  return "Étape";
}

function buildTimeWindowDiagnosticSummaryLines(diagnostics = []) {
  return (Array.isArray(diagnostics) ? diagnostics : []).reduce((items, diagnostic) => {
    if (!diagnostic || typeof diagnostic !== "object") {
      return items;
    }

    const detail = normalizeInlineText(diagnostic.detail);
    if (!detail) {
      return items;
    }

    const priority = normalizeInlineText(diagnostic.priority).toUpperCase();
    const label = [getTimeWindowStepLabel(diagnostic.step_type), priority].filter(Boolean).join(" • ");
    items.push(label ? `${label}: ${detail}` : detail);
    return items;
  }, []).slice(0, 4);
}

function isLocalRebuildCompatibility(targetCompatibility = null) {
  const reasonCode = normalizeInlineText(targetCompatibility?.reason_code).toLowerCase();
  return reasonCode === "local_rebuild_check_pending" || reasonCode === "local_rebuild_required";
}

export function resolveTimeWindowRequirementState(targetCompatibility = null) {
  if (isLocalRebuildCompatibility(targetCompatibility)) {
    return null;
  }

  const mode = getTruckCompatibilityTimeWindowMode(targetCompatibility);
  if (!["override_required", "soft"].includes(mode)) {
    return null;
  }

  const detail = normalizeInlineText(targetCompatibility?.detail)
    || normalizeInlineText(targetCompatibility?.reason_label)
    || "Diagnostic de fenêtre horaire disponible.";
  const diagnosticLines = buildTimeWindowDiagnosticSummaryLines(targetCompatibility?.time_window_diagnostics);

  return {
    description: [detail, ...diagnosticLines].filter(Boolean).join(" "),
    mode,
    reasonLabel: normalizeInlineText(targetCompatibility?.reason_label),
  };
}

function formatTimeWindowClock(value) {
  const match = /(\d{1,2}):(\d{2})/.exec(normalizeInlineText(value));
  if (!match) {
    return "";
  }
  return match[2] === "00" ? `${Number(match[1])} h` : `${Number(match[1])} h ${match[2]}`;
}

function formatTimeWindowGap(minutes) {
  const total = Math.round(Number(minutes) || 0);
  return total >= 60 ? `${Math.floor(total / 60)} h${total % 60 ? ` ${total % 60} min` : ""}` : `${total} min`;
}

function findPlannedStepTime(state, { deliveryId = "", orderId = "", isLoading = false } = {}) {
  const matchesStep = (step) => (isLoading
    ? step?.step_type === "loading" && normalizeInlineText(step?.order_id) === orderId
    : step?.step_type === "delivery" && normalizeInlineText(step?.delivery_id) === deliveryId);
  for (const assignment of Array.isArray(state?.resultsData?.assignments) ? state.resultsData.assignments : []) {
    const steps = [
      ...(Array.isArray(assignment?.operational_steps) ? assignment.operational_steps : []),
      ...(Array.isArray(assignment?.trips) ? assignment.trips : []).flatMap((trip) => (Array.isArray(trip?.operational_steps) ? trip.operational_steps : [])),
    ];
    const step = steps.find(matchesStep);
    if (step) {
      return normalizeInlineText(step.scheduled_at || step.arrival_at);
    }
  }
  return "";
}

function findDeliveryItem(state, { deliveryId = "", orderId = "" } = {}) {
  const items = [
    ...(state?.deliveryItemsByOrderId instanceof Map ? state.deliveryItemsByOrderId.values() : []),
    ...(Array.isArray(state?.resultsData?.suspended_deliveries) ? state.resultsData.suspended_deliveries : []),
  ];
  return items.find((item) => (deliveryId
    ? normalizeInlineText(item?.delivery_id) === deliveryId
    : normalizeInlineText(item?.order_id) === orderId)) || null;
}

// Fenêtre P2/P3 dépassée : dit en clair quelle livraison sort de sa fenêtre, de combien, et si elle était déjà sur le camion cible.
export function buildTimeWindowConfirmationContent(state, {
  confirmLabel = "Réaffecter",
  deliveryIds = [],
  targetCompatibility = null,
  targetTruckName = "",
  title = "",
} = {}) {
  const movedItems = deliveryIds
    .map((deliveryId) => findDeliveryItem(state, { deliveryId: normalizeInlineText(deliveryId) }))
    .filter(Boolean);
  const movedDeliveryIds = new Set(movedItems.map((item) => normalizeInlineText(item.delivery_id)));
  const movedOrderIds = new Set(movedItems.map((item) => normalizeInlineText(item.order_id)));
  const groups = new Map();
  for (const diagnostic of Array.isArray(targetCompatibility?.time_window_diagnostics) ? targetCompatibility.time_window_diagnostics : []) {
    if (diagnostic?.is_violated !== true) {
      continue;
    }
    const isLoading = diagnostic.step_type === "loading";
    const deliveryId = normalizeInlineText(diagnostic.delivery_id);
    const orderId = normalizeInlineText(diagnostic.order_id);
    const item = findDeliveryItem(state, isLoading ? { orderId } : { deliveryId });
    const clientName = normalizeInlineText(isLoading ? item?.loading_client_name : item?.client_name) || orderId;
    const isMoved = isLoading ? movedOrderIds.has(orderId) : movedDeliveryIds.has(deliveryId);
    const isLate = Number(diagnostic.late_minutes) > 0;
    const key = [isLoading, clientName, isMoved, isLate].join("|");
    const group = groups.get(key) || { clientName, count: 0, gapMinutes: 0, isLate, isLoading, isMoved, plannedAt: "" };
    group.count += 1;
    group.priority = normalizeInlineText(diagnostic.priority).toUpperCase();
    group.scheduledAt = diagnostic.scheduled_at;
    group.windowLabel = `${formatTimeWindowClock(diagnostic.effective_window_start)} à ${formatTimeWindowClock(diagnostic.effective_window_end)}`;
    group.gapMinutes = Math.max(group.gapMinutes, Number(isLate ? diagnostic.late_minutes : diagnostic.early_minutes) || 0);
    group.plannedAt = group.plannedAt || (isMoved ? "" : findPlannedStepTime(state, { deliveryId, isLoading, orderId }));
    groups.set(key, group);
  }

  const truckLabel = normalizeInlineText(targetTruckName) || "ce camion";
  const lines = [...groups.values()].flatMap((group) => {
    const plural = group.count > 1;
    const subject = plural ? `ses ${group.count} commandes ${group.priority}` : `sa commande ${group.priority}`;
    const verb = group.isLoading ? "chargée" : "livrée";
    const movedLine = plural
      ? `Les ${group.count} commandes déplacées (${group.priority}) seraient ${verb}s à ${formatTimeWindowClock(group.scheduledAt)}.`
      : `La commande déplacée (${group.priority}) serait ${verb} à ${formatTimeWindowClock(group.scheduledAt)}.`;
    const timeLine = group.isMoved
      ? movedLine
      : `Déjà sur ${truckLabel} : ${subject} ${plural ? "passeraient" : "passerait"}${group.plannedAt ? ` de ${formatTimeWindowClock(group.plannedAt)}` : ""} à ${formatTimeWindowClock(group.scheduledAt)}.`;
    return [
      ...(groups.size > 1 ? [`${group.clientName} :`] : []),
      timeLine,
      `Fenêtre demandée : ${group.windowLabel}, soit ${formatTimeWindowGap(group.gapMinutes)} ${group.isLate ? "de retard" : "d'avance"}.`,
    ];
  });
  const [onlyGroup] = groups.values();
  const warningTitle = groups.size > 1
    ? `${groups.size} arrêts sortiraient de leur fenêtre horaire`
    : onlyGroup
      ? `${onlyGroup.isLoading ? `Le chargement chez ${onlyGroup.clientName} serait` : `${onlyGroup.clientName} serait livré`} ${onlyGroup.isLate ? "en retard" : "en avance"}`
      : "Une livraison sortirait de sa fenêtre horaire P2 ou P3";
  const movedClientNames = [...new Set(movedItems.map((item) => normalizeInlineText(item.client_name)).filter(Boolean))];
  const movedTons = movedItems.reduce((total, item) => total + (Number(item.quantity_tons) || 0), 0);
  const sourceTruckLabel = movedItems.length ? resolveDeliverySourceTruckLabel(state, movedItems[0].delivery_id) : "";
  const subjectLabel = movedItems.length > 1
    ? `${movedItems.length} commandes de ${movedClientNames.join(", ")}`
    : `La commande de ${movedClientNames[0] || "ce client"}`;
  const tonsLabel = movedTons > 0 ? ` (${movedTons.toLocaleString("fr-CA", { maximumFractionDigits: 1 })} t)` : "";
  const sourceLabel = sourceTruckLabel ? `, ${movedItems.length > 1 ? "prises" : "prise"} sur ${sourceTruckLabel}` : "";

  return {
    confirmLabel: `${confirmLabel} quand même`,
    description: movedItems.length ? `${subjectLabel}${tonsLabel}${sourceLabel}.` : "",
    impactItems: [],
    title: title || `Réaffecter vers ${truckLabel} ?`,
    warningMessage: lines.join("\n") || "Ce déplacement ferait sortir une livraison de sa fenêtre horaire P2 ou P3.",
    warningTitle,
    warningTone: "warning",
  };
}

export function resolveDompeurOverrideRequirementState(targetCompatibility = null) {
  const confirmationCode = normalizeInlineText(targetCompatibility?.confirmation_code).toLowerCase();
  const reasonCode = normalizeInlineText(targetCompatibility?.reason_code).toLowerCase();
  const requiresDompeurOverride = reasonCode === "dompeur_override_required"
    || (
      targetCompatibility?.requires_confirmation === true
      && confirmationCode === "dompeur_override"
    );
  if (!requiresDompeurOverride) {
    return null;
  }

  const description = normalizeInlineText(targetCompatibility?.detail)
    || normalizeInlineText(targetCompatibility?.reason_label)
    || "Cette réaffectation contourne la règle Dompeur.";

  return {
    description,
    reasonLabel: normalizeInlineText(targetCompatibility?.reason_label),
  };
}

function buildRequestedDompeurOverrideDetailItems({ confirmDompeurOverride = false, targetCompatibility = null } = {}) {
  const requirement = resolveDompeurOverrideRequirementState(targetCompatibility);
  if (!requirement) {
    return [];
  }

  return [{
    key: "dompeur-override",
    label: "Dompeur",
    value: confirmDompeurOverride ? "Override confirmé" : "Confirmation requise",
  }];
}

function buildPlanningMutationCheckboxFields({
  dompeurOverrideRequirement = null,
  overtimeWarningContext = null,
} = {}) {
  const fields = [];

  if (dompeurOverrideRequirement) {
    fields.push({
      key: DOMPEUR_CONFIRM_FIELD_KEY,
      label: "Confirmer l'override Dompeur",
      description: dompeurOverrideRequirement.description,
      required: true,
    });
  }

  if (overtimeWarningContext?.shouldShow) {
    fields.push({
      key: OVERTIME_CONFIRM_FIELD_KEY,
      label: "Permettre de dépasser les heures maximales",
      description: overtimeWarningContext.checkboxDescription,
    });
  }

  return fields;
}

function buildPlanningMutationTextPrompt(timeWindowRequirement = null) {
  if (timeWindowRequirement?.mode !== "override_required") {
    return null;
  }

  return {
    key: TIME_WINDOW_OVERRIDE_REASON_FIELD_KEY,
    label: "Motif obligatoire",
    description: `${timeWindowRequirement.description} Ce motif sera enregistré dans l'audit de fenêtre horaire.`,
    maxLength: 300,
    placeholder: "Décrivez la justification opérationnelle de cet override.",
    required: true,
    rows: 3,
  };
}

function shouldAutoApproveFastOvertime({
  onlyOvertimeCheckbox = false,
  overtimeWarningContext = null,
  rebuildMode = DISPATCH_REASSIGNMENT_MODE_COMPLETE,
} = {}) {
  return (
    normalizeDispatchReassignmentMode(rebuildMode) === DISPATCH_REASSIGNMENT_MODE_FAST
    && overtimeWarningContext?.shouldShow === true
    && onlyOvertimeCheckbox === true
  );
}

function normalizePlanningMutationDialogValues(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  return {
    checkboxValues: {},
    confirmCheckboxValue: value === true,
    textPromptValue: "",
  };
}

function readPlanningMutationCheckboxValue(dialogValues, checkboxKey, { usePrimaryFallback = false } = {}) {
  const normalizedDialogValues = normalizePlanningMutationDialogValues(dialogValues);
  if (
    checkboxKey
    && normalizedDialogValues.checkboxValues
    && typeof normalizedDialogValues.checkboxValues === "object"
    && normalizedDialogValues.checkboxValues[checkboxKey] === true
  ) {
    return true;
  }

  return usePrimaryFallback ? normalizedDialogValues.confirmCheckboxValue === true : false;
}

function readPlanningMutationTextPromptValue(dialogValues) {
  return normalizeInlineText(normalizePlanningMutationDialogValues(dialogValues).textPromptValue);
}

function readPlanningMutationTextPromptValues(dialogValues) {
  const normalizedDialogValues = normalizePlanningMutationDialogValues(dialogValues);
  return normalizedDialogValues.textPromptValues
    && typeof normalizedDialogValues.textPromptValues === "object"
    && !Array.isArray(normalizedDialogValues.textPromptValues)
    ? normalizedDialogValues.textPromptValues
    : {};
}

function buildTruckPreloadTimeTextPrompt(currentPreloadTime = "") {
  const normalizedCurrentPreloadTime = normalizeTruckPreloadTimeValue(currentPreloadTime);
  const preloadTimeLabel = formatTruckPreloadTimeLabel(normalizedCurrentPreloadTime);

  return {
    key: TRUCK_PRELOAD_TIME_FIELD_KEY,
    label: "Heure de préchargement",
    description: preloadTimeLabel
      ? `Heure actuelle : ${preloadTimeLabel}. Laissez vide pour retirer la saisie manuelle.`
      : "Saisissez l'heure manuelle à reprendre pour le dispatch courant.",
    defaultValue: normalizedCurrentPreloadTime,
    required: !normalizedCurrentPreloadTime,
    step: 60,
    type: "time",
  };
}

function normalizeTruckStartTimeValue(value) {
  const normalizedValue = normalizeTruckPreloadTimeValue(value);
  return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(normalizedValue) ? normalizedValue : "";
}

function buildTruckStartTimeTextPrompt(currentStartTime = "") {
  const normalizedCurrentStartTime = normalizeTruckStartTimeValue(currentStartTime);
  const currentStartTimeLabel = formatTruckPreloadTimeLabel(normalizedCurrentStartTime);

  return {
    key: TRUCK_START_TIME_FIELD_KEY,
    label: "Heure de début du chauffeur",
    description: currentStartTimeLabel
      ? `Heure actuelle dans Data Manager : ${currentStartTimeLabel}.`
      : "Saisissez l'heure de début maître à enregistrer dans Data Manager.",
    defaultValue: normalizedCurrentStartTime,
    openPickerOnClick: true,
    required: true,
    step: 60,
    type: "time",
    width: "compact",
  };
}

function buildTruckStartTimeConfirmationContent(truckName, {
  currentStartTime = "",
  hasOrders = false,
} = {}) {
  const safeTruckName = normalizeInlineText(truckName) || "ce camion";
  const currentStartTimeLabel = formatTruckPreloadTimeLabel(currentStartTime);

  return {
    eyebrow: "Début chauffeur",
    title: currentStartTimeLabel
      ? `Modifier l'heure de début de ${safeTruckName} ?`
      : `Ajouter l'heure de début de ${safeTruckName} ?`,
    description: "Cette heure maître sera enregistrée dans Data Manager : fiche du chauffeur affecté.",
    contentDensity: "compact",
    impactItems: hasOrders
      ? [
        "Après l'enregistrement, seul ce camion sera réoptimisé; aucune commande ne pourra passer sur un autre camion.",
        "Si le recalcul est refusé, l'heure restera enregistrée et le changement sera visible dans Mise à jour.",
      ]
      : [
        "Ce camion est vide : rien ne sera recalculé.",
      ],
    panelSize: "narrow",
    confirmLabel: hasOrders ? "Enregistrer et optimiser" : "Enregistrer",
    cancelLabel: "Annuler",
    tone: "accent",
  };
}

function normalizeTruckMaxWorkHoursValue(value) {
  const normalizedValue = typeof value === "string" ? value.trim() : value;
  if (normalizedValue === "" || normalizedValue == null) {
    return null;
  }

  const numericValue = Number(normalizedValue);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }
  return numericValue;
}

function formatTruckMaxWorkHoursLabel(value) {
  const normalizedValue = normalizeTruckMaxWorkHoursValue(value);
  return normalizedValue == null ? "" : `${normalizedValue} h`;
}

function normalizeTruckCapacityTonsValue(value) {
  const normalizedValue = typeof value === "string" ? value.trim() : value;
  if (normalizedValue === "" || normalizedValue == null) {
    return null;
  }

  const numericValue = Number(normalizedValue);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return null;
  }
  return numericValue;
}

function formatTruckCapacityTonsLabel(value) {
  const normalizedValue = normalizeTruckCapacityTonsValue(value);
  return normalizedValue == null ? "" : `${normalizedValue} t`;
}

// Fiche qui recevra la valeur : le chauffeur affecté pour les heures, le camion pour la capacité.
function formatTruckSettingsSourceLabel(driverName = "") {
  const safeDriverName = normalizeInlineText(driverName);
  return safeDriverName ? `Fiche de ${safeDriverName}` : "Data Manager";
}

// Dans une carte étroite, la ligne se coupe avant la valeur, jamais dans « 06 h 30 ».
function formatTruckSettingsSourceValue(sourceLabel, valueLabel) {
  return `${sourceLabel}\u00a0: ${String(valueLabel).replace(/ /g, "\u00a0")}.`;
}

// Champ grisé avec sa bulle : remorque « Camion à choisir » pour les heures, droit absent.
function disabledTextPromptField(reason = "") {
  return reason ? { disabled: true, title: reason } : {};
}

function buildTruckTimeSettingsTextPromptFields({
  currentCapacityTons = null,
  currentMaxWorkHours = null,
  currentStartTime = "",
  hoursDisabledReason = "",
  trailerSettingsDisabledReason = "",
  driverName = "",
  trailerName = "",
} = {}) {
  const normalizedCurrentStartTime = normalizeTruckStartTimeValue(currentStartTime);
  const normalizedCurrentMaxWorkHours = normalizeTruckMaxWorkHoursValue(currentMaxWorkHours);
  const normalizedCurrentCapacityTons = normalizeTruckCapacityTonsValue(currentCapacityTons);
  const hoursSourceLabel = formatTruckSettingsSourceLabel(driverName);
  const safeTrailerName = normalizeInlineText(trailerName);
  const capacitySourceLabel = safeTrailerName ? `Fiche de la remorque ${safeTrailerName}` : "Data Manager";

  return [
    {
      key: TRUCK_TIME_SETTINGS_START_FIELD_KEY,
      label: "Heure début",
      defaultValue: normalizedCurrentStartTime,
      description: normalizedCurrentStartTime
        ? formatTruckSettingsSourceValue(hoursSourceLabel, formatTruckPreloadTimeLabel(normalizedCurrentStartTime))
        : "Aucune heure de début n'est définie dans Data Manager.",
      openPickerOnClick: true,
      required: Boolean(normalizedCurrentStartTime),
      step: 60,
      type: "time",
      ...disabledTextPromptField(hoursDisabledReason),
    },
    {
      key: TRUCK_TIME_SETTINGS_MAX_FIELD_KEY,
      label: "Heures max",
      defaultValue: normalizedCurrentMaxWorkHours == null ? "" : normalizedCurrentMaxWorkHours,
      description: normalizedCurrentMaxWorkHours == null
        ? "Saisissez la durée maximale de travail du chauffeur."
        : formatTruckSettingsSourceValue(hoursSourceLabel, formatTruckMaxWorkHoursLabel(normalizedCurrentMaxWorkHours)),
      min: 0.1,
      positive: true,
      required: true,
      step: 0.1,
      type: "number",
      ...disabledTextPromptField(hoursDisabledReason),
    },
    {
      key: TRUCK_TIME_SETTINGS_CAPACITY_FIELD_KEY,
      label: "Capacité t",
      defaultValue: normalizedCurrentCapacityTons == null ? "" : normalizedCurrentCapacityTons,
      description: normalizedCurrentCapacityTons == null
        ? "Saisissez la capacité de la remorque."
        : formatTruckSettingsSourceValue(capacitySourceLabel, formatTruckCapacityTonsLabel(normalizedCurrentCapacityTons)),
      min: 0.1,
      positive: true,
      required: true,
      step: 0.1,
      type: "number",
      ...disabledTextPromptField(trailerSettingsDisabledReason),
    },
  ];
}

function buildTruckTimeSettingsConfirmationContent(truckName, { hasOrders = false, savesOnly = false } = {}) {
  const safeTruckName = normalizeInlineText(truckName) || "ce camion";

  return {
    eyebrow: "Paramètres chauffeur et camion",
    title: `Modifier les paramètres du chauffeur et du camion de ${safeTruckName} ?`,
    description: "Ces paramètres seront enregistrés dans Data Manager : heures dans la fiche du chauffeur affecté, capacité et rayon Soir dans celle de la remorque.",
    contentDensity: "compact",
    impactItems: savesOnly
      ? [
        "Ce camion du Soir n'est pas recalculé depuis le Dispatch Jour.",
        "Le changement sera visible dans Mise à jour.",
      ]
      : hasOrders
      ? [
        "Après l'enregistrement, seul ce camion sera recalculé.",
        "Si le recalcul est refusé, les paramètres resteront enregistrés et le changement sera visible dans Mise à jour.",
      ]
      : [
        "Ce camion est vide : rien ne sera recalculé.",
      ],
    panelSize: "narrow",
    confirmLabel: hasOrders ? "Enregistrer et recalculer" : "Enregistrer",
    cancelLabel: "Annuler",
    tone: "accent",
  };
}

function buildTruckPreloadTimeDetailItems(preloadTime = "") {
  const normalizedPreloadTime = normalizeTruckPreloadTimeValue(preloadTime);

  return [{
    key: "truck-preload-time",
    label: "Heure preload",
    value: normalizedPreloadTime ? formatTruckPreloadTimeLabel(normalizedPreloadTime) : "Retirée",
  }];
}

export function buildTruckPreloadTimeMutationRequest(truckId, preloadTime = "") {
  const safeTruckId = normalizeInlineText(truckId);
  const normalizedPreloadTime = normalizeTruckPreloadTimeValue(preloadTime);

  return {
    url: safeTruckId ? `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/preload-time` : "",
    body: {
      preload_time: normalizedPreloadTime || null,
    },
    actionName: normalizedPreloadTime ? "set_truck_preload_time" : "clear_truck_preload_time",
  };
}

export function createTruckPreloadTimeClearConfirmationDialog({
  content = {},
  dismissConfirmation = null,
  submitPreloadTime = null,
} = {}) {
  const dismiss = typeof dismissConfirmation === "function" ? dismissConfirmation : () => {};
  const submit = typeof submitPreloadTime === "function" ? submitPreloadTime : () => {};

  return {
    ...content,
    isOpen: true,
    onCancel: dismiss,
    onClose: dismiss,
    onConfirm: () => {
      dismiss();
      return submit(null);
    },
  };
}

function buildRequestedTimeWindowDetailItems({ targetCompatibility = null, timeWindowOverrideReason = "" } = {}) {
  const requirement = resolveTimeWindowRequirementState(targetCompatibility);
  if (!requirement) {
    return [];
  }

  const detailItems = [{
    key: "requested-time-window-mode",
    label: "Fenêtre horaire",
    value: requirement.mode === "override_required" ? "Override justifié" : "Écart accepté",
  }];

  const reasonValue = normalizeInlineText(timeWindowOverrideReason);
  if (reasonValue) {
    detailItems.push({
      key: "requested-time-window-reason",
      label: "Motif",
      value: reasonValue,
    });
  }

  const reasonLabel = normalizeInlineText(targetCompatibility?.reason_label);
  if (reasonLabel) {
    detailItems.push({
      key: "requested-time-window-diagnostic",
      label: "Diagnostic",
      value: reasonLabel,
    });
  }

  return detailItems;
}

function findPersistedTimeWindowOverride(resultPayload, deliveryId) {
  const safeDeliveryId = normalizeInlineText(deliveryId);
  if (!safeDeliveryId || !resultPayload || typeof resultPayload !== "object") {
    return null;
  }

  for (const assignment of Array.isArray(resultPayload.assignments) ? resultPayload.assignments : []) {
    const deliveryItem = Array.isArray(assignment?.delivery_items)
      ? assignment.delivery_items.find((item) => normalizeInlineText(item?.delivery_id) === safeDeliveryId) || null
      : null;
    if (deliveryItem?.time_window_override && typeof deliveryItem.time_window_override === "object") {
      return deliveryItem.time_window_override;
    }

    const assignmentStep = Array.isArray(assignment?.operational_steps)
      ? assignment.operational_steps.find((step) => normalizeInlineText(step?.delivery_id) === safeDeliveryId) || null
      : null;
    if (assignmentStep?.time_window_override && typeof assignmentStep.time_window_override === "object") {
      return assignmentStep.time_window_override;
    }

    for (const trip of Array.isArray(assignment?.trips) ? assignment.trips : []) {
      const tripStep = Array.isArray(trip?.operational_steps)
        ? trip.operational_steps.find((step) => normalizeInlineText(step?.delivery_id) === safeDeliveryId) || null
        : null;
      if (tripStep?.time_window_override && typeof tripStep.time_window_override === "object") {
        return tripStep.time_window_override;
      }
    }
  }

  const suspendedItem = Array.isArray(resultPayload?.suspended_deliveries)
    ? resultPayload.suspended_deliveries.find((item) => normalizeInlineText(item?.delivery_id) === safeDeliveryId) || null
    : null;
  return suspendedItem?.time_window_override && typeof suspendedItem.time_window_override === "object"
    ? suspendedItem.time_window_override
    : null;
}

function buildPersistedTimeWindowDetailItems(resultPayload, deliveryId) {
  const timeWindowOverride = findPersistedTimeWindowOverride(resultPayload, deliveryId);
  if (!timeWindowOverride) {
    return [];
  }

  const mode = normalizeInlineText(timeWindowOverride.mode).toLowerCase();
  const detailItems = [{
    key: "persisted-time-window-mode",
    label: "Fenêtre horaire",
    value: mode === "override_required" ? "Override justifié" : "Confirmation simple",
  }];

  const reasonValue = normalizeInlineText(timeWindowOverride.reason);
  if (reasonValue) {
    detailItems.push({
      key: "persisted-time-window-reason",
      label: "Motif",
      value: reasonValue,
    });
  }

  const reasonLabel = normalizeInlineText(timeWindowOverride.reason_label) || normalizeInlineText(timeWindowOverride.detail);
  if (reasonLabel) {
    detailItems.push({
      key: "persisted-time-window-diagnostic",
      label: "Diagnostic",
      value: reasonLabel,
    });
  }

  return detailItems;
}

function formatOvertimeUtilizationMessage(workloadMinutes, maxWorkMinutes, utilizationRatio) {
  const workloadLabel = formatDurationWarningLabel(workloadMinutes);
  const maxWorkLabel = formatDurationWarningLabel(maxWorkMinutes);
  const utilizationPercent = Math.round((Number.isFinite(utilizationRatio) ? utilizationRatio : 0) * 100);
  return `Charge actuelle ${workloadLabel} sur ${maxWorkLabel} (${utilizationPercent} %).`;
}

function buildSyncedPlanningResultPayload(resultPayload, planningDiagnostics) {
  if (!resultPayload || typeof resultPayload !== "object" || Array.isArray(resultPayload)) {
    return resultPayload;
  }

  if (!planningDiagnostics || typeof planningDiagnostics !== "object" || Array.isArray(planningDiagnostics)) {
    return resultPayload;
  }

  return {
    ...resultPayload,
    _planning_diagnostics: planningDiagnostics,
    diagnostics: planningDiagnostics,
  };
}

export function resolvePlanningPerformanceActionName(actionName, {
  isUndo = false,
} = {}) {
  const safeActionName = normalizeInlineText(actionName);
  return isUndo === true && safeActionName
    ? `undo_${safeActionName}`
    : safeActionName;
}

export function buildDeliveryMoveHighlightedStepKeys({ deliveryId = "", orderIds = [] } = {}) {
  return [
    getDeliveryHighlightKey(deliveryId),
    ...(Array.isArray(orderIds) ? orderIds : []).map((orderId) => getLoadingOrderHighlightKey(orderId)),
  ].filter((stepKey, index, stepKeys) => stepKey && stepKeys.indexOf(stepKey) === index);
}

const DISPATCH_GANTT_RESYNC_ACTIONS = new Set([
  "end_of_day_load_assignment",
  "end_of_day_load_removal",
  "gantt_source_backed_save",
  "move_delivery",
  "move_delivery_group",
  "remove_preloaded_previous_day_assignment",
  "reassign_truck_orders",
  "refresh_truck_data_preserve_order_auto",
  "reactivate",
  "preloaded_previous_day_assignment",
  "route_alternative_apply",
  "route_alternative_remove",
  "segment_buffer",
  "segment_reorder",
  "suspend",
  "suspend_delivery_group",
]);

const DELIVERY_LINKED_LOADING_HIGHLIGHT_ACTIONS = new Set([
  "confirm",
  "freeze",
  "move_delivery",
  "reactivate",
  "release",
  "suspend",
  "suspend_from_error",
  "unconfirm",
  "unfreeze",
]);

const DELIVERY_GROUP_LINKED_LOADING_HIGHLIGHT_ACTIONS = new Set([
  "confirm_delivery_group",
  "evening_standard_transfer",
  "freeze_delivery_group",
  "move_delivery_group",
  "release_delivery_group",
  "suspend_delivery_group",
  "unconfirm_delivery_group",
  "unfreeze_delivery_group",
]);

function requiresDispatchGanttResync(actionName = "") {
  return DISPATCH_GANTT_RESYNC_ACTIONS.has(normalizeInlineText(actionName));
}

async function resolveDispatchMutationResultPayload({
  actionName,
  allowMissingResultPayload = false,
  body = null,
  deliveryId = "",
  diagnosticRecord = null,
  payload = null,
  skipWorkspaceSync = false,
}) {
  if (skipWorkspaceSync) {
    return null;
  }

  if (payload && typeof payload.result_payload === "object") {
    if (isDeliveryMovePayloadSynced({
      actionName,
      body,
      deliveryId,
      resultPayload: payload.result_payload,
    })) {
      return payload.result_payload;
    }
  }

  if (allowMissingResultPayload) {
    return null;
  }

  if (!requiresDispatchGanttResync(actionName)) {
    return undefined;
  }

  invalidatePageDataCacheEntries([RESULTS_LATEST_API_URL]);
  invalidatePageDataCacheEntries(PLANNING_MUTATION_INVALIDATED_API_URLS);
  invalidateShellDataCache();

  diagnosticRecord?.increment("results_latest_fallback");
  diagnosticRecord?.mark("results_latest_fallback_start");
  try {
    const fetchedPayload = await loadPageData(getForcedResultsLatestApiUrl(), { force: true });
    if (fetchedPayload && typeof fetchedPayload === "object" && !Array.isArray(fetchedPayload)) {
      return fetchedPayload;
    }
  } finally {
    diagnosticRecord?.mark("results_latest_fallback_end");
  }

  return undefined;
}

function buildResolvedDispatchMutationResultPayload({
  actionName,
  currentState,
  planningDiagnostics = null,
  resultPayload,
}) {
  const syncedResultPayloadBase = buildSyncedPlanningResultPayload(resultPayload, planningDiagnostics);
  const syncedResultPayload = currentState.resultsData?.ui_dispatch_archive_open === true
    ? {
      ...syncedResultPayloadBase,
      ui_dispatch_archive_open: true,
    }
    : syncedResultPayloadBase;

  return preserveOperationalFreshnessForProtectionRemoval(
    currentState.resultsData,
    syncedResultPayload,
    actionName,
  );
}

function buildDispatchMutationWorkspaceSelectionContext({
  actionName,
  body = null,
  currentState,
  deliveryId = null,
  explicitPreferredAssignmentId = null,
  loadingStepId = null,
  resolvedResultPayload,
}) {
  const linkedStepTransition = loadingStepId && (actionName === "confirm_loading" || actionName === "unconfirm_loading")
    ? resolveLoadingActivationTransition(currentState.resultsData, resolvedResultPayload, loadingStepId)
    : deliveryId && actionName === "unconfirm"
      ? resolveDeliveryLinkedLoadingTransition(currentState.resultsData, resolvedResultPayload, deliveryId)
      : null;
  const deliveryActivationTransition = deliveryId && DELIVERY_LINKED_LOADING_HIGHLIGHT_ACTIONS.has(actionName)
    ? resolveDeliveryLinkedLoadingTransition(currentState.resultsData, resolvedResultPayload, deliveryId)
    : DELIVERY_GROUP_LINKED_LOADING_HIGHLIGHT_ACTIONS.has(actionName) && Array.isArray(body?.delivery_ids)
      ? resolveDeliveryGroupLinkedLoadingTransition(
        currentState.resultsData,
        resolvedResultPayload,
        body.delivery_ids,
        { stableOrderKeysOnly: actionName === "evening_standard_transfer" },
      )
      : null;
  const planningToast = deliveryId && (actionName === "suspend" || actionName === "reactivate")
    ? buildDeliveryReassignmentToast(currentState.resultsData, resolvedResultPayload, deliveryId, actionName) || null
    : null;
  const targetAssignmentId = planningToast?.targetTruckId
    || (body && typeof body.target_truck_id === "string" ? body.target_truck_id : null)
    || (body && typeof body.truckId === "string" && actionName === "segment_reorder" ? body.truckId : null);

  return {
    deliveryActivationTransition,
    linkedStepTransition,
    planningToast,
    preferredAssignmentId: explicitPreferredAssignmentId || targetAssignmentId || linkedStepTransition?.preferredAssignmentId || null,
    preferredDeliveryId: deliveryId || linkedStepTransition?.preferredDeliveryId || null,
  };
}

function isDiagnosticObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function capturePlanningMutationErrorDiagnostics(state, payload, planningDiagnostics) {
  if (!state || !isDiagnosticObject(planningDiagnostics)) {
    return null;
  }

  const resultsData = isDiagnosticObject(state.resultsData) ? state.resultsData : {};
  const currentPlanningSummary = isDiagnosticObject(resultsData.planning_summary)
    ? resultsData.planning_summary
    : {};
  const payloadPlanningSummary = isDiagnosticObject(payload?.planning_summary)
    ? payload.planning_summary
    : {};
  const dispatchDate = normalizeInlineText(payload?.dispatch_date)
    || normalizeInlineText(payloadPlanningSummary.dispatch_date)
    || normalizeInlineText(resultsData.dispatch_date)
    || normalizeInlineText(currentPlanningSummary.dispatch_date)
    || getCurrentDispatchDateFromLocation();
  return captureLastReassignmentDiagnosticPayload(state, {
    _planning_diagnostics: planningDiagnostics,
    dispatch_date: dispatchDate,
    planning_summary: dispatchDate ? { dispatch_date: dispatchDate } : currentPlanningSummary,
  });
}

function hasPlanningDiagnosticStage(planningDiagnostics, stageName) {
  if (!isDiagnosticObject(planningDiagnostics?.stages_ms)) {
    return false;
  }

  const stageValue = planningDiagnostics.stages_ms[stageName];
  return typeof stageValue === "number" && Number.isFinite(stageValue) && stageValue >= 0;
}

function resolvePlanningMutationExecutionMode(planningDiagnostics) {
  if (!isDiagnosticObject(planningDiagnostics)) {
    return "";
  }

  const explicitMode = normalizeInlineText(
    planningDiagnostics.move_mode
    || planningDiagnostics.suspend_mode
    || planningDiagnostics.reactivate_mode,
  ).toLowerCase();

  if (explicitMode === "natural_restoration") {
    return "state_update";
  }

  if (explicitMode.startsWith("local_patch")) {
    return "local_patch";
  }

  if (hasPlanningDiagnosticStage(planningDiagnostics, "rebuild_latest_snapshot")) {
    return "rebuild";
  }

  if (
    hasPlanningDiagnosticStage(planningDiagnostics, "local_move_delivery_patch")
    || hasPlanningDiagnosticStage(planningDiagnostics, "local_suspend_patch")
    || hasPlanningDiagnosticStage(planningDiagnostics, "local_reactivate_patch")
    || hasPlanningDiagnosticStage(planningDiagnostics, "local_order_edit_patch")
  ) {
    return "local_patch";
  }

  return "state_update";
}

function buildPlanningMutationStatusNoticeDetailItems({
  deliveryId = null,
  optimizationDiagnostics = null,
  planningDiagnostics = null,
  resultPayload = null,
  statusNoticeContent = null,
} = {}) {
  const detailItems = Array.isArray(statusNoticeContent?.successDetailItems)
    ? statusNoticeContent.successDetailItems.filter((item) => item && typeof item === "object")
    : [];
  const executionMode = resolvePlanningMutationExecutionMode(planningDiagnostics);

  let executionModeLabel = "";

  if (executionMode === "rebuild") {
    executionModeLabel = "Recalcul complet";
  } else if (executionMode === "local_patch") {
    executionModeLabel = "Patch local";
  } else if (executionMode === "state_update") {
    executionModeLabel = "Mise à jour locale";
  }

  if (executionModeLabel) {
    detailItems.push({
      key: "planning-execution-mode",
      label: "Mode",
      value: executionModeLabel,
    });
  }

  detailItems.push(...buildPersistedTimeWindowDetailItems(resultPayload, deliveryId));

  return detailItems;
}

function resolveDeliveryNoticeSubject(state, deliveryId) {
  const safeDeliveryId = normalizeInlineText(deliveryId);
  if (!safeDeliveryId) {
    return "";
  }

  const planningItem = state?.deliveryItemsByOrderId?.get?.(safeDeliveryId) || null;
  const suspendedItem = Array.isArray(state?.resultsData?.suspended_deliveries)
    ? state.resultsData.suspended_deliveries.find((item) => normalizeInlineText(item?.delivery_id) === safeDeliveryId) || null
    : null;
  const sourceItem = planningItem || suspendedItem || null;
  const orderId = normalizeInlineText(sourceItem?.order_id);
  const clientName = normalizeInlineText(sourceItem?.client_name);

  if (orderId && clientName) {
    return `Mission ${orderId} - ${clientName}`;
  }

  if (orderId) {
    return `Mission ${orderId}`;
  }

  if (clientName) {
    return clientName;
  }

  return `Livraison ${safeDeliveryId}`;
}

function appendNoticeSubject(title, subject) {
  return subject ? `${title} · ${subject}` : title;
}

const DISPATCH_DAY_CLOSED_BLOCKED_ACTIONS = new Set([
  "confirm",
  "confirm_delivery_group",
  "confirm_loading",
  "create_truck_incident",
  "delete_truck_incident",
  "freeze",
  "freeze_delivery_group",
  "move_delivery",
  "reassign_truck_orders",
  "reactivate",
  "refresh_truck_data_preserve_order_auto",
  "release",
  "route_alternative_apply",
  "route_alternative_remove",
  "segment_buffer",
  "segment_reorder",
  "suspend",
  "suspend_delivery_group",
  "unconfirm",
  "unconfirm_delivery_group",
  "unconfirm_loading",
  "unfreeze",
  "unfreeze_delivery_group",
  "update_truck_incident",
  "update_truck_start_time",
]);

function resolveDispatchDayRecordFromState(state, dispatchDate = "") {
  const safeDispatchDate = normalizeInlineText(dispatchDate) || getCurrentDispatchDateFromLocation();
  const dispatchDays = Array.isArray(state?.resultsData?.dispatch_days) ? state.resultsData.dispatch_days : [];

  return dispatchDays.find((item) => normalizeInlineText(item?.dispatch_date) === safeDispatchDate) || null;
}

function resolveDispatchDayMutationContext(state, dispatchDate = "") {
  const dispatchDayRecord = resolveDispatchDayRecordFromState(state, dispatchDate);
  const planningSummary = state?.planningSummary && typeof state.planningSummary === "object"
    ? state.planningSummary
    : {};
  const summaryDispatchDate = normalizeInlineText(planningSummary.dispatch_date);
  const currentDispatchDate = normalizeInlineText(dispatchDate) || getCurrentDispatchDateFromLocation();
  const resolvedDispatchDate = normalizeInlineText(dispatchDayRecord?.dispatch_date) || currentDispatchDate || summaryDispatchDate;
  const isClosedFromRecord = Boolean(
    dispatchDayRecord
    && (
      dispatchDayRecord.is_closed === true
      || ["archived", "closed"].includes(normalizeInlineText(dispatchDayRecord.dispatch_day_state).toLowerCase())
    )
  );
  const isClosedFromSummary = Boolean(
    (!currentDispatchDate || !summaryDispatchDate || currentDispatchDate === summaryDispatchDate)
    && (
      planningSummary.is_dispatch_day_closed === true
      || ["archived", "closed"].includes(normalizeInlineText(planningSummary.dispatch_day_state).toLowerCase())
    )
  );

  return {
    dispatchDate: resolvedDispatchDate,
    dispatchDayRecord,
    isClosed: isClosedFromRecord || isClosedFromSummary,
  };
}

export function resolvePlanningPerformanceEventScope(resultPayload = null, state = null) {
  const safeResultPayload = resultPayload && typeof resultPayload === "object"
    ? resultPayload
    : {};
  const resultPlanningSummary = safeResultPayload.planning_summary
    && typeof safeResultPayload.planning_summary === "object"
    ? safeResultPayload.planning_summary
    : {};
  const statePlanningSummary = state?.planningSummary && typeof state.planningSummary === "object"
    ? state.planningSummary
    : {};
  const payloadDispatchDate = normalizeInlineText(safeResultPayload.dispatch_date)
    || normalizeInlineText(resultPlanningSummary.dispatch_date);
  const mutationContext = resolveDispatchDayMutationContext(state, payloadDispatchDate);
  const dispatchDate = payloadDispatchDate
    || normalizeInlineText(mutationContext.dispatchDate)
    || normalizeInlineText(statePlanningSummary.dispatch_date)
    || getCurrentDispatchDateFromLocation();
  const dispatchPeriod = normalizePlanningDispatchPeriod(
    normalizeInlineText(safeResultPayload.dispatch_period)
      || normalizeInlineText(safeResultPayload.ui_dispatch_period)
      || normalizeInlineText(resultPlanningSummary.dispatch_period)
      || normalizeInlineText(statePlanningSummary.dispatch_period)
      || normalizeInlineText(mutationContext.dispatchDayRecord?.dispatch_period)
      || getCurrentDispatchPeriodFromLocation(),
  );
  return { dispatchDate, dispatchPeriod };
}

function isDispatchDayClosedBlockedAction(actionName = "") {
  return DISPATCH_DAY_CLOSED_BLOCKED_ACTIONS.has(normalizeInlineText(actionName));
}

function isClosedDispatchDayMutationError(errorMessage = "") {
  const normalizedErrorMessage = normalizeInlineText(errorMessage).toLowerCase();
  return normalizedErrorMessage.includes("est clôturée :");
}

function buildClosedDispatchDayBlockedMutationFeedback() {
  return {
    detailItems: [
      { key: "dispatch-day-status", label: "Statut", value: "Fermée" },
      { key: "dispatch-day-next-step", label: "Action requise", value: "« Actions de journée » › « Rouvrir la journée »" },
    ],
    duration: 10000,
    emphasis: "high",
    message: "Cette journée est fermée. Rouvrez-la dans « Actions de journée ».",
    statusLabel: "Action bloquée",
    title: "Journée fermée",
  };
}

function resolvePlanningMutationErrorFeedback(state, errorMessage, fallbackTitle) {
  const operatorErrorMessage = resolvePlanningMutationOperatorErrorMessage(errorMessage);
  if (isClosedDispatchDayMutationError(operatorErrorMessage)) {
    return buildClosedDispatchDayBlockedMutationFeedback();
  }

  return {
    detailItems: [],
    duration: 8000,
    emphasis: null,
    message: operatorErrorMessage,
    statusLabel: "Échec",
    title: fallbackTitle,
  };
}

function resolveTruckDisplayName(state, truckId, fallbackLabel = "") {
  const safeTruckId = normalizeInlineText(truckId);
  const safeFallbackLabel = normalizeInlineText(fallbackLabel);

  if (!safeTruckId) {
    return safeFallbackLabel;
  }

  const assignmentTruckName = normalizeInlineText(state?.assignmentsById?.get?.(safeTruckId)?.truck_name);
  if (assignmentTruckName) {
    return assignmentTruckName;
  }

  if (Array.isArray(state?.resultsData?.assignments)) {
    const matchingAssignment = state.resultsData.assignments.find(
      (assignment) => normalizeInlineText(assignment?.truck_id) === safeTruckId,
    ) || null;
    const resultsTruckName = normalizeInlineText(matchingAssignment?.truck_name);
    if (resultsTruckName) {
      return resultsTruckName;
    }
  }

  return safeFallbackLabel || safeTruckId;
}

function resolveDeliverySourceTruckLabel(state, deliveryId) {
  const safeDeliveryId = normalizeInlineText(deliveryId);
  if (!safeDeliveryId) {
    return "";
  }

  const planningItem = state?.deliveryItemsByOrderId?.get?.(safeDeliveryId) || null;
  const suspendedItem = Array.isArray(state?.resultsData?.suspended_deliveries)
    ? state.resultsData.suspended_deliveries.find((item) => normalizeInlineText(item?.delivery_id) === safeDeliveryId) || null
    : null;
  const sourceItem = planningItem || suspendedItem || null;
  const sourceTruckId = normalizeInlineText(sourceItem?.current_truck_id);

  return resolveTruckDisplayName(state, sourceTruckId);
}

function buildDeliveryMoveNoticeDetailItems(state, { deliveryId, targetTruckId = null, targetTruckName = null } = {}) {
  const safeDeliveryId = normalizeInlineText(deliveryId);
  if (!safeDeliveryId) {
    return [];
  }

  const planningItem = state?.deliveryItemsByOrderId?.get?.(safeDeliveryId) || null;
  const suspendedItem = Array.isArray(state?.resultsData?.suspended_deliveries)
    ? state.resultsData.suspended_deliveries.find((item) => normalizeInlineText(item?.delivery_id) === safeDeliveryId) || null
    : null;
  const sourceItem = planningItem || suspendedItem || null;

  const orderId = normalizeInlineText(sourceItem?.order_id);
  const orderClientName = normalizeInlineText(sourceItem?.order_client_name);
  const deliveryClientName = normalizeInlineText(sourceItem?.client_name);
  const loadingClientName = normalizeInlineText(sourceItem?.loading_client_name);
  const sourceTruckId = normalizeInlineText(sourceItem?.current_truck_id);
  const sourceTruckLabel = resolveTruckDisplayName(state, sourceTruckId);
  const targetTruckLabel = resolveTruckDisplayName(state, targetTruckId, targetTruckName);
  const commandLabel = orderId || safeDeliveryId;

  return [
    {
      key: "delivery-command",
      label: "Commande",
      value: commandLabel,
    },
    {
      key: "delivery-trucks",
      label: "Camions",
      value: `${sourceTruckLabel || "À confirmer"} → ${targetTruckLabel || "À confirmer"}`,
    },
    {
      key: "delivery-loading",
      label: "Chargement",
      value: loadingClientName || "À confirmer",
    },
    {
      key: "delivery-delivery",
      label: "Déchargement",
      value: deliveryClientName || orderClientName || "À confirmer",
    },
  ];
}

export function shouldAllowReturnToOptimizedDelivery(state, { deliveryId = null, targetTruckId = null } = {}) {
  const safeDeliveryId = normalizeInlineText(deliveryId);
  const safeTargetTruckId = normalizeInlineText(targetTruckId);
  if (!safeDeliveryId || !safeTargetTruckId) {
    return false;
  }

  const planningItem = state?.deliveryItemsByOrderId?.get?.(safeDeliveryId) || null;
  if (!planningItem) {
    return false;
  }

  return normalizeInlineText(planningItem.assignment_origin).toLowerCase() === "manual"
    && normalizeInlineText(planningItem.optimized_truck_id) === safeTargetTruckId;
}

function buildUniqueNoticeExamples(values) {
  const seenValues = new Set();

  return (Array.isArray(values) ? values : []).reduce((items, value) => {
    const normalizedValue = normalizeInlineText(value);
    if (!normalizedValue) {
      return items;
    }

    const key = normalizedValue.toLowerCase();
    if (seenValues.has(key)) {
      return items;
    }

    seenValues.add(key);
    items.push(normalizedValue);
    return items;
  }, []).slice(0, 10);
}

function buildUniqueNoticeExampleRecords(values) {
  const seenValues = new Set();

  return (Array.isArray(values) ? values : []).reduce((items, value) => {
    if (!value || typeof value !== "object") {
      return items;
    }

    const label = normalizeInlineText(value.label);
    const detail = normalizeInlineText(value.detail);
    if (!label) {
      return items;
    }

    const key = `${label.toLowerCase()}::${detail.toLowerCase()}`;
    if (seenValues.has(key)) {
      return items;
    }

    seenValues.add(key);
    items.push({
      ...(detail ? { detail } : {}),
      label,
    });
    return items;
  }, []).slice(0, 10);
}

function getTripStopAddress(trip, stopType) {
  if (!Array.isArray(trip?.stops)) {
    return "";
  }

  const matchingStop = trip.stops.find((stop) => (
    stop
    && normalizeInlineText(stop.stop_type) === stopType
    && normalizeInlineText(stop.address)
  )) || null;

  return normalizeInlineText(matchingStop?.address);
}

function resolveAssignmentDepartureAddress(assignment) {
  if (!assignment || typeof assignment !== "object") {
    return "";
  }

  const directAddress = normalizeInlineText(assignment.departure_address);
  if (directAddress) {
    return directAddress;
  }

  const firstTrip = Array.isArray(assignment.trips) ? assignment.trips.find(Boolean) || null : null;
  return getTripStopAddress(firstTrip, "depot_start");
}

function findAssignmentByTruckId(resultsData, truckId) {
  const safeTruckId = normalizeInlineText(truckId);
  if (!safeTruckId || !Array.isArray(resultsData?.assignments)) {
    return null;
  }

  return resultsData.assignments.find((assignment) => normalizeInlineText(assignment?.truck_id) === safeTruckId) || null;
}

function countProtectedAssignmentOrders(state, truckId) {
  const safeTruckId = normalizeInlineText(truckId);
  const assignment = state?.assignmentsById?.get?.(safeTruckId)
    || findAssignmentByTruckId(state?.resultsData, safeTruckId);
  if (!assignment || !Array.isArray(assignment.delivery_items)) {
    return 0;
  }

  const protectedOrderIds = new Set();
  let protectedOrdersWithoutId = 0;
  assignment.delivery_items.forEach((item) => {
    if (item?.is_confirmed !== true) {
      return;
    }
    const orderId = normalizeInlineText(item?.order_id);
    if (orderId) {
      protectedOrderIds.add(orderId);
    } else {
      protectedOrdersWithoutId += 1;
    }
  });
  return protectedOrderIds.size + protectedOrdersWithoutId;
}

function resolveTargetedAssignmentOvertimeWarningContext(state, targetTruckId, targetCompatibility = null) {
  const safeTargetTruckId = normalizeInlineText(targetTruckId);
  if (!safeTargetTruckId) {
    return {
      assignment: null,
      checkboxDescription: "",
      feedbackMessage: "",
      isOvertimeAuthorized: false,
      maxWorkMinutes: 0,
      shouldShow: false,
      utilizationRatio: 0,
      workloadMinutes: 0,
    };
  }

  const assignment = state?.assignmentsById?.get?.(safeTargetTruckId)
    || findAssignmentByTruckId(state?.resultsData, safeTargetTruckId)
    || null;

  const overtimeState = getTargetedReactivateOvertimeState(assignment, targetCompatibility, {
    warningThreshold: REACTIVATE_OVERTIME_WARNING_THRESHOLD,
  });
  const compatibilityDetail = normalizeInlineText(overtimeState.compatibilityDetail);

  if (!assignment) {
    return {
      assignment: null,
      checkboxDescription: compatibilityDetail,
      feedbackMessage: compatibilityDetail || "Camion introuvable dans le dispatch : heures max non vérifiées.",
      isOvertimeAuthorized: false,
      ...overtimeState,
    };
  }

  const utilizationMessage = overtimeState.maxWorkMinutes > 0
    ? formatOvertimeUtilizationMessage(
      overtimeState.workloadMinutes,
      overtimeState.maxWorkMinutes,
      overtimeState.utilizationRatio,
    )
    : "";
  const isOvertimeAuthorized = isAssignmentOvertimeAuthorized(assignment, {
    authorizedTruckIds: state?.overtimeWarningTruckIds,
    truckId: safeTargetTruckId,
  });
  const shouldShow = overtimeState.shouldShow && !isOvertimeAuthorized;

  return {
    assignment,
    checkboxDescription: shouldShow
      ? [compatibilityDetail, utilizationMessage].filter(Boolean).join(" ")
      : "",
    // Aucun avis sans risque, comme pour un déplacement.
    feedbackMessage: isOvertimeAuthorized || overtimeState.maxWorkMinutes > 0
      ? ""
      : "Heures max non renseignées pour ce camion : dépassement non vérifié.",
    isOvertimeAuthorized,
    ...overtimeState,
    shouldShow,
  };
}

function findDeliveryExecutionContext(resultsData, deliveryId) {
  const safeDeliveryId = normalizeInlineText(deliveryId);
  if (!safeDeliveryId) {
    return null;
  }

  for (const assignment of resultsData?.assignments || []) {
    for (const trip of assignment?.trips || []) {
      for (const order of trip?.orders || []) {
        const deliveries = Array.isArray(order?.deliveries) ? order.deliveries.filter(Boolean) : [];
        const delivery = deliveries.find((candidate) => normalizeInlineText(candidate?.delivery_id) === safeDeliveryId) || null;
        if (!delivery) {
          continue;
        }

        return {
          assignment,
          delivery,
          order,
          trip,
        };
      }
    }
  }

  const suspendedDelivery = Array.isArray(resultsData?.suspended_deliveries)
    ? resultsData.suspended_deliveries.find((item) => normalizeInlineText(item?.delivery_id) === safeDeliveryId) || null
    : null;

  if (!suspendedDelivery) {
    return null;
  }

  return {
    assignment: findAssignmentByTruckId(resultsData, suspendedDelivery.current_truck_id),
    delivery: suspendedDelivery,
    order: {
      address: suspendedDelivery.address,
      loading_address: suspendedDelivery.loading_address,
      loading_client_name: suspendedDelivery.loading_client_name,
      order_id: suspendedDelivery.order_id,
    },
    trip: null,
  };
}

function buildPlanningMutationExecutionSummaryContext(
  previousResultsData,
  nextResultsData,
  { deliveryId = null, targetTruckId = null } = {},
) {
  const deliveryContext = findDeliveryExecutionContext(nextResultsData, deliveryId)
    || findDeliveryExecutionContext(previousResultsData, deliveryId);

  if (!deliveryContext) {
    return null;
  }

  const sourceAssignment = deliveryContext.assignment || findAssignmentByTruckId(previousResultsData, deliveryContext?.delivery?.current_truck_id);
  const targetAssignment = findAssignmentByTruckId(nextResultsData, targetTruckId) || findAssignmentByTruckId(previousResultsData, targetTruckId);
  const sourceTruckLabel = normalizeInlineText(sourceAssignment?.truck_name || sourceAssignment?.truck_id);
  const targetTruckLabel = normalizeInlineText(targetAssignment?.truck_name || targetAssignment?.truck_id);
  const loadingClientLabel = normalizeInlineText(deliveryContext?.order?.loading_client_name || deliveryContext?.delivery?.loading_client_name) || "Chargement";
  const deliveryClientLabel = normalizeInlineText(deliveryContext?.delivery?.client_name || deliveryContext?.order?.client_name) || "Déchargement";
  const sourceDepartureAddress = resolveAssignmentDepartureAddress(sourceAssignment);
  const targetDepartureAddress = resolveAssignmentDepartureAddress(targetAssignment);
  const loadingAddress = normalizeInlineText(deliveryContext?.order?.loading_address || deliveryContext?.delivery?.loading_address);
  const deliveryAddress = normalizeInlineText(deliveryContext?.delivery?.address || deliveryContext?.order?.address);
  const returnAddress = getTripStopAddress(deliveryContext.trip, "depot_return") || sourceDepartureAddress;
  const geocodeExamples = buildUniqueNoticeExamples([
    sourceDepartureAddress,
    targetDepartureAddress,
    loadingAddress,
    deliveryAddress,
    returnAddress,
  ]);
  const routeExamples = buildUniqueNoticeExamples([
    sourceDepartureAddress && loadingAddress ? `${sourceDepartureAddress} → ${loadingAddress}` : "",
    targetDepartureAddress && loadingAddress && targetDepartureAddress !== sourceDepartureAddress ? `${targetDepartureAddress} → ${loadingAddress}` : "",
    loadingAddress && deliveryAddress ? `${loadingAddress} → ${deliveryAddress}` : "",
    deliveryAddress && returnAddress ? `${deliveryAddress} → ${returnAddress}` : "",
  ]);
  const geocodeExampleRecords = buildUniqueNoticeExampleRecords([
    sourceDepartureAddress ? { label: sourceTruckLabel ? `Départ ${sourceTruckLabel}` : "Départ camion", detail: sourceDepartureAddress } : null,
    targetDepartureAddress && targetDepartureAddress !== sourceDepartureAddress
      ? { label: targetTruckLabel ? `Départ ${targetTruckLabel}` : "Départ camion cible", detail: targetDepartureAddress }
      : null,
    loadingAddress ? { label: loadingClientLabel, detail: loadingAddress } : null,
    deliveryAddress ? { label: deliveryClientLabel, detail: deliveryAddress } : null,
    returnAddress && returnAddress !== sourceDepartureAddress
      ? { label: sourceTruckLabel ? `Retour ${sourceTruckLabel}` : "Retour camion", detail: returnAddress }
      : null,
  ]);
  const routeExampleRecords = buildUniqueNoticeExampleRecords([
    sourceDepartureAddress && loadingAddress
      ? {
          label: `${sourceTruckLabel || "Départ camion"} → ${loadingClientLabel}`,
          detail: `${sourceDepartureAddress} → ${loadingAddress}`,
        }
      : null,
    targetDepartureAddress && loadingAddress && targetDepartureAddress !== sourceDepartureAddress
      ? {
          label: `${targetTruckLabel || "Camion cible"} → ${loadingClientLabel}`,
          detail: `${targetDepartureAddress} → ${loadingAddress}`,
        }
      : null,
    loadingAddress && deliveryAddress
      ? {
          label: `${loadingClientLabel} → ${deliveryClientLabel}`,
          detail: `${loadingAddress} → ${deliveryAddress}`,
        }
      : null,
    deliveryAddress && returnAddress
      ? {
          label: `${deliveryClientLabel} → ${sourceTruckLabel || "Retour camion"}`,
          detail: `${deliveryAddress} → ${returnAddress}`,
        }
      : null,
  ]);

  if (!geocodeExamples.length && !routeExamples.length) {
    return null;
  }

  return {
    ...(routeExamples.length
      ? {
          routeCache: {
            description: "Trajets déjà calculés entre deux points pour cette action. Chaque ligne correspond à une liaison origine → destination réutilisée depuis le cache de routage.",
            ...(routeExampleRecords.length ? { exampleRecords: routeExampleRecords } : {}),
            examples: routeExamples,
            examplesTitle: "Trajets du clic",
          },
        }
      : {}),
    ...(geocodeExamples.length
      ? {
          geocodeCache: {
            description: "Adresses individuelles déjà converties en coordonnées pour cette action. Ici, il s'agit de points isolés réutilisés depuis le cache de géocodage.",
            ...(geocodeExampleRecords.length ? { exampleRecords: geocodeExampleRecords } : {}),
            examples: geocodeExamples,
            examplesTitle: "Adresses du clic",
          },
        }
      : {}),
  };
}

export default function usePlanningMutation({
  onPortalPublicationsChange = null,
  onPortalPublicationsRefresh = null,
  onWorkspaceRender,
  protectAfterReassignment = true,
  reassignmentRebuildMode = DISPATCH_REASSIGNMENT_MODE_COMPLETE,
  workspaceStore,
}) {
  const [confirmationDialog, setConfirmationDialog] = useState(null);
  const abortControllersByDeliveryIdRef = useRef(new Map());
  const activePlanningRecoveriesRef = useRef(new Set());
  const isMountedRef = useRef(true);
  const highlightRestartFrameRef = useRef(null);
  const highlightResetTimeoutRef = useRef(null);
  const pendingTruckMutationIdsRef = useRef(new Set());
  const { dismissToast, showToast, updateToast } = useToast();
  const { setPlanningResetLaunchScope, trackOptimizationLaunch } = useOptimizationJobTracker();
  const resolvedProtectAfterReassignment = normalizeDispatchProtectAfterReassignment(
    protectAfterReassignment,
  );
  const resolvedReassignmentRebuildMode = normalizeDispatchReassignmentMode(reassignmentRebuildMode);

  const resolveWorkspaceState = (fallbackState = null) => workspaceStore.getState() || fallbackState;

  const rememberTruckDataManagerTimeSettings = (truckId, {
    capacityTons = null,
    eveningRadiusKm = null,
    maxWorkHours = null,
    startTime = "",
  } = {}) => {
    const safeTruckId = normalizeInlineText(truckId);
    const normalizedStartTime = normalizeTruckStartTimeValue(startTime);
    const normalizedMaxWorkHours = normalizeTruckMaxWorkHoursValue(maxWorkHours);
    const normalizedCapacityTons = normalizeTruckCapacityTonsValue(capacityTons);
    const currentState = resolveWorkspaceState();
    if (
      !currentState
      || !safeTruckId
      || (!normalizedStartTime && normalizedMaxWorkHours == null && normalizedCapacityTons == null && eveningRadiusKm == null)
    ) {
      return;
    }
    const assignment = currentState.assignmentsById?.get?.(safeTruckId)
      || findAssignmentByTruckId(currentState.resultsData, safeTruckId);
    if (assignment && typeof assignment === "object") {
      if (eveningRadiusKm != null) assignment.evening_radius_km = eveningRadiusKm;
      if (normalizedStartTime) {
        assignment.data_manager_start_time = normalizedStartTime;
      }
      if (normalizedMaxWorkHours != null) {
        assignment.data_manager_max_work_hours = normalizedMaxWorkHours;
      }
      if (normalizedCapacityTons != null) {
        assignment.data_manager_capacity_tons = normalizedCapacityTons;
      }
    }
  };

  const rememberTruckDataManagerStartTime = (truckId, startTime) => {
    rememberTruckDataManagerTimeSettings(truckId, { startTime });
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;

      // Une réaffectation confirmée doit continuer même si l'utilisateur change de vue.
      abortControllersByDeliveryIdRef.current.clear();
      activePlanningRecoveriesRef.current.forEach(({ controller }) => controller.abort());
      activePlanningRecoveriesRef.current.clear();

      if (highlightResetTimeoutRef.current) {
        window.clearTimeout(highlightResetTimeoutRef.current);
        highlightResetTimeoutRef.current = null;
      }
      if (highlightRestartFrameRef.current) {
        window.cancelAnimationFrame(highlightRestartFrameRef.current);
        highlightRestartFrameRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    activePlanningRecoveriesRef.current.forEach(({ controller, canContinue }) => {
      if (!canContinue()) controller.abort();
    });
  });

  const dismissConfirmation = () => {
    setConfirmationDialog(null);
  };

  const scheduleTransientActivatedSteps = (
    stepKeys = [],
    {
      dispatchPeriod = "",
      durationMs = TRANSIENT_ACTIVATION_HIGHLIGHT_DURATION_MS,
      highlightSurface = "timeline",
    } = {},
  ) => {
    if (highlightResetTimeoutRef.current) {
      window.clearTimeout(highlightResetTimeoutRef.current);
      highlightResetTimeoutRef.current = null;
    }
    if (highlightRestartFrameRef.current) {
      window.cancelAnimationFrame(highlightRestartFrameRef.current);
      highlightRestartFrameRef.current = null;
    }

    const state = workspaceStore.getState();
    if (!state) {
      return;
    }

    const nextStepKeys = (Array.isArray(stepKeys) ? stepKeys : [])
      .filter((stepKey) => typeof stepKey === "string" && stepKey);
    const nextDispatchPeriod = normalizeInlineText(dispatchPeriod)
      ? normalizePlanningDispatchPeriod(dispatchPeriod)
      : getCurrentDispatchPeriodFromLocation();
    const nextHighlightSurface = highlightSurface === "suspended" ? "suspended" : "timeline";

    state.transientActivatedDispatchPeriod = "";
    state.transientActivatedHighlightSurface = "";
    state.transientActivatedStepKeys = new Set();
    if (isMountedRef.current) {
      rerenderWorkspace(workspaceStore, onWorkspaceRender);
    }

    if (!nextStepKeys.length) {
      return;
    }

    highlightRestartFrameRef.current = window.requestAnimationFrame(() => {
      highlightRestartFrameRef.current = null;
      const currentState = workspaceStore.getState();
      if (!currentState) {
        return;
      }

      currentState.transientActivatedDispatchPeriod = nextDispatchPeriod;
      currentState.transientActivatedHighlightSurface = nextHighlightSurface;
      currentState.transientActivatedStepKeys = new Set(nextStepKeys);
      if (isMountedRef.current) {
        rerenderWorkspace(workspaceStore, onWorkspaceRender);
      }

      highlightResetTimeoutRef.current = window.setTimeout(() => {
        highlightResetTimeoutRef.current = null;
        const nextState = workspaceStore.getState();
        if (!nextState) {
          return;
        }

        nextState.transientActivatedDispatchPeriod = "";
        nextState.transientActivatedHighlightSurface = "";
        nextState.transientActivatedStepKeys = new Set();
        if (isMountedRef.current) {
          rerenderWorkspace(workspaceStore, onWorkspaceRender);
        }
      }, durationMs);
    });
  };

  const isDeliveryMutationPending = (deliveryId) => {
    return hasPendingPlanningMutation(workspaceStore.getState(), { deliveryId });
  };

  const showPendingDeliveryMutationToast = () => {
    showToast({
      message: "Cette commande est déjà en train d'être mise à jour. Attendez la fin de l'opération avant de lancer une nouvelle action.",
      title: "Mise à jour déjà en cours",
      type: "info",
      duration: 5000,
    });
  };

  const showPendingOrderMutationToast = () => {
    showToast({
      message: "Cette commande est déjà en train d'être enregistrée. Attendez la fin de l'opération avant de réessayer.",
      title: "Enregistrement déjà en cours",
      type: "info",
      duration: 5000,
    });
  };

  const requestProtectionConfirmation = ({
    actionName = "confirm",
    content = null,
    deliveryId = "",
    loadingStepId = "",
    url = "",
  } = {}) => {
    if (!content || !url) {
      return;
    }

    setConfirmationDialog({
      ...content,
      isOpen: true,
      onCancel: dismissConfirmation,
      onClose: dismissConfirmation,
      onConfirm: () => {
        const currentState = workspaceStore.getState();
        if (!currentState || (!deliveryId && !loadingStepId)) {
          dismissConfirmation();
          return;
        }

        if (hasPendingPlanningMutation(currentState, { deliveryId, loadingStepId })) {
          dismissConfirmation();
          showPendingDeliveryMutationToast();
          return;
        }

        dismissConfirmation();
        applyPlanningMutation({
          url,
          actionName,
          ...(deliveryId ? { deliveryId } : {}),
          ...(loadingStepId ? { loadingStepId } : {}),
        });
      },
    });
  };

  const isTruckMutationPending = (truckId) => {
    const safeTruckId = normalizeInlineText(truckId);
    return Boolean(safeTruckId) && pendingTruckMutationIdsRef.current.has(safeTruckId);
  };

  const applyTruckScopedMutation = async (truckId, runMutation) => {
    const safeTruckIds = [
      ...new Set(
        (Array.isArray(truckId) ? truckId : [truckId])
          .map((item) => normalizeInlineText(item))
          .filter(Boolean),
      ),
    ];
    if (
      safeTruckIds.length === 0
      || typeof runMutation !== "function"
      || safeTruckIds.some((safeTruckId) => isTruckMutationPending(safeTruckId))
    ) {
      return;
    }

    safeTruckIds.forEach((safeTruckId) => {
      pendingTruckMutationIdsRef.current.add(safeTruckId);
    });
    try {
      return await runMutation();
    } finally {
      safeTruckIds.forEach((safeTruckId) => {
        pendingTruckMutationIdsRef.current.delete(safeTruckId);
      });
    }
  };

  const publishStatusNotice = (notice) => {
    if (!notice || typeof notice !== "object") {
      return;
    }

    dispatchAppStatusNotice({
      source: "planning",
      ...notice,
    });
  };

  const emitMutationToast = showToast;
  const replaceMutationToast = updateToast;
  const emitMutationStatusNotice = publishStatusNotice;
  const applyPlanningMutation = async ({
    url,
    body = null,
    orderId = null,
    deliveryId = null,
    loadingStepId = null,
    preferredAssignmentId: explicitPreferredAssignmentId = null,
    highlightedStepKeys: explicitHighlightedStepKeys = null,
    orderEditSubmission: submittedOrderEdit = null,
    highlightSurface = "timeline",
    actionName = "mutation",
    loadingToastContent = null,
    statusNoticeContent = null,
    loadingOverlayContent = null,
    existingLoadingToastId = null,
    preserveLoadingToastOnSuccess = false,
    handOffLoadingToast = false,
    requestHeaders = null,
    onMutationChallenge = null,
    onMutationError = null,
    onMutationStart = null,
    onMutationSuccess = null,
    beforeSuccessFeedback = null,
    resolveServerErrorMessage = null,
    allowMissingResultPayload = false,
    dispatchPeriod = "",
    resultDispatchPeriod = "",
    expectedRuntimeRevision = "",
    isUndo = false,
    rethrowOnError = false,
    refreshResultsAfterMissingPayload = false,
    method = "POST",
  }) => {
    const state = resolveWorkspaceState();
    if (!state) {
      return null;
    }
    const normalizedMutationDispatchPeriod = normalizeInlineText(dispatchPeriod)
      ? normalizePlanningDispatchPeriod(dispatchPeriod)
      : "";
    const normalizedResultDispatchPeriod = normalizeInlineText(resultDispatchPeriod)
      ? normalizePlanningDispatchPeriod(resultDispatchPeriod)
      : normalizedMutationDispatchPeriod;
    const shouldPreserveCurrentWorkspace = Boolean(
      normalizedResultDispatchPeriod
      && normalizedResultDispatchPeriod !== getCurrentDispatchPeriodFromLocation()
    );
    const requiresSavedPlanningView = requiresSavedPlanningViewForMutation(actionName, requestHeaders);
    refreshResultsAfterMissingPayload ||= requiresSavedPlanningView;
    const canCompleteWithoutWorkspaceSync = allowMissingResultPayload || shouldPreserveCurrentWorkspace || requiresSavedPlanningView;
    let requestBody = buildRuntimeRevisionGuardedPlanningMutationBody({
      actionName,
      body,
      expectedRuntimeRevision,
      resultsData: shouldPreserveCurrentWorkspace ? null : state.resultsData,
    });
    const mutationStartedAt = performance.now();
    const mutationTransport = ["suspend", "suspend_delivery_group"].includes(actionName) ? createMutationTransportDiagnostic() : null;
    const orderEditSubmission = actionName === GANTT_ORDER_EDIT_ACTION
      ? normalizeOrderEditEvidence({ ...submittedOrderEdit, expected_revision: requestBody?.expected_runtime_revision,
        expected_order_updated_at: requestBody?.expected_updated_at,
        truck_name: submittedOrderEdit?.truck_name || state.assignmentsById?.get?.(submittedOrderEdit?.truck_id)?.truck_name,
        dispatch_period: getCurrentDispatchPeriodFromLocation(), dispatch_date: getCurrentDispatchDateFromLocation() }, "browser_submission")
      : null;
    let ganttDiagnostics = null;
    let ganttHttpStatus = null;
    const performanceChainId = createPerformanceCorrelationId();
    const performanceTabId = getPerformanceTabId();
    const performanceScope = resolvePlanningPerformanceEventScope(null, state);
    const mutationOriginDate = getCurrentDispatchDateFromLocation();
    const mutationOriginPeriod = getCurrentDispatchPeriodFromLocation();
    const mutationOriginPath = window.location.pathname;
    const mutationOriginTruck = state.currentAssignment?.truck_id || "";
    const originResults = state.resultsData;
    const isCurrentMutationContext = () => isMountedRef.current
      && getCurrentDispatchDateFromLocation() === mutationOriginDate
      && getCurrentDispatchPeriodFromLocation() === mutationOriginPeriod
      && window.location.pathname === mutationOriginPath;
    // Un changement de chauffeur va au bout même si un autre camion est sélectionné pendant l'enregistrement :
    // la sélection courante est gardée et sa carte Mise à jour s'actualise seule.
    const canApplySavedPlanningView = () => isCurrentMutationContext()
      && (!requiresSavedPlanningView || actionName === "update_truck_driver_name"
        || (workspaceStore.getState()?.currentAssignment?.truck_id || "") === mutationOriginTruck);
    const planningRevision = {
      dispatch_date: mutationOriginDate,
      dispatch_period: normalizedMutationDispatchPeriod || mutationOriginPeriod,
      initial_runtime_revision: planningRevisionForScope(originResults, mutationOriginDate, normalizedMutationDispatchPeriod || mutationOriginPeriod),
      expected_assignment_version: requestBody?.expected_assignment_version,
      initial_planning_operation: state.lastPlanningOperation,
      initial_runtime_revision_present: Boolean(normalizeInlineText(requestBody?.expected_runtime_revision)),
      expected_runtime_revision_present: Boolean(normalizeInlineText(requestBody?.expected_runtime_revision)),
      expected_assignment_version_present: Number.isInteger(requestBody?.expected_assignment_version)
        && requestBody.expected_assignment_version >= 1,
      refresh_outcome: "not_needed",
    };
    let failedRevisionEvidence = null;
    const revisionEvidence = () => ({
      ...planningRevision,
      installed_runtime_revision: planningRevisionForScope(workspaceStore.getState()?.resultsData, mutationOriginDate, normalizedMutationDispatchPeriod || mutationOriginPeriod),
      last_planning_operation: workspaceStore.getState()?.lastPlanningOperation || state.lastPlanningOperation,
      events: planningRevisionTimeline(mutationOriginDate, normalizedMutationDispatchPeriod || mutationOriginPeriod),
    });
    let validationErrors = [];
    const requestUrl = appendCurrentDispatchDate(
      appendPlanningDispatchPeriod(url, normalizedMutationDispatchPeriod),
    );
    const createMutationCaptureContext = ({
      detailItems = [],
      failureKind = "",
      httpStatus = null,
      planningDiagnostics = null,
      reasonCode = "",
      correlationId = performanceChainId,
    } = {}) => buildPlanningAlertCaptureContext({
      actionLabel: formatPlanningDiagnosticActionLabel(actionName),
      actionName,
      body: {
        ...(requestBody || {}),
        ...(explicitPreferredAssignmentId ? { truck_id: explicitPreferredAssignmentId } : {}),
        source_dispatch_date: requestBody?.source_dispatch_date || mutationOriginDate,
        target_dispatch_date: requestBody?.target_dispatch_date || mutationOriginDate,
        target_period: requestBody?.target_period || normalizedMutationDispatchPeriod || mutationOriginPeriod,
      },
      correlationId,
      deliveryId,
      detailItems,
      failureKind,
      httpStatus,
      interactionOrigin: isUndo ? "undo" : "direct",
      loadingStepId,
      method,
      operationId: performanceChainId,
      mutationTransport: mutationTransport?.snapshot(),
      orderEditSubmission,
      validationErrors,
      planningRevision: { ...(failedRevisionEvidence || revisionEvidence()), refresh_outcome: planningRevision.refresh_outcome },
      orderId,
      path: requestUrl,
      planningDiagnostics,
      reasonCode,
      state,
      subjectLabel: resolveDeliveryNoticeSubject(state, deliveryId) || orderId || loadingStepId || "",
    });
    const baseCaptureContext = createMutationCaptureContext();
    const ganttCapture = actionName === GANTT_ORDER_EDIT_ACTION ? createOperationFeedbackCapture({
      action: actionName, operationEventId: performanceChainId,
      dispatchDate: mutationOriginDate, dispatchPeriods: [mutationOriginPeriod],
      resourceKind: "order", resourceIds: [orderId], resourceLabel: orderEditSubmission?.client_name || orderId,
      orderEditSubmission,
      progressSteps: [{key: "process", label: "Enregistrement et recalcul"}, {key: "refresh", label: "Rafraîchissement du planning"}],
    }) : null;
    const enrichOrderEditToast = (toast) => {
      if (!ganttCapture || !["success", "error", "warning"].includes(toast.type)) return toast;
      const feedback = buildOrderEditFeedback({ submission: orderEditSubmission, diagnostics: ganttDiagnostics,
        httpStatus: toast.captureContext?.technical?.failure_kind === "validation" ? 409 : ganttHttpStatus,
        message: toast.message, success: toast.type === "success" });
      ganttCapture.setStatus(feedback.status);
      const captureContext = { ...(toast.captureContext || baseCaptureContext), order_edit: {
        ...feedback.edit,
        ...(toast.type !== "success" ? { failure: { ...feedback.edit?.failure, message: feedback.edit?.failure?.message || toast.message } } : {}),
      } };
      return ganttCapture.recordToast({ ...toast, title: feedback.title, message: feedback.message, captureContext });
    };
    const showToast = (toast) => emitMutationToast(enrichOrderEditToast(toast));
    const updateToast = (id, toast) => replaceMutationToast(id, enrichOrderEditToast(toast));
    const publishStatusNotice = (notice) => {
      if (!ganttCapture) return emitMutationStatusNotice(notice);
      const type = notice.status === "completed" ? "success" : notice.status === "failed" ? "error" : "loading";
      const enriched = enrichOrderEditToast({ ...notice, type });
      return emitMutationStatusNotice({ ...notice, title: enriched.title, message: enriched.message, captureContext: enriched.captureContext });
    };
    const finishGanttCapture = () => ganttCapture?.finish({ totalVisibleMs: performance.now() - mutationStartedAt });
    const refuseBusyGanttAttempt = () => {
      if (!ganttCapture) {
        showToast({ type: "warning", title: PLANNING_UPDATE_BUSY_MESSAGE,
          message: "Cette action n’a pas été envoyée. Attendez la fin, puis validez à nouveau." });
        return;
      }
      showToast({ type: "error", message: "Une modification est déjà en cours. Cette nouvelle tentative n’a pas été envoyée.",
        captureContext: createMutationCaptureContext({ failureKind: "validation", reasonCode: "planning_mutation_in_progress" }) });
      window.requestAnimationFrame(() => window.requestAnimationFrame(finishGanttCapture));
      if (document.visibilityState === "hidden") finishGanttCapture();
    };

    const statusNoticeErrorDetailItems = Array.isArray(statusNoticeContent?.errorDetailItems)
      ? statusNoticeContent.errorDetailItems.filter((item) => item && typeof item === "object")
      : [];

    if (isDispatchDayClosedBlockedAction(actionName) && resolveDispatchDayMutationContext(state).isClosed) {
      const blockedMutationFeedback = buildClosedDispatchDayBlockedMutationFeedback();
      const resolvedErrorDetailItems = [
        ...statusNoticeErrorDetailItems,
        ...blockedMutationFeedback.detailItems,
      ];
      const blockedCaptureContext = createMutationCaptureContext({
        detailItems: resolvedErrorDetailItems,
        failureKind: "validation",
        reasonCode: "dispatch_day_closed",
      });

      showToast({
        captureContext: blockedCaptureContext,
        duration: blockedMutationFeedback.duration,
        emphasis: blockedMutationFeedback.emphasis,
        message: blockedMutationFeedback.message,
        title: blockedMutationFeedback.title,
        type: "error",
      });

      if (statusNoticeContent) {
        clearAppStatusNotice();
        publishStatusNotice({
          captureContext: blockedCaptureContext,
          detailItems: resolvedErrorDetailItems,
          eyebrow: statusNoticeContent.eyebrow || "Journée dispatch",
          finishedAt: new Date().toISOString(),
          message: blockedMutationFeedback.message,
          status: "failed",
          statusLabel: blockedMutationFeedback.statusLabel,
          title: blockedMutationFeedback.title,
        });
      }

      onMutationError?.({
        detailItems: resolvedErrorDetailItems,
        deliveryId,
        message: blockedMutationFeedback.message,
        title: blockedMutationFeedback.title,
      });
      finishGanttCapture();
      if (rethrowOnError) {
        const planningMutationError = new Error(blockedMutationFeedback.message);
        planningMutationError.hasPublishedPlanningMutationFeedback = true;
        throw planningMutationError;
      }
      return null;
    }

    if (hasPendingPlanningMutation(state, { orderId, loadingStepId, deliveryId })) {
      refuseBusyGanttAttempt();
      return null;
    }

    const finishPlanningMutationActivity = tryBeginPlanningMutationActivity();
    if (!finishPlanningMutationActivity) {
      refuseBusyGanttAttempt();
      return null;
    }

    const diagnosticRecord = state.planningDiagnostics && typeof state.planningDiagnostics.start === "function"
      ? state.planningDiagnostics.start(actionName, loadingStepId || deliveryId || orderId, url)
      : null;
    let diagnosticPayloadForClientTiming = null;

    if (statusNoticeContent) {
      clearAppStatusNotice();
    }
    const progress = createToastProgress(loadingToastContent?.progressSteps);
    let loadingToastId = existingLoadingToastId || (loadingToastContent
      ? showToast({
        captureContext: baseCaptureContext,
        duration: null,
        message: loadingToastContent.message,
        title: loadingToastContent.title,
        type: "loading",
        progress,
      })
      : null);
    if (existingLoadingToastId && loadingToastContent) {
      updateToast(existingLoadingToastId, {
        captureContext: baseCaptureContext,
        duration: null,
        message: loadingToastContent.message,
        title: loadingToastContent.title,
        type: "loading",
        progress,
      });
    }
    const reportToastProgress = createToastProgressReporter(progress, (nextProgress) => {
      updateToast(loadingToastId, { progress: nextProgress });
    });
    const reportProgress = (...args) => {
      ganttCapture?.reportProgress(...args);
      return reportToastProgress(...args);
    };
    let hasResolvedLoadingToast = false;
    let canApplyRecoveredMutation = null;

    if (diagnosticRecord) {
      diagnosticRecord.mark("mutation_start");
    }

    addPendingPlanningMutation(state, { orderId, loadingStepId, deliveryId });

    rerenderWorkspace(workspaceStore, onWorkspaceRender);
    onMutationStart?.({
      deliveryId,
      loadingToastContent,
      statusNoticeContent,
    });
    if (diagnosticRecord) {
      diagnosticRecord.mark("pending_render");
    }

    try {
      if (diagnosticRecord) {
        diagnosticRecord.mark("network_start");
      }

      const controller = deliveryId ? new AbortController() : null;
      if (deliveryId && controller) {
        abortControllersByDeliveryIdRef.current.set(deliveryId, controller);
      }

      if (actionName === "update_truck_driver_name" && !planningRevision.expected_runtime_revision_present) {
        const truckId = explicitPreferredAssignmentId;
        const expectedVersion = requestBody?.expected_assignment_version;
        const originalLocalVersion = findAssignmentByTruckId(originResults, truckId)?.driver_assignment_version;
        const originalLocalRevision = originResults?.runtime_revision;
        const readController = new AbortController();
        const canContinue = () => isCurrentMutationContext()
          && (!normalizedMutationDispatchPeriod || normalizedMutationDispatchPeriod === mutationOriginPeriod)
          && (workspaceStore.getState()?.currentAssignment?.truck_id || "") === mutationOriginTruck;
        const activeRead = { controller: readController, canContinue };
        activePlanningRecoveriesRef.current.add(activeRead);
        const timeout = setTimeout(() => readController.abort(), 20000);
        planningRevision.refresh_outcome = "pending";
        updateToast(loadingToastId, { title: "Actualisation avant l’affectation",
          message: "Le chauffeur sélectionné est conservé pendant la lecture du Dispatch.",
          captureContext: createMutationCaptureContext() });
        try {
          if (!canContinue()) {
            planningRevision.refresh_outcome = "context_changed";
            throw new Error("Le contexte a changé. Validez à nouveau l’affectation dans le Dispatch souhaité.");
          }
          // Exactly one forced read; never retry a PATCH, nor borrow an in-flight read.
          const refreshUrl = new URL(getForcedResultsLatestApiUrl(mutationOriginPeriod), window.location.origin);
          refreshUrl.searchParams.set("force_fresh", "true");
          const freshResponse = await fetchWithSupabaseAuth(`${refreshUrl.pathname}${refreshUrl.search}`, {
            cache: "no-store", signal: readController.signal,
            headers: buildPlanningPerformanceActionHeaders(actionName, { chainId: performanceChainId, tabId: performanceTabId }),
          });
          if (!freshResponse.ok) throw new Error("Planning indisponible");
          const fresh = await freshResponse.json();
          recordPlanningRevisionResponse(fresh, freshResponse, { url: refreshUrl.href, actionName: "driver_assignment_refresh" });
          planningRevision.received_runtime_revision = fresh?.runtime_revision;
          planningRevision.result_runtime_revision_present = Boolean(normalizeInlineText(fresh?.runtime_revision));
          if (!canContinue() || readController.signal.aborted) {
            planningRevision.refresh_outcome = "context_changed";
          } else if (workspaceStore.getState()?.resultsData !== originResults
            || workspaceStore.getState()?.resultsData?.runtime_revision !== originalLocalRevision) {
            planningRevision.refresh_outcome = "planning_replaced";
          } else if (findAssignmentByTruckId(workspaceStore.getState()?.resultsData, truckId)?.driver_assignment_version !== originalLocalVersion) {
            planningRevision.refresh_outcome = "assignment_changed";
          } else if (!isCoherentPlanningResult(fresh, { dispatchDate: mutationOriginDate, dispatchPeriod: mutationOriginPeriod })) {
            planningRevision.refresh_outcome = planningRevision.result_runtime_revision_present ? "invalid_response" : "missing_revision";
          } else {
            const assignment = findAssignmentByTruckId(fresh, truckId);
            planningRevision.refresh_outcome = !assignment ? "truck_missing"
              : !Number.isInteger(expectedVersion) || expectedVersion < 1 || assignment.driver_assignment_version !== expectedVersion
                ? "assignment_changed" : "succeeded";
            const currentState = workspaceStore.getState();
            writePageDataCacheEntry(getScopedResultsLatestApiUrl(mutationOriginPeriod), fresh);
            const selection = syncResultsAfterPlanningMutation(currentState, fresh, {
              actionName: "driver_assignment_refresh",
            });
            onWorkspaceRender?.({ assignment: mutationOriginTruck ? selection.nextAssignment : null, trip: mutationOriginTruck ? selection.nextTrip : null });
            if (planningRevision.refresh_outcome === "succeeded") {
              // Keep the original employee and assignment version; only supply the missing revision.
              requestBody = { ...requestBody, expected_runtime_revision: fresh.runtime_revision };
            }
          }
          if (["context_changed", "planning_replaced", "invalid_response", "missing_revision"].includes(planningRevision.refresh_outcome)) {
            recordPlanningRevisionIgnored(fresh, planningRevision.refresh_outcome);
          }
        } catch {
          if (!canContinue()) planningRevision.refresh_outcome = "context_changed";
          else if (planningRevision.refresh_outcome === "pending") planningRevision.refresh_outcome = "failed";
        } finally {
          clearTimeout(timeout);
          activePlanningRecoveriesRef.current.delete(activeRead);
        }
        if (planningRevision.refresh_outcome !== "succeeded" || !canContinue() || readController.signal.aborted) {
          if (!canContinue()) planningRevision.refresh_outcome = "context_changed";
          const preflightError = new Error(["assignment_changed", "truck_missing", "planning_replaced"].includes(planningRevision.refresh_outcome)
            ? "Le camion ou son affectation a changé. Vérifiez le chauffeur puis validez à nouveau. Aucune affectation n’a été envoyée."
            : planningRevision.refresh_outcome === "context_changed"
              ? "Le contexte a changé. Validez à nouveau l’affectation dans le Dispatch souhaité. Aucune affectation n’a été envoyée."
              : "Le Dispatch n’a pas pu être actualisé avec une version valide. Le chauffeur sélectionné est conservé ; réessayez l’enregistrement.");
          preflightError.isPlanningPreflight = true;
          preflightError.reasonCode = `driver_assignment_refresh_${planningRevision.refresh_outcome}`;
          throw preflightError;
        }
      }

      planningRevision.expected_runtime_revision_present = Boolean(normalizeInlineText(requestBody?.expected_runtime_revision));
      planningRevision.expected_runtime_revision = requestBody?.expected_runtime_revision;
      reportProgress("process");
      mutationTransport?.request();
      let response = await fetchWithSupabaseAuth(requestUrl, {
        method,
        headers: {
          ...(requestBody ? { "Content-Type": "application/json" } : {}),
          ...(requestHeaders && typeof requestHeaders === "object" ? requestHeaders : {}),
          ...(diagnosticRecord?.enabled ? { "X-Planning-Diagnostics": "1" } : {}),
          ...buildPlanningPerformanceActionHeaders(resolvePlanningPerformanceActionName(actionName, {
            isUndo,
          }), {
            chainId: performanceChainId,
            tabId: performanceTabId,
          }),
        },
        body: requestBody ? JSON.stringify(requestBody) : undefined,
        signal: controller?.signal,
      }).catch((requestError) => resolveUnansweredPlanningRequest(actionName, requestError));

      if (diagnosticRecord) {
        diagnosticRecord.mark("response_received");
      }

      mutationTransport?.response(response);
      let payload = await response.json().then((value) => {
        mutationTransport?.json("parsed", null, value);
        return value;
      }, (error) => {
        mutationTransport?.json("invalid", error);
        return {};
      });
      validationErrors = response.status === 422 ? extractPlanningValidationErrors(payload) : [];
      planningRevision.result_runtime_revision_present = Boolean(normalizeInlineText(payload?.result_payload?.runtime_revision || payload?.runtime_revision));
      recordPlanningRevisionResponse(payload, response, { url: requestUrl, actionName });
      planningRevision.request_id = response.headers?.get?.("X-Request-ID") || payload?.request_id;
      planningRevision.current_runtime_revision = payload?.current_runtime_revision;
      planningRevision.revision_cause = payload?.revision_cause;
      planningRevision.received_runtime_revision = payload?.result_payload?.runtime_revision || payload?.runtime_revision
        || planningRevisionTimeline(mutationOriginDate, normalizedMutationDispatchPeriod || mutationOriginPeriod)
          .findLast(event => event.outcome === "received" && event.runtime_revision)?.runtime_revision;
      if (!response.ok) failedRevisionEvidence = revisionEvidence();
      ganttHttpStatus = response.status;
      ganttDiagnostics = payload?._planning_diagnostics || null;
      if (diagnosticRecord) {
        diagnosticRecord.mark("response_json_parsed");
      }
      if (shouldRecoverPlanningMutation(actionName, response)) {
        const recoveryDate = performanceScope.dispatchDate || mutationOriginDate;
        const recoveryPeriod = normalizedMutationDispatchPeriod || mutationOriginPeriod;
        const canContinueRecovery = () => isMountedRef.current
          && !controller?.signal.aborted
          && getCurrentDispatchDateFromLocation() === mutationOriginDate
          && getCurrentDispatchPeriodFromLocation() === mutationOriginPeriod;
        if (!canContinueRecovery()) return null;
        const captureContext = createMutationCaptureContext({
          correlationId: resolvePlanningResponseCorrelationId(response, payload, performanceChainId),
          detailItems: statusNoticeErrorDetailItems,
          failureKind: response.type === "error" ? "network" : "http",
          httpStatus: response.status || null,
          reasonCode: "planning_mutation_outcome_unknown",
        });
        const publishRecoveryFeedback = (kind) => {
          const feedback = buildPlanningRecoveryFeedback(kind, actionName);
          const toast = { ...feedback, captureContext, progress: null };
          if (loadingToastId) updateToast(loadingToastId, toast);
          else loadingToastId = showToast(toast);
          hasResolvedLoadingToast = kind !== "checking";
          if (statusNoticeContent) publishStatusNotice({
            ...feedback, captureContext, detailItems: statusNoticeErrorDetailItems,
            ...(kind !== "checking" ? { finishedAt: new Date().toISOString() } : {}),
          });
          return feedback;
        };
        publishRecoveryFeedback("checking");
        // Cancel recovery reads independently; the submitted write may still finish.
        const recoveryController = new AbortController();
        const activeRecovery = { controller: recoveryController, canContinue: canContinueRecovery };
        const abortRecovery = () => recoveryController.abort();
        controller?.signal.addEventListener("abort", abortRecovery, { once: true });
        if (controller?.signal.aborted) abortRecovery();
        activePlanningRecoveriesRef.current.add(activeRecovery);
        let recovery;
        try {
          recovery = await recoverPlanningMutation({
            operationId: performanceChainId, actionName, dispatchDate: recoveryDate, dispatchPeriod: recoveryPeriod,
            fetchImpl: fetchWithSupabaseAuth, signal: recoveryController.signal,
            canContinue: canContinueRecovery,
          });
        } finally {
          activePlanningRecoveriesRef.current.delete(activeRecovery);
          controller?.signal.removeEventListener("abort", abortRecovery);
        }
        if (recovery.kind === "cancelled") {
          if (getAppStatusNoticeSnapshot().notice?.captureContext === captureContext) clearAppStatusNotice();
          return null;
        }
        if (!["recovered", "rejected"].includes(recovery.kind)) {
          const feedback = publishRecoveryFeedback(recovery.kind);
          invalidatePageDataCacheEntries([RESULTS_LATEST_API_URL]);
          invalidatePageDataCacheEntries(PLANNING_MUTATION_INVALIDATED_API_URLS);
          invalidateShellDataCache();
          dispatchPlanningStateChange("dispatch", {
            actionName: "planning_mutation_outcome_unknown",
            ...performanceScope, dispatchDate: recoveryDate, dispatchPeriod: recoveryPeriod, performanceChainId,
            revalidateCurrentView: true,
          });
          onMutationError?.({ deliveryId, ...feedback, reasonCode: "planning_mutation_outcome_unknown" });
          if (rethrowOnError) {
            const error = new Error(feedback.message);
            error.hasPublishedPlanningMutationFeedback = true;
            throw error;
          }
          return null;
        }
        payload = recovery.payload;
        response = { ok: recovery.kind === "recovered", status: recovery.kind === "recovered" ? 200 : 409, headers: response.headers };
        if (recovery.kind === "recovered") {
          canApplyRecoveredMutation = canContinueRecovery;
          updateToast(loadingToastId, { captureContext: baseCaptureContext });
        }
      }
      if (!response.ok) {
        if (
          typeof onMutationChallenge === "function"
          && onMutationChallenge({
            actionName,
            payload,
            requestBody,
            response,
            state,
          }) === true
        ) {
          return null;
        }
        const planningDiagnostics = isDiagnosticObject(payload?._planning_diagnostics)
          ? payload._planning_diagnostics
          : null;
        if (diagnosticRecord && planningDiagnostics) {
          diagnosticRecord.attachServerDiagnostics(planningDiagnostics);
        }
        if (planningDiagnostics) {
          const currentState = resolveWorkspaceState(state);
          diagnosticPayloadForClientTiming = capturePlanningMutationErrorDiagnostics(
            currentState,
            payload,
            planningDiagnostics,
          );
          if (diagnosticPayloadForClientTiming) {
            rerenderWorkspace(workspaceStore, onWorkspaceRender);
          }
        }
        const planningMutationConflictReasonCode = response.status === 409
          ? normalizeInlineText(payload?.reason_code || payload?.code).toLowerCase()
          : "";
        const isRuntimeRevisionConflict = planningMutationConflictReasonCode === "runtime_revision_conflict";
        const isPlanningMutationInProgress = planningMutationConflictReasonCode === "planning_mutation_in_progress";
        const isDeliveryClientRepeatConfirmationStale = planningMutationConflictReasonCode
          === DELIVERY_CLIENT_REPEAT_CONFIRMATION_STALE_CODE;
        const isCapacitySplitConfirmationStale = planningMutationConflictReasonCode
          === "delivery_group_capacity_split_confirmation_stale";
        const isCompartmentSharingConfirmationStale = planningMutationConflictReasonCode === COMPARTMENT_SHARING_STALE_CODE;
        if (
          isRuntimeRevisionConflict
          || isPlanningMutationInProgress
          || isDeliveryClientRepeatConfirmationStale
          || isCapacitySplitConfirmationStale
          || isCompartmentSharingConfirmationStale
        ) {
          invalidatePageDataCacheEntries([RESULTS_LATEST_API_URL]);
          invalidatePageDataCacheEntries(PLANNING_MUTATION_INVALIDATED_API_URLS);
          invalidateShellDataCache();
          dispatchPlanningStateChange("dispatch", {
            actionName: planningMutationConflictReasonCode,
            currentRuntimeRevision: normalizeInlineText(
              payload?.current_runtime_revision || payload?.error_details?.runtime_revision,
            ),
            ...performanceScope,
            ...(normalizedMutationDispatchPeriod
              ? { dispatchPeriod: normalizedMutationDispatchPeriod }
              : {}),
            expectedRuntimeRevision: normalizeInlineText(
              payload?.expected_runtime_revision || payload?.error_details?.expected_runtime_revision,
            )
              || normalizeInlineText(requestBody?.expected_runtime_revision),
            ...(isPlanningMutationInProgress ? { deferRevalidationUntilRuntimeRevision: true } : {}),
            performanceChainId,
            reasonCode: planningMutationConflictReasonCode,
          });

          if (isRuntimeRevisionConflict || isDeliveryClientRepeatConfirmationStale || isCapacitySplitConfirmationStale || isCompartmentSharingConfirmationStale) {
            if (actionName === "update_truck_driver_name" && isRuntimeRevisionConflict) {
              const readController = new AbortController();
              const canContinue = () => isCurrentMutationContext() && !shouldPreserveCurrentWorkspace;
              const activeRead = { controller: readController, canContinue };
              activePlanningRecoveriesRef.current.add(activeRead);
              const timeout = setTimeout(() => readController.abort(), 20000);
              const beforeRefresh = workspaceStore.getState()?.resultsData;
              planningRevision.refresh_outcome = "pending";
              try {
                if (!canContinue()) throw new Error("Contexte changé");
                const refreshed = await loadPageData(getForcedResultsLatestApiUrl(mutationOriginPeriod), {
                  force: true, signal: readController.signal,
                });
                if (!canContinue() || readController.signal.aborted) planningRevision.refresh_outcome = "context_changed";
                else if (workspaceStore.getState()?.resultsData !== beforeRefresh) planningRevision.refresh_outcome = "planning_replaced";
                else if (!isCoherentPlanningResult(refreshed, { dispatchDate: mutationOriginDate, dispatchPeriod: mutationOriginPeriod })) {
                  planningRevision.refresh_outcome = "invalid_response";
                } else {
                  const selection = syncResultsAfterPlanningMutation(workspaceStore.getState(), refreshed, {
                    actionName: "driver_assignment_conflict_refresh",
                  });
                  onWorkspaceRender?.({ assignment: selection.nextAssignment, trip: selection.nextTrip });
                  planningRevision.refresh_outcome = "succeeded";
                }
                if (planningRevision.refresh_outcome !== "succeeded") recordPlanningRevisionIgnored(refreshed, planningRevision.refresh_outcome);
              } catch {
                planningRevision.refresh_outcome = canContinue() ? "failed" : "context_changed";
              } finally {
                clearTimeout(timeout);
                activePlanningRecoveriesRef.current.delete(activeRead);
              }
            } else {
            try {
              const refreshedPayload = await loadPageData(
                getForcedResultsLatestApiUrl(normalizedMutationDispatchPeriod),
                { force: true },
              );
              if (
                refreshedPayload
                && typeof refreshedPayload === "object"
                && !Array.isArray(refreshedPayload)
              ) {
                const currentState = resolveWorkspaceState(state);
                writePageDataCacheEntry(
                  getScopedResultsLatestApiUrl(normalizedMutationDispatchPeriod),
                  refreshedPayload,
                );
                if (isMountedRef.current && currentState && !shouldPreserveCurrentWorkspace) {
                  const nextSelection = syncResultsAfterPlanningMutation(
                    currentState,
                    refreshedPayload,
                    { actionName: `${actionName}_conflict_refresh` },
                  );
                  onWorkspaceRender?.({
                    assignment: nextSelection.nextAssignment,
                    trip: nextSelection.nextTrip,
                  });
                }
                dispatchPlanningStateChange("dispatch", {
                  actionName: `${planningMutationConflictReasonCode}_reload`,
                  ...resolvePlanningPerformanceEventScope(refreshedPayload, currentState),
                  ...(normalizedMutationDispatchPeriod
                    ? { dispatchPeriod: normalizedMutationDispatchPeriod }
                    : {}),
                  performanceChainId,
                  runtimeRevision: normalizeInlineText(
                    refreshedPayload?.runtime_revision
                      || payload?.current_runtime_revision,
                  ),
                });
              }
            } catch (refreshError) {
              console.warn(
                "Le planning n'a pas pu être rechargé immédiatement après le conflit de révision.",
                refreshError,
              );
            }
            }
          }
        }

        const rawErrorMessage = actionName === "update_truck_driver_name" && isRuntimeRevisionConflict
          ? planningRevision.refresh_outcome === "succeeded"
            ? "Le planning a changé et vient d’être actualisé. Vérifiez le chauffeur, puis validez à nouveau l’affectation."
            : planningRevision.refresh_outcome === "context_changed"
              ? "L’affectation n’a pas été enregistrée. La journée ou la période affichée a changé ; vérifiez le Dispatch souhaité avant de valider à nouveau."
              : "L’affectation n’a pas été enregistrée. Le planning a changé et n’a pas pu être actualisé ; actualisez le Dispatch avant de valider à nouveau."
          : payload && payload.error
          ? payload.error
          : payload && payload.detail
            ? payload.detail
            : isDeliveryClientRepeatConfirmationStale
              ? "Le planning a changé depuis la confirmation. La vue a été actualisée; recommencez la réaffectation."
              : `La mise à jour du planning manuel a échoué (HTTP ${response.status}).`;
        const errorMessage = typeof resolveServerErrorMessage === "function"
          ? (resolveServerErrorMessage({
            actionName,
            body,
            errorMessage: rawErrorMessage,
            payload,
            response,
            state,
          }) || rawErrorMessage)
          : rawErrorMessage;
        const errorFeedback = resolvePlanningMutationErrorFeedback(
          state,
          errorMessage,
          loadingToastContent?.errorTitle || "Modification du planning interrompue",
        );
        const resolvedErrorDetailItems = [
          ...statusNoticeErrorDetailItems,
          ...errorFeedback.detailItems,
        ];
        const responseCorrelationId = resolvePlanningResponseCorrelationId(response, payload, performanceChainId);
        const failureCaptureContext = createMutationCaptureContext({
          correlationId: responseCorrelationId,
          detailItems: resolvedErrorDetailItems,
          failureKind: "http",
          httpStatus: response.status,
          planningDiagnostics,
          reasonCode: normalizeInlineText(payload?.reason_code || payload?.code || payload?.detail?.code),
        });

        if (loadingToastId) {
          updateToast(loadingToastId, {
            captureContext: failureCaptureContext,
            duration: errorFeedback.duration,
            emphasis: errorFeedback.emphasis,
            message: errorFeedback.message,
            title: errorFeedback.title,
            type: "error",
          });
          hasResolvedLoadingToast = true;
        } else {
          showToast({
            captureContext: failureCaptureContext,
            duration: errorFeedback.duration,
            emphasis: errorFeedback.emphasis,
            message: errorFeedback.message,
            title: errorFeedback.title,
            type: "error",
          });
        }

        if (statusNoticeContent) {
          publishStatusNotice({
            captureContext: failureCaptureContext,
            detailItems: resolvedErrorDetailItems,
            eyebrow: statusNoticeContent.eyebrow || "Mise à jour du planning",
            finishedAt: new Date().toISOString(),
            message: errorFeedback.message,
            planningDiagnostics,
            status: "failed",
            statusLabel: errorFeedback.statusLabel,
            title: errorFeedback.title,
          });
        }

        onMutationError?.({
          detailItems: resolvedErrorDetailItems,
          deliveryId,
          message: errorFeedback.message,
          payload,
          reasonCode: normalizeInlineText(payload?.reason_code),
          title: errorFeedback.title,
        });

        if (payload?.source_changes_refresh_required === true) {
          dispatchPlanningStateChange("dispatch", {
            actionName,
            ...buildSourceChangesVerificationEventMetadata(actionName, requestBody),
            ...performanceScope,
            performanceChainId,
            runtimeRevision: normalizeInlineText(payload?.runtime_revision),
          });
        }

        if (rethrowOnError) {
          const planningMutationError = new Error(errorFeedback.message);
          planningMutationError.payload = payload;
          planningMutationError.reasonCode = normalizeInlineText(payload?.reason_code);
          planningMutationError.status = response.status;
          planningMutationError.hasPublishedPlanningMutationFeedback = true;
          throw planningMutationError;
        }
        return null;
      }

      reportProgress("refresh");
      const portalOnlyResult = isPortalOnlyPlanningMutationResult(actionName, payload);
      if (portalOnlyResult) refreshResultsAfterMissingPayload = false;
      let mutationResultPayload = await resolveDispatchMutationResultPayload({
        actionName,
        allowMissingResultPayload: canCompleteWithoutWorkspaceSync,
        body,
        deliveryId,
        diagnosticRecord,
        payload,
        skipWorkspaceSync: shouldPreserveCurrentWorkspace || portalOnlyResult,
      });
      if (canApplyRecoveredMutation && !canApplyRecoveredMutation()) return null;
      if (requiresSavedPlanningView && (payload?.planning_refresh_required === true || !isCoherentPlanningResult(mutationResultPayload, {
        dispatchDate: mutationOriginDate, dispatchPeriod: normalizedMutationDispatchPeriod || mutationOriginPeriod,
      }))) mutationResultPayload = null;
      if ((requiresSavedPlanningView || actionName === "update_truck_driver_name") && !canApplySavedPlanningView()) {
        updateToast(loadingToastId, { title: "Modification enregistrée", message: "La modification est enregistrée dans le Dispatch d’origine.", type: "success", duration: 5000 });
        hasResolvedLoadingToast = true;
        return payload;
      }

      if (mutationResultPayload === null && canCompleteWithoutWorkspaceSync) {
        let successMessage = withCollateralPreloadNote(
          normalizeInlineText(payload?.message)
            || resolvePlanningMutationContentValue(statusNoticeContent?.successMessage, { payload, actionName, body })
            || resolvePlanningMutationContentValue(loadingToastContent?.successMessage, { payload, actionName, body })
            || "La mise à jour technique est terminée.",
          payload,
        );

        invalidatePageDataCacheEntries([RESULTS_LATEST_API_URL]);
        invalidatePageDataCacheEntries(PLANNING_MUTATION_INVALIDATED_API_URLS);
        invalidateShellDataCache();

        if (payload?.portal_publications && !shouldPreserveCurrentWorkspace) {
          onPortalPublicationsChange?.(payload.portal_publications, { actionName, payload, resultPayload: null });
        }
        let refreshedResultPayloadAfterMissing = null;
        let refreshedWorkspaceStateAfterMissing = null;
        if (refreshResultsAfterMissingPayload) {
          const readController = new AbortController();
          const activeRead = { controller: readController, canContinue: canApplySavedPlanningView };
          activePlanningRecoveriesRef.current.add(activeRead);
          const beforeRefresh = workspaceStore.getState()?.resultsData;
          const timeout = setTimeout(() => readController.abort(), 20000);
          planningRevision.refresh_outcome = "pending";
          try {
            const refreshedResultPayload = await loadPageData(
              getForcedResultsLatestApiUrl(normalizedMutationDispatchPeriod),
              { force: true, signal: readController.signal },
            );
            planningRevision.result_runtime_revision_present = Boolean(normalizeInlineText(refreshedResultPayload?.runtime_revision));
            planningRevision.received_runtime_revision = refreshedResultPayload?.runtime_revision;
            if (!canApplySavedPlanningView() || readController.signal.aborted) planningRevision.refresh_outcome = "context_changed";
            else if (workspaceStore.getState()?.resultsData !== beforeRefresh) planningRevision.refresh_outcome = "planning_replaced";
            else if (!isCoherentPlanningResult(refreshedResultPayload, {
              dispatchDate: mutationOriginDate, dispatchPeriod: normalizedMutationDispatchPeriod || mutationOriginPeriod,
            })) planningRevision.refresh_outcome = planningRevision.result_runtime_revision_present ? "invalid_response" : "missing_revision";
            else {
              const refreshedWorkspaceState = resolveWorkspaceState(state);
              refreshedResultPayloadAfterMissing = refreshedResultPayload;
              refreshedWorkspaceStateAfterMissing = refreshedWorkspaceState;
              writePageDataCacheEntry(getScopedResultsLatestApiUrl(normalizedMutationDispatchPeriod), refreshedResultPayload);
              if (refreshedWorkspaceState && !shouldPreserveCurrentWorkspace) {
                const nextSelection = syncResultsAfterPlanningMutation(refreshedWorkspaceState, refreshedResultPayload, {
                  actionName: `${actionName}_refresh`,
                });
                onWorkspaceRender?.({ assignment: mutationOriginTruck ? nextSelection.nextAssignment : null, trip: mutationOriginTruck ? nextSelection.nextTrip : null });
              }
              if (refreshedResultPayload.portal_publications) onPortalPublicationsChange?.(refreshedResultPayload.portal_publications, {
                actionName, payload, resultPayload: refreshedResultPayload,
              });
              planningRevision.refresh_outcome = "succeeded";
            }
            if (planningRevision.refresh_outcome !== "succeeded") recordPlanningRevisionIgnored(refreshedResultPayload, planningRevision.refresh_outcome);
          } catch {
            planningRevision.refresh_outcome = canApplySavedPlanningView() ? "failed" : "context_changed";
          } finally {
            clearTimeout(timeout);
            activePlanningRecoveriesRef.current.delete(activeRead);
          }
        }
        const refreshIncomplete = refreshResultsAfterMissingPayload && !refreshedResultPayloadAfterMissing;
        const successTitle = refreshIncomplete ? "Modification enregistrée — Dispatch à actualiser"
          : resolvePlanningMutationContentValue(loadingToastContent?.successTitle, { payload, actionName, body }) || "Mise à jour terminée";
        const savedCaptureContext = createMutationCaptureContext();
        if (refreshIncomplete) successMessage = "La modification est enregistrée. Le Dispatch n’a pas pu être actualisé ; actualisez la vue avant la prochaine modification.";

        if (beforeSuccessFeedback) await beforeSuccessFeedback({ deliveryId, loadingToastContent, payload });
        // Le toast reste en cours et passe, avec l'événement ci-dessous, à l'actualisation camion qui suit.
        const handedOffToast = handOffLoadingToast && loadingToastId && !refreshIncomplete
          && (shouldPreserveCurrentWorkspace || refreshedResultPayloadAfterMissing)
          ? { id: loadingToastId, title: loadingToastContent?.title,
            message: resolvePlanningMutationContentValue(loadingToastContent?.successMessage, { payload, actionName, body }) || successMessage,
            // Entrée Captures du changement, complétée à la fin de l'actualisation qui suit.
            capture: payload?.capture ? { ...payload.capture, startedAt: mutationStartedAt, changeMs: performance.now() - mutationStartedAt } : null }
          : null;

        if (loadingToastId && (preserveLoadingToastOnSuccess || handedOffToast)) {
          hasResolvedLoadingToast = true;
        } else if (loadingToastId) {
          updateToast(loadingToastId, {
            duration: typeof loadingToastContent?.successDuration === "number"
              ? loadingToastContent.successDuration
              : 5000,
            message: successMessage,
            title: successTitle,
            captureContext: savedCaptureContext,
            type: refreshIncomplete ? "warning" : "success",
          });
          hasResolvedLoadingToast = true;
        } else {
          showToast({
            duration: 5000,
            message: successMessage,
            title: successTitle,
            captureContext: savedCaptureContext,
            type: refreshIncomplete ? "warning" : "success",
          });
        }

        const portalPublicationInvalidationToast = buildPlanningPortalPublicationInvalidationToast(
          actionName,
          payload,
        );
        if (portalPublicationInvalidationToast) {
          showToast(portalPublicationInvalidationToast);
        }

        if (statusNoticeContent) {
          publishStatusNotice({
            detailItems: buildPlanningMutationStatusNoticeDetailItems({
              statusNoticeContent,
            }),
            eyebrow: statusNoticeContent.eyebrow || "Mise à jour du planning",
            finishedAt: new Date().toISOString(),
            message: successMessage,
            resultActionLabel: statusNoticeContent.resultActionLabel || "Ouvrir Affectation",
            resultUrl: statusNoticeContent.resultUrl || appendCurrentDispatchDate("/results"),
            status: refreshIncomplete ? "partial" : "completed",
            captureContext: savedCaptureContext,
            statusLabel: refreshIncomplete ? "À actualiser" : statusNoticeContent.successStatusLabel || "Terminé",
            title: refreshIncomplete ? successTitle : resolvePlanningMutationContentValue(statusNoticeContent.successTitle, { payload, actionName, body }) || successTitle,
          });
        }

        if (shouldPreserveCurrentWorkspace) {
          if (Array.isArray(explicitHighlightedStepKeys) && explicitHighlightedStepKeys.length > 0) {
            scheduleTransientActivatedSteps(explicitHighlightedStepKeys, {
              dispatchPeriod: normalizedMutationDispatchPeriod,
              durationMs: resolveTransientActivationHighlightDuration({ actionName, body }),
              highlightSurface,
            });
          }
          dispatchPlanningStateChange("dispatch", {
            actionName,
            dispatchDate: getCurrentDispatchDateFromLocation(),
            dispatchPeriod: normalizedMutationDispatchPeriod,
            ...(handedOffToast ? { loadingToast: handedOffToast } : {}),
            runtimeRevision: normalizeInlineText(
              payload?.result_payload?.runtime_revision || payload?.runtime_revision,
            ),
          });
        }

        if (
          refreshedResultPayloadAfterMissing
          && !shouldPreserveCurrentWorkspace
        ) {
          dispatchPlanningStateChange("dispatch", {
            actionName,
            ...resolvePlanningPerformanceEventScope(
              refreshedResultPayloadAfterMissing,
              refreshedWorkspaceStateAfterMissing,
            ),
            ...(handedOffToast ? { loadingToast: handedOffToast } : {}),
            runtimeRevision: normalizeInlineText(
              refreshedResultPayloadAfterMissing?.runtime_revision,
            ),
          });
        }

        onMutationSuccess?.({
          deliveryId,
          loadingToastContent,
          payload,
        });
        return payload;
      }

      if (typeof mutationResultPayload !== "object" || mutationResultPayload === null) {
        const invalidPayloadMessage = "La réponse du serveur est incomplète. Rechargez la vue et réessayez.";
        const invalidPayloadDiagnostics = isDiagnosticObject(payload?._planning_diagnostics)
          ? payload._planning_diagnostics
          : null;
        const invalidPayloadCaptureContext = createMutationCaptureContext({
          detailItems: statusNoticeErrorDetailItems,
          failureKind: "invalid_response",
          httpStatus: response.status,
          planningDiagnostics: invalidPayloadDiagnostics,
          reasonCode: "missing_result_payload",
        });

        if (loadingToastId) {
          updateToast(loadingToastId, {
            captureContext: invalidPayloadCaptureContext,
            duration: 8000,
            message: invalidPayloadMessage,
            title: loadingToastContent?.errorTitle || "Modification du planning interrompue",
            type: "error",
          });
          hasResolvedLoadingToast = true;
        } else {
          showToast({
            captureContext: invalidPayloadCaptureContext,
            duration: 8000,
            message: invalidPayloadMessage,
            title: "Modification du planning interrompue",
            type: "error",
          });
        }

        if (statusNoticeContent) {
          publishStatusNotice({
            captureContext: invalidPayloadCaptureContext,
            detailItems: statusNoticeErrorDetailItems,
            eyebrow: statusNoticeContent.eyebrow || "Mise à jour du planning",
            finishedAt: new Date().toISOString(),
            message: invalidPayloadMessage,
            planningDiagnostics: invalidPayloadDiagnostics,
            status: "failed",
            statusLabel: statusNoticeContent.errorStatusLabel || "Échec",
            title: statusNoticeContent.errorTitle || loadingToastContent?.errorTitle || "Modification du planning interrompue",
          });
        }

        onMutationError?.({
          detailItems: statusNoticeErrorDetailItems,
          deliveryId,
          message: invalidPayloadMessage,
          title: loadingToastContent?.errorTitle || "Modification du planning interrompue",
        });
        if (rethrowOnError) {
          const planningMutationError = new Error(invalidPayloadMessage);
          planningMutationError.hasPublishedPlanningMutationFeedback = true;
          throw planningMutationError;
        }
        return null;
      }

      if (diagnosticRecord && payload?._planning_diagnostics) {
        diagnosticRecord.attachServerDiagnostics(payload._planning_diagnostics);
      }

      const currentState = resolveWorkspaceState(state);
      const planningDiagnostics = isDiagnosticObject(payload?._planning_diagnostics)
        ? payload._planning_diagnostics
        : null;
      const resolvedResultPayload = buildResolvedDispatchMutationResultPayload({
        actionName,
        currentState,
        planningDiagnostics,
        resultPayload: mutationResultPayload,
      });
      if (planningDiagnostics) {
        const diagnosticDispatchDate = normalizeInlineText(resolvedResultPayload?.dispatch_date)
          || normalizeInlineText(resolvedResultPayload?.planning_summary?.dispatch_date)
          || normalizeInlineText(payload?.dispatch_date)
          || normalizeInlineText(payload?.planning_summary?.dispatch_date)
          || getCurrentDispatchDateFromLocation();
        diagnosticPayloadForClientTiming = captureLastReassignmentDiagnosticPayload(currentState, {
          _planning_diagnostics: planningDiagnostics,
          dispatch_date: diagnosticDispatchDate,
          planning_summary: diagnosticDispatchDate ? { dispatch_date: diagnosticDispatchDate } : {},
        });
      }
      const syncedResultPayload = resolvedResultPayload;
      const nextPortalPublications = payload?.portal_publications
        || syncedResultPayload?.portal_publications
        || null;
      if (nextPortalPublications && typeof onPortalPublicationsChange === "function") {
        onPortalPublicationsChange(nextPortalPublications, {
          actionName,
          payload,
          resultPayload: syncedResultPayload,
        });
      }
      const workspaceSelectionContext = buildDispatchMutationWorkspaceSelectionContext({
        actionName,
        body,
        currentState,
        deliveryId,
        explicitPreferredAssignmentId,
        loadingStepId,
        resolvedResultPayload,
      });
      const {
        deliveryActivationTransition,
        linkedStepTransition,
        planningToast,
        preferredAssignmentId,
        preferredDeliveryId,
      } = workspaceSelectionContext;

      invalidatePageDataCacheEntries([RESULTS_LATEST_API_URL]);
      invalidatePageDataCacheEntries(PLANNING_MUTATION_INVALIDATED_API_URLS);
      invalidateShellDataCache();
      writePageDataCacheEntry(getScopedResultsLatestApiUrl(), resolvedResultPayload);

      if (isMountedRef.current) {
        if (diagnosticRecord) {
          diagnosticRecord.mark("sync_start");
        }

        const nextSelection = syncResultsAfterPlanningMutation(
          currentState,
          resolvedResultPayload,
          {
            preferredAssignmentId,
            preferredDeliveryId,
            actionName,
          },
        );
        syncOvertimeWarningTruckIds(currentState, resolvedResultPayload, { actionName, body });

        if (diagnosticRecord) {
          diagnosticRecord.mark("sync_end");
        }

        onWorkspaceRender?.({
          assignment: nextSelection.nextAssignment,
          trip: nextSelection.nextTrip,
        });

        if (preferredAssignmentId) {
          revealAssignmentCard(currentState, preferredAssignmentId);
        }

        const resolvedHighlightedStepKeys = resolvePlanningMutationHighlightKeys({
          actionName,
          assignmentId: preferredAssignmentId,
          body,
          deliveryId,
          deliveryIds: Array.isArray(body?.delivery_ids) ? body.delivery_ids : [],
          deliveryTransition: deliveryActivationTransition,
          linkedStepTransition,
          loadingStepId,
          orderId,
          resultPayload: syncedResultPayload,
        });
        const mergedHighlightedStepKeys = [
          ...(Array.isArray(explicitHighlightedStepKeys) ? explicitHighlightedStepKeys : []),
          ...resolvedHighlightedStepKeys,
        ].filter((stepKey, index, stepKeys) => (
          typeof stepKey === "string"
          && stepKey
          && stepKeys.indexOf(stepKey) === index
        ));
        scheduleTransientActivatedSteps(
          mergedHighlightedStepKeys,
          {
            dispatchPeriod: normalizedMutationDispatchPeriod,
            durationMs: resolveTransientActivationHighlightDuration({ actionName, body }),
            highlightSurface,
          },
        );
      }

      const successContentContext = {
        actionName,
        body,
        payload,
        resultPayload: syncedResultPayload,
      };
      if (beforeSuccessFeedback) await beforeSuccessFeedback({ deliveryId, loadingToastContent, payload });
      if (canApplyRecoveredMutation && !canApplyRecoveredMutation()) return null;
      const resolvedLoadingSuccessMessage = withCollateralPreloadNote(
        planningToast?.message
          || resolvePlanningMutationContentValue(loadingToastContent?.successMessage, successContentContext)
          || normalizeInlineText(payload?.message)
          || "Le planning manuel a été mis à jour.",
        payload,
      );
      const resolvedLoadingSuccessTitle = planningToast?.title || resolvePlanningMutationContentValue(
        loadingToastContent?.successTitle,
        successContentContext,
      ) || "Mise à jour terminée";
      const resolvedStatusSuccessMessage = resolvePlanningMutationContentValue(
        statusNoticeContent?.successMessage,
        successContentContext,
      ) || resolvedLoadingSuccessMessage;
      const resolvedStatusSuccessTitle = resolvePlanningMutationContentValue(
        statusNoticeContent?.successTitle,
        successContentContext,
      ) || resolvedLoadingSuccessTitle;
      const resolvedStatusSuccessLabel = resolvePlanningMutationContentValue(
        statusNoticeContent?.successStatusLabel,
        successContentContext,
      ) || "Terminé";

      if (loadingToastId && preserveLoadingToastOnSuccess) {
        hasResolvedLoadingToast = true;
      } else if (loadingToastId) {
        updateToast(loadingToastId, {
          duration: typeof loadingToastContent?.successDuration === "number"
            ? loadingToastContent.successDuration
            : 5000,
          message: resolvedLoadingSuccessMessage,
          title: resolvedLoadingSuccessTitle,
          type: "success",
        });
        hasResolvedLoadingToast = true;
      } else if (planningToast) {
        showToast({
          duration: 5000,
          message: planningToast.message,
          title: planningToast.title,
          type: "success",
        });
      }

      const portalPublicationInvalidationToast = buildPlanningPortalPublicationInvalidationToast(
        actionName,
        payload,
        syncedResultPayload,
      );
      if (portalPublicationInvalidationToast) {
        showToast(portalPublicationInvalidationToast);
      }

      if (statusNoticeContent) {
        const targetTruckId = body && typeof body.target_truck_id === "string"
          ? body.target_truck_id
          : null;
        const planningDiagnostics = isDiagnosticObject(payload._planning_diagnostics)
          ? payload._planning_diagnostics
          : null;
        const optimizationDiagnostics = isDiagnosticObject(syncedResultPayload?.optimization_diagnostics)
          ? syncedResultPayload.optimization_diagnostics
          : null;

        publishStatusNotice({
          detailItems: buildPlanningMutationStatusNoticeDetailItems({
            deliveryId,
            optimizationDiagnostics,
            planningDiagnostics,
            resultPayload: syncedResultPayload,
            statusNoticeContent,
          }),
          eyebrow: statusNoticeContent.eyebrow || "Mise à jour du planning",
          executionSummaryContext: buildPlanningMutationExecutionSummaryContext(
            currentState.resultsData,
            syncedResultPayload,
            {
              deliveryId,
              targetTruckId,
            },
          ),
          finishedAt: new Date().toISOString(),
          message: resolvedStatusSuccessMessage,
          optimizationDiagnostics,
          planningDiagnostics,
          resultActionLabel: statusNoticeContent.resultActionLabel || "Ouvrir Affectation",
          resultUrl: statusNoticeContent.resultUrl || appendCurrentDispatchDate("/results"),
          serviceHealth: syncedResultPayload?.service_health,
          status: "completed",
          statusLabel: resolvedStatusSuccessLabel,
          title: resolvedStatusSuccessTitle,
        });
      }

      dispatchPlanningStateChange("dispatch", {
        actionName,
        deliveryId,
        eveningSuspendedUpdate: payload?.evening_suspended_update,
        ...buildSourceChangesVerificationEventMetadata(actionName, requestBody),
        ...resolvePlanningPerformanceEventScope(syncedResultPayload, currentState),
        performanceChainId,
        runtimeRevision: normalizeInlineText(syncedResultPayload?.runtime_revision),
      });

      onMutationSuccess?.({
        deliveryId,
        loadingToastContent,
        payload,
      });
      return payload;
    } catch (error) {
      mutationTransport?.fail(error);
      if (!ganttCapture && error instanceof DOMException && error.name === "AbortError") {
        return null;
      }
      if (error?.hasPublishedPlanningMutationFeedback === true) {
        throw error;
      }

      console.error("Erreur pendant la mise à jour du planning manuel.", error);
      const currentState = resolveWorkspaceState(state);
      const errorMessage = error instanceof Error && error.message
        ? error.message
        : "La mise à jour du planning manuel a échoué.";
      const errorFeedback = resolvePlanningMutationErrorFeedback(
        currentState,
        errorMessage,
        loadingToastContent?.errorTitle || "Modification du planning interrompue",
      );
      const resolvedErrorDetailItems = [
        ...statusNoticeErrorDetailItems,
        ...errorFeedback.detailItems,
      ];
      const failedPlanningDiagnostics = isDiagnosticObject(diagnosticPayloadForClientTiming?._planning_diagnostics)
        ? diagnosticPayloadForClientTiming._planning_diagnostics
        : (isDiagnosticObject(diagnosticRecord?.server) ? diagnosticRecord.server : null);
      const failedCaptureContext = createMutationCaptureContext({
        detailItems: resolvedErrorDetailItems,
        failureKind: error?.isPlanningPreflight ? "validation" : Number.isInteger(Number(error?.status)) ? "request" : "network",
        httpStatus: Number.isInteger(Number(error?.status)) ? Number(error.status) : null,
        planningDiagnostics: failedPlanningDiagnostics,
        reasonCode: normalizeInlineText(error?.reasonCode || error?.code),
      });

      if (loadingToastId) {
        updateToast(loadingToastId, {
          captureContext: failedCaptureContext,
          duration: errorFeedback.duration,
          emphasis: errorFeedback.emphasis,
          message: errorFeedback.message,
          title: errorFeedback.title,
          type: "error",
        });
        hasResolvedLoadingToast = true;
      } else {
        showToast({
          captureContext: failedCaptureContext,
          duration: errorFeedback.duration,
          emphasis: errorFeedback.emphasis,
          message: errorFeedback.message,
          title: errorFeedback.title,
          type: "error",
        });
      }

      if (statusNoticeContent) {
        publishStatusNotice({
          captureContext: failedCaptureContext,
          detailItems: resolvedErrorDetailItems,
          eyebrow: statusNoticeContent.eyebrow || "Mise à jour du planning",
          finishedAt: new Date().toISOString(),
          message: errorFeedback.message,
          planningDiagnostics: failedPlanningDiagnostics,
          status: "failed",
          statusLabel: errorFeedback.statusLabel,
          title: errorFeedback.title,
        });
      }

      onMutationError?.({
        detailItems: resolvedErrorDetailItems,
        deliveryId,
        message: errorFeedback.message,
        title: errorFeedback.title,
      });
      if (rethrowOnError) {
        throw new Error(errorFeedback.message);
      }
      return null;
    } finally {
      finishPlanningMutationActivity();
      const currentState = resolveWorkspaceState(state);
      clearPendingPlanningMutation(currentState, { orderId, loadingStepId, deliveryId });
      if (deliveryId) {
        abortControllersByDeliveryIdRef.current.delete(deliveryId);
      }

      if (isMountedRef.current) {
        rerenderWorkspace(workspaceStore, onWorkspaceRender);
      }
      if (loadingToastId && !hasResolvedLoadingToast) {
        dismissToast(loadingToastId);
      }
      if (diagnosticRecord && (diagnosticRecord.enabled || ganttCapture)) {
        diagnosticRecord.mark("final_render");
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            diagnosticRecord.finish();
            finishGanttCapture();
          });
        });
        if (document.visibilityState === "hidden") finishGanttCapture();
      } else finishGanttCapture();
    }
  };

  const resolveErrorSuspendMutationContext = (entry = {}) => ({
    deliveryId: normalizeInlineText(entry?.suspendAnchorDeliveryId || entry?.deliveryId),
    errorId: normalizeInlineText(entry?.errorId),
    orderId: normalizeInlineText(entry?.suspendTargetOrderId || entry?.orderId),
  });

  const runSuspendOrderFromErrorMutation = (entry = {}, {
    groupMetadata = null,
    loadingToastContent = null,
    rethrowOnError = false,
    statusNoticeContent = null,
  } = {}) => {
    const state = workspaceStore.getState();
    const { deliveryId, errorId, orderId } = resolveErrorSuspendMutationContext(entry);
    if (!state || !orderId || !deliveryId || !errorId) {
      return null;
    }

    if (hasPendingPlanningMutation(state, { orderId, deliveryId })) {
      showPendingDeliveryMutationToast();
      return null;
    }

    const deliverySubject = resolveDeliveryNoticeSubject(state, deliveryId) || orderId;
    const suspendedGroupKey = normalizeInlineText(groupMetadata?.groupKey || groupMetadata?.suspendedGroupKey);
    const suspendedGroupLabel = normalizeInlineText(groupMetadata?.groupLabel || groupMetadata?.suspendedGroupLabel);
    const suspendedGroupSourceKey = normalizeInlineText(groupMetadata?.sourceKey || groupMetadata?.suspendedGroupSourceKey);
    const suspendedGroupMemberIds = Array.isArray(groupMetadata?.memberDeliveryIds || groupMetadata?.suspendedGroupMemberIds)
      ? (groupMetadata.memberDeliveryIds || groupMetadata.suspendedGroupMemberIds)
        .map((memberId) => normalizeInlineText(memberId))
        .filter(Boolean)
      : [];
    const suspendedGroupPayload = suspendedGroupKey && suspendedGroupMemberIds.length >= 2
      ? {
        suspended_group_key: suspendedGroupKey,
        suspended_group_label: suspendedGroupLabel || undefined,
        suspended_group_member_ids: suspendedGroupMemberIds,
        suspended_group_source_key: suspendedGroupSourceKey || "non_assignable_errors",
      }
      : {};

    return applyPlanningMutation({
      url: `/api/planning/orders/${encodeURIComponent(orderId)}/suspend-from-error`,
      body: {
        error_id: errorId,
        ...(entry?.errorClassification ? { expected_classification: entry.errorClassification } : {}),
        ...suspendedGroupPayload,
      },
      orderId,
      deliveryId,
      actionName: "suspend_from_error",
      loadingToastContent: loadingToastContent || {
        errorTitle: "Transfert interrompu",
        title: "Transfert en cours",
        message: "Nous retirons la commande de la flotte active et conservons le motif initial dans Suspendues.",
        successTitle: "Commande transférée",
        successMessage: "La commande est maintenant visible dans Suspendues avec son motif initial.",
      },
      statusNoticeContent: statusNoticeContent || {
        eyebrow: "Centre des alertes",
        errorTitle: "Transfert interrompu",
        runningMessage: "Transfert dans Suspendues en cours. Le dernier résultat reste affiché pendant la mise à jour.",
        runningStatusLabel: "Traitement en cours",
        runningTitle: "Transfert de commande en cours",
        successMessage: deliverySubject
          ? `La commande ${deliverySubject} a été transférée dans Suspendues.`
          : "La commande a été transférée dans Suspendues.",
        successStatusLabel: "Prêt",
        successTitle: appendNoticeSubject("Commande transférée", deliverySubject),
      },
      rethrowOnError,
    });
  };

  const createSavedTruckSettingsSynchronization = (truckId, truckName, loadingToastId) => {
    const dispatchDate = getCurrentDispatchDateFromLocation();
    const dispatchPeriod = getCurrentDispatchPeriodFromLocation();
    const scopedUrl = getScopedResultsLatestApiUrl(dispatchPeriod);
    const refreshUrl = getForcedResultsLatestApiUrl(dispatchPeriod);
    let runtimeRevision = "";
    let cancelled = false;
    const isCurrent = () => isMountedRef.current
      && getCurrentDispatchDateFromLocation() === dispatchDate
      && getCurrentDispatchPeriodFromLocation() === dispatchPeriod;

    return {
      isCurrent,
      async beforeSuccessFeedback() {
        if (!isCurrent()) {
          cancelled = true;
          return;
        }
        updateToast(loadingToastId, {
          title: "Paramètres enregistrés — Synchronisation du Dispatch",
          message: `Nous actualisons le Dispatch avant de recalculer ${truckName}.`,
          type: "loading", duration: null,
        });
        const controller = new AbortController();
        const activeRead = { controller, canContinue: isCurrent };
        activePlanningRecoveriesRef.current.add(activeRead);
        try {
          // This read must start after the PATCH, and never reuse a pre-save request.
          const resultPayload = await loadPageData(refreshUrl, { force: true, signal: controller.signal });
          if (!isCurrent() || controller.signal.aborted) {
            cancelled = true;
            return;
          }
          const currentState = resolveWorkspaceState();
          const freshRevision = normalizeInlineText(resultPayload?.runtime_revision);
          if (!currentState || !freshRevision
            || (resultPayload?.dispatch_date || resultPayload?.planning_summary?.dispatch_date) !== dispatchDate
            || resultPayload?.dispatch_period !== dispatchPeriod
            || !findAssignmentByTruckId(resultPayload, truckId)) {
            return;
          }
          // A pending source change is expected; require a coherent view, not a recalculated one.
          writePageDataCacheEntry(scopedUrl, resultPayload);
          const selection = syncResultsAfterPlanningMutation(currentState, resultPayload, { preferredAssignmentId: truckId, actionName: "update_truck_time_settings_refresh" });
          onWorkspaceRender?.({ assignment: selection.nextAssignment, trip: selection.nextTrip });
          if (isCurrent() && resultPayload.portal_publications) {
            onPortalPublicationsChange?.(resultPayload.portal_publications, {
              actionName: "update_truck_time_settings", resultPayload,
            });
          }
          runtimeRevision = freshRevision;
        } catch {
          cancelled = !isCurrent() || controller.signal.aborted;
        } finally {
          activePlanningRecoveriesRef.current.delete(activeRead);
        }
      },
      getRevisionForOptimization() {
        if (cancelled || !isCurrent()) {
          dismissToast(loadingToastId);
          return "";
        }
        if (runtimeRevision) return runtimeRevision;
        const title = "Paramètres enregistrés — Dispatch à actualiser";
        const message = `Les paramètres de ${truckName} sont enregistrés dans Data Manager, mais le Dispatch n'a pas pu être synchronisé. `
          + "Le recalcul n'a pas été lancé. Actualisez le Dispatch avant de réessayer. Le changement reste disponible dans Mise à jour.";
        updateToast(loadingToastId, { title, message, type: "warning", duration: 12000, progress: null });
        publishStatusNotice({ title, message, status: "partial", statusLabel: "À actualiser",
          eyebrow: "Planning manuel", finishedAt: new Date().toISOString() });
        return "";
      },
    };
  };

  const submitTruckRouteOptimization = ({
    existingLoadingToastId = null,
    expectedRuntimeRevision = "",
    requiredCapacityTons = null,
    requiredMaxWorkHours = null,
    requiredStartTime = "",
    rethrowOnError = false,
    truckId = "",
    truckName = "",
  } = {}) => {
    const safeTruckId = normalizeInlineText(truckId);
    const safeTruckName = normalizeInlineText(truckName) || safeTruckId;
    const normalizedRequiredStartTime = normalizeTruckStartTimeValue(requiredStartTime);
    const normalizedRequiredMaxWorkHours = normalizeTruckMaxWorkHoursValue(requiredMaxWorkHours);
    const normalizedRequiredCapacityTons = normalizeTruckCapacityTonsValue(requiredCapacityTons);
    const hasAuthoritativeStartTime = Boolean(normalizedRequiredStartTime);
    const hasAuthoritativeMaxWorkHours = normalizedRequiredMaxWorkHours != null;
    const hasAuthoritativeCapacityTons = normalizedRequiredCapacityTons != null;
    const hasAuthoritativeTimeSettings = (
      hasAuthoritativeStartTime
      || hasAuthoritativeMaxWorkHours
      || hasAuthoritativeCapacityTons
    );
    const formattedRequiredStartTime = formatTruckPreloadTimeLabel(normalizedRequiredStartTime);
    const formattedRequiredMaxWorkHours = formatTruckMaxWorkHoursLabel(normalizedRequiredMaxWorkHours);
    const formattedRequiredCapacityTons = formatTruckCapacityTonsLabel(normalizedRequiredCapacityTons);
    const authoritativeRouteFields = {
      ...(hasAuthoritativeStartTime ? { required_start_time: normalizedRequiredStartTime } : {}),
      ...(hasAuthoritativeMaxWorkHours ? { required_max_work_hours: normalizedRequiredMaxWorkHours } : {}),
      ...(hasAuthoritativeCapacityTons ? { required_capacity_tons: normalizedRequiredCapacityTons } : {}),
    };
    const isOnlyAuthoritativeStartTime = (
      hasAuthoritativeStartTime
      && !hasAuthoritativeMaxWorkHours
      && !hasAuthoritativeCapacityTons
    );
    const authoritativeSettingsParts = [
      ...(hasAuthoritativeStartTime ? [formattedRequiredStartTime] : []),
      ...(hasAuthoritativeMaxWorkHours ? [`${formattedRequiredMaxWorkHours} max`] : []),
      ...(hasAuthoritativeCapacityTons ? [formattedRequiredCapacityTons] : []),
    ];
    const hasMultipleAuthoritativeSettings = authoritativeSettingsParts.length > 1;
    const authoritativeSettingsLabel = hasMultipleAuthoritativeSettings
      ? `les paramètres chauffeur et camion (${authoritativeSettingsParts.join(" · ")})`
      : hasAuthoritativeStartTime
        ? `l'heure ${formattedRequiredStartTime}`
        : hasAuthoritativeMaxWorkHours
          ? `la durée maximale de ${formattedRequiredMaxWorkHours}`
          : `la capacité de ${formattedRequiredCapacityTons}`;
    const authoritativeSettingsSaveVerb = hasMultipleAuthoritativeSettings
      ? "sont enregistrés"
      : "est enregistrée";
    const authoritativeSettingsFailureMessage = hasMultipleAuthoritativeSettings
      ? `Les paramètres chauffeur et camion (${authoritativeSettingsParts.join(" · ")}) sont enregistrés dans Data Manager, mais le Dispatch n'a pas été recalculé.`
      : hasAuthoritativeStartTime
        ? `L'heure ${formattedRequiredStartTime} est enregistrée dans Data Manager, mais le Dispatch n'a pas été recalculé.`
        : hasAuthoritativeMaxWorkHours
          ? `La durée maximale de ${formattedRequiredMaxWorkHours} est enregistrée dans Data Manager, mais le Dispatch n'a pas été recalculé.`
          : `La capacité de ${formattedRequiredCapacityTons} est enregistrée dans Data Manager, mais le Dispatch n'a pas été recalculé.`;
    const protectedOrderCount = hasAuthoritativeTimeSettings
      ? countProtectedAssignmentOrders(resolveWorkspaceState(), safeTruckId)
      : 0;
    const detailItems = [
      { key: "truck-route-optimization-truck", label: "Camion", value: safeTruckName },
      ...(hasAuthoritativeStartTime
        ? [{ key: "truck-route-optimization-start-time", label: "Heure de début", value: formattedRequiredStartTime }]
        : []),
      ...(hasAuthoritativeMaxWorkHours
        ? [{ key: "truck-route-optimization-max-work-hours", label: "Heures max", value: formattedRequiredMaxWorkHours }]
        : []),
      ...(hasAuthoritativeCapacityTons
        ? [{ key: "truck-route-optimization-capacity-tons", label: "Capacité t", value: formattedRequiredCapacityTons }]
        : []),
    ];

    return applyPlanningMutation({
      url: `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/optimize-routes`,
      ...(hasAuthoritativeTimeSettings ? { body: authoritativeRouteFields } : {}),
      ...(hasAuthoritativeTimeSettings ? { preferredAssignmentId: safeTruckId } : {}),
      actionName: "optimize_truck_routes",
      existingLoadingToastId,
      expectedRuntimeRevision,
      rethrowOnError,
      loadingToastContent: {
        progressSteps: TRUCK_OPTIMIZATION_PROGRESS_STEPS,
        errorTitle: hasAuthoritativeTimeSettings
          ? (isOnlyAuthoritativeStartTime
            ? "Heure enregistrée, recalcul interrompu"
            : "Paramètres chauffeur et camion enregistrés, recalcul interrompu")
          : "Optimisation camion interrompue",
        title: hasAuthoritativeTimeSettings
          ? "Enregistrement terminé — Recalcul en cours"
          : "Optimisation camion en cours",
        message: hasAuthoritativeTimeSettings
          ? `${authoritativeSettingsLabel} ${authoritativeSettingsSaveVerb}. Nous réoptimisons maintenant ${safeTruckName} sans déplacer ses commandes.`
          : `Nous relisons Data Manager puis réoptimisons ${safeTruckName} avec le même mode solveur que le dernier Optimiser flotte, sans déplacer de commandes vers un autre camion.`,
        successTitle: hasAuthoritativeTimeSettings
          ? (isOnlyAuthoritativeStartTime ? "Heure appliquée au dispatch" : "Paramètres chauffeur et camion appliqués au dispatch")
          : "Optimisation camion terminée",
        successMessage: ({ payload }) => normalizeInlineText(payload?.message)
          || (hasAuthoritativeTimeSettings
            ? `${safeTruckName} a maintenant ${authoritativeSettingsLabel} dans Data Manager et le Dispatch.`
            : `${safeTruckName} a été réoptimisé localement avec les données actuelles de Data Manager.`),
      },
      statusNoticeContent: {
        eyebrow: "Planning manuel",
        errorDetailItems: detailItems,
        errorTitle: hasAuthoritativeTimeSettings
          ? (isOnlyAuthoritativeStartTime
            ? "Heure enregistrée, recalcul interrompu"
            : "Paramètres chauffeur et camion enregistrés, recalcul interrompu")
          : "Optimisation camion interrompue",
        runningMessage: hasAuthoritativeTimeSettings
          ? `Application de ${authoritativeSettingsLabel} au camion ${safeTruckName}. Les commandes restent sur le camion courant.`
          : `Réoptimisation ciblée en cours pour ${safeTruckName} avec le même mode solveur que le dernier Optimiser flotte. Les commandes restent sur le camion courant.`,
        runningStatusLabel: "Traitement en cours",
        runningTitle: hasAuthoritativeTimeSettings
          ? (isOnlyAuthoritativeStartTime ? "Application de l'heure de début" : "Application des paramètres chauffeur et camion")
          : "Optimisation camion en cours",
        successDetailItems: detailItems,
        successMessage: ({ payload }) => normalizeInlineText(payload?.message)
          || (hasAuthoritativeTimeSettings
            ? `${safeTruckName} a été recalculé avec ${authoritativeSettingsLabel}.`
            : `${safeTruckName} a été réoptimisé localement avec Data Manager et les routes HERE ont été recalculées.`),
        successStatusLabel: "Prêt",
        successTitle: hasAuthoritativeTimeSettings
          ? (isOnlyAuthoritativeStartTime
            ? `Heure de début de ${safeTruckName} appliquée`
            : `Paramètres chauffeur et camion de ${safeTruckName} appliqués`)
          : `Optimisation de ${safeTruckName} terminée`,
      },
      resolveServerErrorMessage({ errorMessage, payload }) {
        if (hasAuthoritativeTimeSettings) {
          const reasonCode = normalizeInlineText(payload?.reason_code || payload?.code).toLowerCase();
          const runtimeConflictDetail = reasonCode === "runtime_revision_conflict"
            ? "Le planning a changé pendant l'opération. Ce recalcul a été interrompu pour préserver la version courante."
            : reasonCode === "planning_mutation_in_progress"
              ? "Une autre modification du planning est en cours. Réessayez lorsqu'elle sera terminée."
              : "";
          const durationConflictDetail = buildTruckTimeSettingsDurationConflictDetail(payload, {
            protectedOrderCount,
          });
          const capacityConflictDetail = buildTruckTimeSettingsCapacityConflictDetail(payload, {
            protectedOrderCount,
          });
          const timeWindowConflictDetail = buildTruckTimeSettingsTimeWindowConflictDetail(payload);
          return (
            `${authoritativeSettingsFailureMessage} `
            + (runtimeConflictDetail ? `${runtimeConflictDetail} ` : "")
            + (durationConflictDetail ? `${durationConflictDetail} ` : "")
            + (capacityConflictDetail ? `${capacityConflictDetail} ` : "")
            + (timeWindowConflictDetail ? `${timeWindowConflictDetail} ` : "")
            + "Le changement reste disponible dans Mise à jour."
          );
        }
        return normalizeInlineText(errorMessage)
          || `Impossible de recalculer ${safeTruckName} avec les données actuelles de Data Manager.`;
      },
    });
  };

  return {
    cancelDeliveryMutation(deliveryId) {
      if (!deliveryId) {
        return false;
      }

      const controller = abortControllersByDeliveryIdRef.current.get(deliveryId);
      if (!controller) {
        return false;
      }

      controller.abort();
      abortControllersByDeliveryIdRef.current.delete(deliveryId);
      return true;
    },
    confirmationDialog: confirmationDialog ? { ...confirmationDialog, planningWriteGuard: true } : null,
    dismissConfirmation,
    isTruckMutationPending,
    reassignmentRebuildMode: resolvedReassignmentRebuildMode,
    publishDispatchDayPortal({
      dispatchDate = "",
      dispatchPeriod = "day",
      routes = [],
    } = {}) {
      const safeDispatchDate = normalizeInlineText(dispatchDate) || getCurrentDispatchDateFromLocation();
      const mutationUrl = buildPortalPublicationMutationUrl(safeDispatchDate, "publish");
      const mutationBody = buildPortalPublicationMutationBody("publish", routes, dispatchPeriod);
      if (!mutationUrl || mutationBody.routes.length === 0) {
        return;
      }
      const publicationSelectionSummary = buildPortalPublicationSelectionSummary(routes);

      const state = workspaceStore.getState();
      const dispatchDayRecord = resolveDispatchDayRecordFromState(state, safeDispatchDate) || { dispatch_date: safeDispatchDate };
      const content = buildDispatchDayTransitionConfirmationContent({
        ...dispatchDayRecord,
        portal_driver_count: publicationSelectionSummary.driverCount,
        portal_driver_name: publicationSelectionSummary.driverName,
        portal_order_count: publicationSelectionSummary.orderCount,
        portal_route_count: mutationBody.routes.length,
        portal_truck_name: publicationSelectionSummary.truckName,
      }, "publish_portal");
      const preferredAssignmentId = mutationBody.routes.length === 1 ? mutationBody.routes[0].truck_id : null;

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          applyPlanningMutation({
            url: mutationUrl,
            body: mutationBody,
            preferredAssignmentId,
            actionName: "publish_driver_portal_routes",
            loadingToastContent: {
              errorTitle: "Publication interrompue",
              title: "Publication portail en cours",
              message: mutationBody.routes.length === 1
                ? "Nous publions la tournée sélectionnée dans le portail chauffeur."
                : `Nous publions ${mutationBody.routes.length} tournées dans le portail chauffeur.`,
              successTitle: "Portail chauffeur mis à jour",
              successMessage: ({ payload }) => normalizeInlineText(payload?.message)
                || (mutationBody.routes.length === 1
                  ? "La tournée est maintenant visible dans le portail chauffeur."
                  : `${mutationBody.routes.length} tournées sont maintenant visibles dans le portail chauffeur.`),
            },
            onMutationError() {
              void onPortalPublicationsRefresh?.();
            },
            resolveServerErrorMessage({ errorMessage }) {
              return normalizeInlineText(errorMessage) || "Impossible de publier les tournées dans le portail chauffeur.";
            },
          });
        },
      });
    },
    unpublishDispatchDayPortal({
      dispatchDate = "",
      dispatchPeriod = "day",
      routes = [],
    } = {}) {
      const safeDispatchDate = normalizeInlineText(dispatchDate) || getCurrentDispatchDateFromLocation();
      const mutationUrl = buildPortalPublicationMutationUrl(safeDispatchDate, "unpublish");
      const mutationBody = buildPortalPublicationMutationBody("unpublish", routes, dispatchPeriod);
      if (!mutationUrl || mutationBody.routes.length === 0) {
        return;
      }
      const publicationSelectionSummary = buildPortalPublicationSelectionSummary(routes);

      const state = workspaceStore.getState();
      const dispatchDayRecord = resolveDispatchDayRecordFromState(state, safeDispatchDate) || { dispatch_date: safeDispatchDate };
      const content = buildDispatchDayTransitionConfirmationContent({
        ...dispatchDayRecord,
        portal_driver_count: publicationSelectionSummary.driverCount,
        portal_driver_name: publicationSelectionSummary.driverName,
        portal_order_count: publicationSelectionSummary.orderCount,
        portal_route_count: mutationBody.routes.length,
        portal_truck_name: publicationSelectionSummary.truckName,
      }, "unpublish_portal");
      const preferredAssignmentId = mutationBody.routes.length === 1 ? mutationBody.routes[0].truck_id : null;

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          applyPlanningMutation({
            url: mutationUrl,
            body: mutationBody,
            preferredAssignmentId,
            actionName: "unpublish_driver_portal_routes",
            loadingToastContent: {
              errorTitle: "Retrait interrompu",
              title: "Retrait du portail en cours",
              message: mutationBody.routes.length === 1
                ? "Nous retirons la tournée sélectionnée du portail chauffeur."
                : `Nous retirons ${mutationBody.routes.length} tournées du portail chauffeur.`,
              successTitle: "Publication retirée",
              successMessage: ({ payload }) => normalizeInlineText(payload?.message)
                || (mutationBody.routes.length === 1
                  ? "La tournée n'est plus visible dans le portail chauffeur."
                  : `${mutationBody.routes.length} tournées ne sont plus visibles dans le portail chauffeur.`),
            },
            onMutationError() {
              void onPortalPublicationsRefresh?.();
            },
            resolveServerErrorMessage({ errorMessage }) {
              return normalizeInlineText(errorMessage) || "Impossible de retirer les tournées du portail chauffeur.";
            },
          });
        },
      });
    },
    requestDeliveryMove({
      allowReturnToOptimized: explicitAllowReturnToOptimized = false,
      deliveryId,
      dispatchPeriod = "day",
      expectedRuntimeRevision = "",
      forcedPreloadedPreviousDayOrderIds = [],
      isUndo = false,
      orderIds = [],
      rebuildMode = "",
      onMutationError = null,
      onMutationStart = null,
      onMutationSuccess = null,
      skipConfirmation = false,
      targetCompatibility = null,
      targetTruckId,
      targetTruckName,
    }) {
      const state = workspaceStore.getState();
      if (!state || !deliveryId || !targetTruckId || isDeliveryMutationPending(deliveryId)) {
        return;
      }
      const capturedRebuildMode = normalizeDispatchReassignmentMode(
        rebuildMode || resolvedReassignmentRebuildMode,
      );
      const capturedProtectAfterReassignment = resolvedProtectAfterReassignment;

      const content = buildDeliveryMoveConfirmationContent(targetTruckName, { protect: capturedProtectAfterReassignment !== false });
      const overtimeWarningContext = resolveTargetedAssignmentOvertimeWarningContext(
        state,
        targetTruckId,
        targetCompatibility,
      );
      const timeWindowRequirement = resolveTimeWindowRequirementState(targetCompatibility);
      const dompeurOverrideRequirement = resolveDompeurOverrideRequirementState(targetCompatibility);
      const checkboxFields = buildPlanningMutationCheckboxFields({
        dompeurOverrideRequirement,
        overtimeWarningContext,
      });
      const timeWindowContent = timeWindowRequirement?.mode === "soft"
        ? buildTimeWindowConfirmationContent(state, {
          confirmLabel: content.confirmLabel,
          deliveryIds: [deliveryId],
          targetCompatibility,
          targetTruckName,
          title: `Réaffecter la commande vers ${targetTruckName || "ce camion"} ?`,
        })
        : null;
      const textPrompt = buildPlanningMutationTextPrompt(timeWindowRequirement);
      const summaryItems = [
        ...(dompeurOverrideRequirement ? [{
          label: "Dompeur",
          tone: "warning",
          value: "Override manuel",
        }] : []),
        ...(timeWindowRequirement?.mode === "override_required" ? [{
          label: "Fenêtre",
          tone: "warning",
          value: "Motif requis",
        }] : []),
      ];
      const onlyOvertimeCheckbox = checkboxFields.length === 1 && checkboxFields[0]?.key === OVERTIME_CONFIRM_FIELD_KEY && !textPrompt;
      const shouldAutoApproveOvertime = !timeWindowContent && shouldAutoApproveFastOvertime({
        onlyOvertimeCheckbox,
        overtimeWarningContext,
        rebuildMode: capturedRebuildMode,
      });
      const dompeurIsConfirmCheckbox = checkboxFields[0]?.key === DOMPEUR_CONFIRM_FIELD_KEY;
      const onlyDompeurCheckbox = checkboxFields.length === 1 && dompeurIsConfirmCheckbox && !textPrompt;
      const dialogWarningMessage = [
        dompeurOverrideRequirement?.description,
        overtimeWarningContext?.shouldShow
          ? (overtimeWarningContext.feedbackMessage || "Cette réaffectation pourrait dépasser le nombre d'heures maximal.")
          : "",
      ].filter(Boolean).join(" ") || content.warningMessage;

      const commitDeliveryMove = (dialogValues = false) => {
        const currentState = workspaceStore.getState();
        if (!currentState || !deliveryId || !targetTruckId) {
          dismissConfirmation();
          return;
        }

        if (isDeliveryMutationPending(deliveryId)) {
          dismissConfirmation();
          showPendingDeliveryMutationToast();
          return;
        }

        const targetTruckLabel = targetTruckName || "le camion sélectionné";
        const resolvedForcedPreloadedPreviousDayOrderIds = (Array.isArray(forcedPreloadedPreviousDayOrderIds)
          ? forcedPreloadedPreviousDayOrderIds
          : []
        )
          .map((orderId) => normalizeInlineText(orderId))
          .filter(Boolean)
          .filter((orderId, index, orderIds) => orderIds.indexOf(orderId) === index);
        const isForcedPreloadMove = resolvedForcedPreloadedPreviousDayOrderIds.length > 0;
        const allowReturnToOptimized = explicitAllowReturnToOptimized || shouldAllowReturnToOptimizedDelivery(currentState, {
          deliveryId,
          targetTruckId,
        });
        const shouldAllowOvertime = Boolean(
          overtimeWarningContext?.shouldShow
          && (
            shouldAutoApproveOvertime
            || readPlanningMutationCheckboxValue(dialogValues, OVERTIME_CONFIRM_FIELD_KEY, {
              usePrimaryFallback: onlyOvertimeCheckbox,
            })
          ),
        );
        // « … quand même » vaut accord pour l'écart P2/P3.
        const confirmTimeWindowOverride = timeWindowRequirement?.mode === "soft";
        const confirmDompeurOverride = Boolean(
          dompeurOverrideRequirement
          && readPlanningMutationCheckboxValue(dialogValues, DOMPEUR_CONFIRM_FIELD_KEY, {
            usePrimaryFallback: onlyDompeurCheckbox || dompeurIsConfirmCheckbox,
          }),
        );
        const timeWindowOverrideReason = timeWindowRequirement?.mode === "override_required"
          ? readPlanningMutationTextPromptValue(dialogValues)
          : "";
        const deliverySubject = resolveDeliveryNoticeSubject(currentState, deliveryId);
        const sourceTruckLabel = resolveDeliverySourceTruckLabel(currentState, deliveryId);
        const requestedDompeurDetailItems = buildRequestedDompeurOverrideDetailItems({
          confirmDompeurOverride,
          targetCompatibility,
        });
        const requestedTimeWindowDetailItems = buildRequestedTimeWindowDetailItems({
          targetCompatibility,
          timeWindowOverrideReason,
        });
        const successTransferMessage = deliverySubject
          ? isForcedPreloadMove
            ? `La commande ${deliverySubject} a été assignée en Preload sur ${targetTruckLabel}.`
            : sourceTruckLabel
              ? `La commande ${deliverySubject} a été réaffectée de ${sourceTruckLabel} vers ${targetTruckLabel}.`
              : `La commande ${deliverySubject} a été réaffectée vers ${targetTruckLabel}.`
          : isForcedPreloadMove
            ? `La commande a été assignée en Preload sur ${targetTruckLabel}.`
          : sourceTruckLabel
            ? `La commande a été réaffectée de ${sourceTruckLabel} vers ${targetTruckLabel}.`
            : `La commande a été réaffectée vers ${targetTruckLabel}.`;
        const errorDetailItems = [
          ...buildDeliveryMoveNoticeDetailItems(currentState, {
            deliveryId,
            targetTruckId,
            targetTruckName,
          }),
          ...requestedDompeurDetailItems,
          ...requestedTimeWindowDetailItems,
        ];
        const loadingToastContent = {
          errorTitle: isForcedPreloadMove ? "Preload interrompu" : "Réaffectation interrompue",
          title: isForcedPreloadMove ? "Preload en cours" : "Réaffectation en cours",
          message: isForcedPreloadMove
            ? "Nous assignons la commande au camion choisi et reconstruisons son Preload."
            : "Transfert de la commande uniquement et recalcul local des tournées concernées.",
          successTitle: isForcedPreloadMove ? "Preload assigné" : "Réaffectation terminée",
          successMessage: isForcedPreloadMove
            ? successTransferMessage
            : capturedProtectAfterReassignment
              ? `${successTransferMessage} Vous pouvez encore la déplacer vers un autre camion compatible ou la libérer pour la rendre réoptimisable.`
              : `${successTransferMessage} Elle reste réoptimisable et pourra être déplacée par une optimisation ultérieure.`,
        };

        const moveBody = buildDeliveryMoveRequestBody({
          allowOvertime: shouldAllowOvertime,
          allowReturnToOptimized,
          confirmDompeurOverride,
          confirmTimeWindowOverride,
          forcedPreloadedPreviousDayOrderIds: resolvedForcedPreloadedPreviousDayOrderIds,
          protectAfterReassignment: capturedProtectAfterReassignment,
          rebuildMode: capturedRebuildMode,
          targetTruckId,
          timeWindowOverrideReason,
        });
        const submitDeliveryMove = ({
          requestBody,
          requestExpectedRuntimeRevision,
          onMutationChallenge = null,
        }) => applyPlanningMutation({
          url: `/api/planning/deliveries/${encodeURIComponent(deliveryId)}/move`,
          body: requestBody,
          deliveryId,
          dispatchPeriod,
          expectedRuntimeRevision: requestExpectedRuntimeRevision,
          highlightedStepKeys: buildDeliveryMoveHighlightedStepKeys({ deliveryId, orderIds }),
          actionName: "move_delivery",
          isUndo,
          loadingToastContent,
          onMutationChallenge,
          onMutationError,
          onMutationStart,
          onMutationSuccess,
          resolveServerErrorMessage({ errorMessage }) {
            return resolveDeliveryMoveRejectedMessage(errorMessage, { targetCompatibility });
          },
          statusNoticeContent: {
            eyebrow: "Planning manuel",
            errorDetailItems,
            errorTitle: isForcedPreloadMove ? "Preload interrompu" : "Réaffectation interrompue",
            runningMessage: "Réaffectation locale en cours. Aucune autre commande ne sera déplacée silencieusement.",
            runningStatusLabel: "Traitement en cours",
            runningTitle: isForcedPreloadMove ? "Preload en cours" : "Réaffectation de commande en cours",
            successDetailItems: [
              ...requestedDompeurDetailItems,
              ...requestedTimeWindowDetailItems,
            ],
            successMessage: successTransferMessage,
            successStatusLabel: "Prêt",
            successTitle: appendNoticeSubject(isForcedPreloadMove ? "Preload assigné" : "Réaffectation terminée", deliverySubject),
          },
        });
        // Partage de compartiment puis multi-visite : chaque relance garde les deux gestionnaires.
        let handleDeliveryMoveChallenge = null;
        const retryDeliveryMove = ({ requestBody, requestExpectedRuntimeRevision }) => {
          void submitDeliveryMove({
            requestBody,
            requestExpectedRuntimeRevision,
            onMutationChallenge: handleDeliveryMoveChallenge,
          });
        };
        const handleCompartmentSharingChallenge = createCompartmentSharingChallengeHandler({
          dismissConfirmation,
          retryMutation: retryDeliveryMove,
          setConfirmationDialog,
          targetTruckLabel,
        });
        const handleDeliveryClientRepeatChallenge = createDeliveryClientRepeatMutationChallengeHandler({
          dismissConfirmation,
          expectedDeliveryIds: [deliveryId],
          retryMutation: retryDeliveryMove,
          setConfirmationDialog,
          targetTruckId,
          targetTruckLabel,
        });
        handleDeliveryMoveChallenge = (challengeContext) => handleCompartmentSharingChallenge(challengeContext)
          || handleDeliveryClientRepeatChallenge(challengeContext);

        dismissConfirmation();
        void submitDeliveryMove({
          requestBody: moveBody,
          requestExpectedRuntimeRevision: expectedRuntimeRevision,
          onMutationChallenge: handleDeliveryMoveChallenge,
        });
      };

      const canAutoCommitSkippedConfirmation = shouldAutoApproveOvertime || (
        skipConfirmation === true
        && checkboxFields.length === 0
        && !textPrompt
        && !timeWindowContent
      );

      if (canAutoCommitSkippedConfirmation) {
        commitDeliveryMove();
        return;
      }

      setConfirmationDialog({
        ...content,
        checkboxFields: checkboxFields.slice(1),
        confirmCheckbox: checkboxFields[0] || null,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: commitDeliveryMove,
        summaryItems,
        textPrompt,
        warningMessage: dialogWarningMessage,
        ...timeWindowContent,
      });
    },
    requestDeliveryGroupMove({
      allowReturnToOptimized: explicitAllowReturnToOptimized = false,
      deliveryIds = [],
      dispatchPeriod = "day",
      expectedRuntimeRevision = "",
      forcedPreloadedPreviousDayOrderIds = [],
      groupStepIds = [],
      isUndo = false,
      orderIds = [],
      rebuildMode = "",
      confirmDompeurOverride: explicitConfirmDompeurOverride = false,
      onMutationError = null,
      onMutationStart = null,
      onMutationSuccess = null,
      skipConfirmation = false,
      sourceGroupKey = "",
      sourceTruckId = "",
      targetCompatibility = null,
      targetTruckId,
      targetTruckName,
    }) {
      const state = workspaceStore.getState();
      const resolvedDeliveryIds = (Array.isArray(deliveryIds) ? deliveryIds : [])
        .filter((deliveryId) => typeof deliveryId === "string" && deliveryId.trim())
        .map((deliveryId) => deliveryId.trim());
      const resolvedForcedPreloadedPreviousDayOrderIds = (Array.isArray(forcedPreloadedPreviousDayOrderIds)
        ? forcedPreloadedPreviousDayOrderIds
        : []
      )
        .map((orderId) => normalizeInlineText(orderId))
        .filter(Boolean)
        .filter((orderId, index, orderIds) => orderIds.indexOf(orderId) === index);
      const resolvedOrderIds = (Array.isArray(orderIds) ? orderIds : [])
        .map((orderId) => normalizeInlineText(orderId))
        .filter(Boolean)
        .filter((orderId, index, allOrderIds) => allOrderIds.indexOf(orderId) === index);
      const isForcedPreloadMove = resolvedForcedPreloadedPreviousDayOrderIds.length > 0;
      const primaryDeliveryId = resolvedDeliveryIds[0] || "";
      if (!state || resolvedDeliveryIds.length === 0 || (resolvedDeliveryIds.length < 2 && !isForcedPreloadMove) || !targetTruckId || resolvedDeliveryIds.some((deliveryId) => isDeliveryMutationPending(deliveryId))) {
        return;
      }
      const capturedRebuildMode = normalizeDispatchReassignmentMode(
        rebuildMode || resolvedReassignmentRebuildMode,
      );
      const capturedProtectAfterReassignment = resolvedProtectAfterReassignment;

      const content = buildDeliveryMoveConfirmationContent(targetTruckName, { scope: "group", protect: capturedProtectAfterReassignment !== false });
      const overtimeWarningContext = resolveTargetedAssignmentOvertimeWarningContext(
        state,
        targetTruckId,
        targetCompatibility,
      );
      const timeWindowRequirement = resolveTimeWindowRequirementState(targetCompatibility);
      const dompeurOverrideRequirement = resolveDompeurOverrideRequirementState(targetCompatibility);
      const checkboxFields = buildPlanningMutationCheckboxFields({
        dompeurOverrideRequirement,
        overtimeWarningContext,
      });
      const timeWindowContent = timeWindowRequirement?.mode === "soft"
        ? buildTimeWindowConfirmationContent(state, {
          confirmLabel: content.confirmLabel,
          deliveryIds: resolvedDeliveryIds,
          targetCompatibility,
          targetTruckName,
          title: `Réaffecter le groupe vers ${targetTruckName || "ce camion"} ?`,
        })
        : null;
      const textPrompt = buildPlanningMutationTextPrompt(timeWindowRequirement);
      const summaryItems = [
        ...(timeWindowContent ? [] : [{
          label: "Commandes",
          value: String(resolvedDeliveryIds.length),
        }]),
        ...(timeWindowRequirement?.mode === "override_required"
          ? [{
            label: "Fenêtre",
            tone: "warning",
            value: "Motif requis",
          }]
          : []),
        ...(dompeurOverrideRequirement
          ? [{
            label: "Dompeur",
            tone: "warning",
            value: "Override manuel",
          }]
          : []),
      ];
      const onlyOvertimeCheckbox = checkboxFields.length === 1 && checkboxFields[0]?.key === OVERTIME_CONFIRM_FIELD_KEY && !textPrompt;
      const shouldAutoApproveOvertime = !timeWindowContent && shouldAutoApproveFastOvertime({
        onlyOvertimeCheckbox,
        overtimeWarningContext,
        rebuildMode: capturedRebuildMode,
      });
      const dompeurIsConfirmCheckbox = checkboxFields[0]?.key === DOMPEUR_CONFIRM_FIELD_KEY;
      const onlyDompeurCheckbox = checkboxFields.length === 1 && dompeurIsConfirmCheckbox && !textPrompt;
      const dialogWarningMessage = [
        dompeurOverrideRequirement?.description,
        overtimeWarningContext?.shouldShow
          ? (overtimeWarningContext.feedbackMessage || "Cette réaffectation pourrait dépasser le nombre d'heures maximal.")
          : "",
      ].filter(Boolean).join(" ") || content.warningMessage;

      const commitDeliveryGroupMove = (dialogValues = false) => {
        const currentState = workspaceStore.getState();
        if (!currentState || resolvedDeliveryIds.length === 0 || (resolvedDeliveryIds.length < 2 && !isForcedPreloadMove) || !targetTruckId) {
          dismissConfirmation();
          return;
        }

        if (resolvedDeliveryIds.some((deliveryId) => isDeliveryMutationPending(deliveryId))) {
          dismissConfirmation();
          showPendingDeliveryMutationToast();
          return;
        }

        const targetTruckLabel = targetTruckName || "le camion sélectionné";
        const allowReturnToOptimized = explicitAllowReturnToOptimized || resolvedDeliveryIds.every((deliveryId) => shouldAllowReturnToOptimizedDelivery(currentState, {
          deliveryId,
          targetTruckId,
        }));
        const shouldAllowOvertime = Boolean(
          overtimeWarningContext?.shouldShow
          && (
            shouldAutoApproveOvertime
            || readPlanningMutationCheckboxValue(dialogValues, OVERTIME_CONFIRM_FIELD_KEY, {
              usePrimaryFallback: onlyOvertimeCheckbox,
            })
          ),
        );
        // « … quand même » vaut accord pour l'écart P2/P3.
        const confirmTimeWindowOverride = timeWindowRequirement?.mode === "soft";
        const confirmDompeurOverride = Boolean(
          explicitConfirmDompeurOverride === true
          || (dompeurOverrideRequirement
          && readPlanningMutationCheckboxValue(dialogValues, DOMPEUR_CONFIRM_FIELD_KEY, {
            usePrimaryFallback: onlyDompeurCheckbox || dompeurIsConfirmCheckbox,
          })),
        );
        const timeWindowOverrideReason = timeWindowRequirement?.mode === "override_required"
          ? readPlanningMutationTextPromptValue(dialogValues)
          : "";
        const moveSubjectLabel = resolvedDeliveryIds.length > 1
          ? `Le groupe de ${resolvedDeliveryIds.length} commandes`
          : "La commande";
        const successTransferMessage = isForcedPreloadMove
          ? `${moveSubjectLabel} a été ${resolvedDeliveryIds.length > 1 ? "assigné" : "assignée"} en Preload sur ${targetTruckLabel}.`
          : `${moveSubjectLabel} a été réaffecté vers ${targetTruckLabel}.`;
        const errorDetailItems = [
          ...resolvedDeliveryIds.flatMap((deliveryId) => buildDeliveryMoveNoticeDetailItems(currentState, {
            deliveryId,
            targetTruckId,
            targetTruckName,
          })),
          ...buildRequestedDompeurOverrideDetailItems({
            confirmDompeurOverride,
            targetCompatibility,
          }),
          ...buildRequestedTimeWindowDetailItems({
            targetCompatibility,
            timeWindowOverrideReason,
          }),
        ];
        const loadingToastContent = {
          errorTitle: isForcedPreloadMove ? "Preload interrompu" : "Réaffectation interrompue",
          title: isForcedPreloadMove ? "Preload en cours" : "Réaffectation en cours",
          message: isForcedPreloadMove
            ? "Nous assignons la sélection au camion choisi et reconstruisons son Preload."
            : "Transfert du groupe sélectionné et recalcul local des tournées concernées.",
          successTitle: isForcedPreloadMove ? "Preload assigné" : "Réaffectation terminée",
          successMessage: successTransferMessage,
        };

        const groupMoveBody = buildDeliveryGroupMoveRequestBody({
          allowOvertime: shouldAllowOvertime,
          allowReturnToOptimized,
          confirmTimeWindowOverride,
          confirmDompeurOverride,
          deliveryIds: resolvedDeliveryIds,
          forcedPreloadedPreviousDayOrderIds: resolvedForcedPreloadedPreviousDayOrderIds,
          groupStepIds,
          protectAfterReassignment: capturedProtectAfterReassignment,
          rebuildMode: capturedRebuildMode,
          sourceGroupKey,
          sourceTruckId,
          targetTruckId,
          timeWindowOverrideReason,
        });
        if (!groupMoveBody) {
          dismissConfirmation();
          return;
        }

        const submitDeliveryGroupMove = ({
          requestBody,
          requestExpectedRuntimeRevision,
          onMutationChallenge = handleDeliveryGroupMutationChallenge,
        }) => applyPlanningMutation({
          url: "/api/planning/delivery-groups/move",
          body: requestBody,
          deliveryId: primaryDeliveryId,
          dispatchPeriod,
          expectedRuntimeRevision: requestExpectedRuntimeRevision,
          highlightedStepKeys: [
            ...resolvedDeliveryIds.map((resolvedDeliveryId) => getDeliveryHighlightKey(resolvedDeliveryId)),
            ...resolvedOrderIds.map((orderId) => getLoadingOrderHighlightKey(orderId)),
          ],
          actionName: "move_delivery_group",
          isUndo,
          loadingToastContent,
          onMutationChallenge,
          onMutationError,
          onMutationStart,
          onMutationSuccess,
          resolveServerErrorMessage({ errorMessage }) {
            return resolveDeliveryMoveRejectedMessage(errorMessage, { targetCompatibility });
          },
          statusNoticeContent: {
            eyebrow: "Planning manuel",
            errorDetailItems,
            errorTitle: isForcedPreloadMove ? "Preload interrompu" : "Réaffectation interrompue",
            runningMessage: isForcedPreloadMove
              ? (resolvedDeliveryIds.length > 1 ? "Preload du groupe en cours…" : "Preload de la commande en cours…")
              : "Réaffectation groupée locale en cours. Aucune autre commande ne sera déplacée silencieusement.",
            runningStatusLabel: "Traitement en cours",
            runningTitle: isForcedPreloadMove ? "Preload en cours" : "Réaffectation de groupe en cours",
            successDetailItems: [
              ...buildRequestedDompeurOverrideDetailItems({
                confirmDompeurOverride,
                targetCompatibility,
              }),
              ...buildRequestedTimeWindowDetailItems({
                targetCompatibility,
                timeWindowOverrideReason,
              }),
            ],
            successMessage: successTransferMessage,
            successStatusLabel: "Prêt",
            successTitle: isForcedPreloadMove ? "Preload assigné" : "Réaffectation de groupe terminée",
          },
        });

        const handleDeliveryClientRepeatChallenge = createDeliveryClientRepeatMutationChallengeHandler({
          dismissConfirmation,
          expectedDeliveryIds: resolvedDeliveryIds,
          retryMutation: ({ requestBody, requestExpectedRuntimeRevision }) => {
            void submitDeliveryGroupMove({
              requestBody,
              requestExpectedRuntimeRevision,
            });
          },
          setConfirmationDialog,
          targetTruckId,
          targetTruckLabel,
        });

        const handleCapacitySplitChallenge = createDeliveryGroupCapacitySplitChallengeHandler({
          dismissConfirmation,
          expectedDeliveryIds: resolvedDeliveryIds,
          retryMutation: (retry) => { void submitDeliveryGroupMove(retry); },
          setConfirmationDialog,
          targetTruckId,
          targetTruckLabel,
        });

        const handleClientCohesionChallenge = ({ payload, requestBody, response }) => {
          const parsedChallenge = resolveClientCohesionJoinConfirmationChallenge({
            payload,
            status: response?.status,
          });
          const challenge = parsedChallenge
            ? {
                ...parsedChallenge,
                sourceTruckLabels: resolveClientCohesionSourceTruckLabels(currentState, parsedChallenge),
                targetTruckLabel,
              }
            : null;
          const confirmationContent = buildClientCohesionJoinConfirmationContent(challenge);
          const keepSplitBody = buildClientCohesionDecisionRequestBody({
            challenge,
            decision: "keep_split",
            initialBody: requestBody,
          });
          const joinBody = challenge?.canJoin
            ? buildClientCohesionDecisionRequestBody({
                challenge,
                decision: "join",
                initialBody: requestBody,
              })
            : null;
          if (!challenge || !confirmationContent || !keepSplitBody || (challenge.canJoin && !joinBody)) {
            return false;
          }

          const retryClientCohesionDecision = (decision) => {
            const retryBody = decision === "join" ? joinBody : keepSplitBody;
            dismissConfirmation();
            if (!retryBody) {
              return;
            }
            void submitDeliveryGroupMove({
              requestBody: retryBody,
              requestExpectedRuntimeRevision: challenge.runtimeRevision,
              onMutationChallenge: handleDeliveryGroupMutationChallenge,
            });
          };

          setConfirmationDialog({
            ...confirmationContent,
            isOpen: true,
            onCancel: challenge.canJoin
              ? () => retryClientCohesionDecision("keep_split")
              : dismissConfirmation,
            onClose: dismissConfirmation,
            onConfirm: () => retryClientCohesionDecision(challenge.canJoin ? "join" : "keep_split"),
            onDismiss: dismissConfirmation,
          });
          return true;
        };
        const handleCompartmentSharingChallenge = createCompartmentSharingChallengeHandler({
          dismissConfirmation,
          retryMutation: (retry) => {
            void submitDeliveryGroupMove({ ...retry, onMutationChallenge: handleDeliveryGroupMutationChallenge });
          },
          setConfirmationDialog,
          targetTruckLabel,
        });
        const handleDeliveryGroupMutationChallenge = (challengeContext) => {
          if (handleCompartmentSharingChallenge(challengeContext)) {
            return true;
          }
          if (handleClientCohesionChallenge(challengeContext)) {
            return true;
          }
          if (handleCapacitySplitChallenge(challengeContext)) {
            return true;
          }
          return handleDeliveryClientRepeatChallenge(challengeContext);
        };

        dismissConfirmation();
        void submitDeliveryGroupMove({
          requestBody: groupMoveBody,
          requestExpectedRuntimeRevision: expectedRuntimeRevision,
          onMutationChallenge: handleDeliveryGroupMutationChallenge,
        });
      };

      if (shouldAutoApproveOvertime || (skipConfirmation === true && checkboxFields.length === 0 && !textPrompt && !timeWindowContent)) {
        commitDeliveryGroupMove();
        return;
      }

      setConfirmationDialog({
        ...content,
        title: "Confirmer la réaffectation du groupe ?",
        description: `Ce groupe regroupe ${resolvedDeliveryIds.length} commande${resolvedDeliveryIds.length > 1 ? "s" : ""}.`,
        checkboxFields: checkboxFields.slice(1),
        confirmCheckbox: checkboxFields[0] || null,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: commitDeliveryGroupMove,
        summaryItems,
        textPrompt,
        warningMessage: dialogWarningMessage,
        ...timeWindowContent,
      });
    },
    requestPreloadedPreviousDayAssignment({
      dispatchPeriod = "day",
      groupStepIds = [],
      orderIds = [],
      scope = "order",
      sourceGroupKey = "",
      sourceTruckId = "",
    } = {}) {
      const state = workspaceStore.getState();
      if (!state) {
        return;
      }

      const body = buildPreloadedPreviousDayRequestBody({
        groupStepIds,
        orderIds,
        rebuildMode: resolvedReassignmentRebuildMode,
        scope,
        sourceGroupKey,
        sourceTruckId,
      });
      if (!body) {
        return;
      }

      const isGroupScope = body.scope === "group";
      const orderCount = Array.isArray(body.order_ids) ? body.order_ids.length : 0;
      const subjectLabel = isGroupScope
        ? `Le groupe de ${orderCount} commande${orderCount > 1 ? "s" : ""}`
        : "La commande";
      const defaultSuccessMessage = `${subjectLabel} est maintenant ${isGroupScope ? "marqué" : "marquée"} Chargé la veille.`;
      // Le serveur dit quand le reste du chargement a été placé juste après le Preload.
      const successMessage = ({ payload: responsePayload } = {}) => (
        normalizeInlineText(responsePayload?.preloaded_previous_day_notice) || defaultSuccessMessage
      );

      // Compléter un compartiment d'une commande chargée la veille : fenêtre, puis relance avec la réponse.
      const submitPreloadedPreviousDay = ({ requestBody, requestExpectedRuntimeRevision = "" }) => applyPlanningMutation({
        url: PRELOADED_PREVIOUS_DAY_API_URL,
        body: requestBody,
        expectedRuntimeRevision: requestExpectedRuntimeRevision,
        dispatchPeriod,
        orderId: body.order_ids[0] || null,
        preferredAssignmentId: body.source_truck_id,
        actionName: "preloaded_previous_day_assignment",
        onMutationChallenge: createCompartmentSharingChallengeHandler({
          dismissConfirmation,
          retryMutation: submitPreloadedPreviousDay,
          setConfirmationDialog,
        }),
        loadingToastContent: {
          errorTitle: "Preload interrompu",
          title: "Preload en cours",
          message: "Nous activons Chargé la veille et reconstruisons uniquement le camion courant.",
          successTitle: "Preload activé",
          successMessage,
        },
        resolveServerErrorMessage({ errorMessage }) {
          return normalizeInlineText(errorMessage) || "Impossible d'activer Chargé la veille depuis le Gantt.";
        },
        statusNoticeContent: {
          eyebrow: "Planning manuel",
          errorTitle: "Preload interrompu",
          runningMessage: "Activation Chargé la veille en cours. Le camion courant est recalculé localement sans revalidation du dispatch.",
          runningStatusLabel: "Traitement en cours",
          runningTitle: "Preload en cours",
          successMessage,
          successStatusLabel: "Prêt",
          successTitle: "Preload activé",
        },
      });
      void submitPreloadedPreviousDay({ requestBody: body });
    },
    requestPreloadedPreviousDayRemoval({
      dispatchPeriod = "day",
      groupStepIds = [],
      orderIds = [],
      scope = "order",
      sourceGroupKey = "",
      sourceTruckId = "",
    } = {}) {
      const state = workspaceStore.getState();
      if (!state) {
        return;
      }

      const body = buildPreloadedPreviousDayRemovalRequestBody({
        groupStepIds,
        orderIds,
        rebuildMode: resolvedReassignmentRebuildMode,
        scope,
        sourceGroupKey,
        sourceTruckId,
      });
      if (!body) {
        return;
      }

      const isGroupScope = body.scope === "group";
      const orderCount = Array.isArray(body.order_ids) ? body.order_ids.length : 0;
      const subjectLabel = isGroupScope
        ? `Le groupe de ${orderCount} commande${orderCount > 1 ? "s" : ""}`
        : "La commande";
      const successMessage = `${subjectLabel} n'est plus ${isGroupScope ? "marqué" : "marquée"} Chargé la veille.`;

      applyPlanningMutation({
        url: PRELOADED_PREVIOUS_DAY_REMOVAL_API_URL,
        body,
        dispatchPeriod,
        orderId: body.order_ids[0] || null,
        preferredAssignmentId: body.source_truck_id,
        actionName: "remove_preloaded_previous_day_assignment",
        loadingToastContent: {
          errorTitle: "Annulation Preload interrompue",
          title: "Annulation Preload en cours",
          message: "Nous remettons Chargé la veille à Non et reconstruisons uniquement le camion courant.",
          successTitle: "Preload annulé",
          successMessage,
        },
        resolveServerErrorMessage({ errorMessage }) {
          return normalizeInlineText(errorMessage) || "Impossible de désactiver Chargé la veille depuis le Gantt.";
        },
        statusNoticeContent: {
          eyebrow: "Planning manuel",
          errorTitle: "Annulation Preload interrompue",
          runningMessage: "Retrait du préchargement veille local en cours.",
          runningStatusLabel: "Traitement en cours",
          runningTitle: "Annulation Preload en cours",
          successMessage,
          successStatusLabel: "Prêt",
          successTitle: "Preload annulé",
        },
      });
    },
    requestEndOfDayLoadAssignment({
      deliveryMode = "",
      deliveryId = "",
      deliveryIds = [],
      dispatchDate = "",
      groupStepIds = [],
      markerDispatchDate = "",
      markerKey = "",
      returnedToDayTruckLabel = "",
      sourceGroupKey = "",
      sourceDispatchDate = "",
      sourceTruckId = "",
      scope = "delivery",
      targetPeriod = "",
      targetDispatchDate = "",
      targetCompatibility = null,
      targetTruckId = "",
      targetTruckName = "",
    } = {}) {
      const state = workspaceStore.getState();
      if (!state) {
        return;
      }

      const targetTruckLabel = normalizeInlineText(targetTruckName) || normalizeInlineText(targetTruckId) || "le camion cible";
      const dompeurOverrideRequirement = resolveDompeurOverrideRequirementState(targetCompatibility);
      const checkboxFields = buildPlanningMutationCheckboxFields({ dompeurOverrideRequirement });
      const dompeurIsConfirmCheckbox = checkboxFields[0]?.key === DOMPEUR_CONFIRM_FIELD_KEY;
      const onlyDompeurCheckbox = checkboxFields.length === 1 && dompeurIsConfirmCheckbox;
      const requestedDeliveryCount = Math.max(
        (Array.isArray(deliveryIds) ? deliveryIds.filter((candidate) => normalizeInlineText(candidate)).length : 0),
        deliveryId ? 1 : 0,
      );

      // Une relance reprend le corps qui a reçu le défi : les réponses déjà données sont gardées.
      const commitEndOfDayLoadAssignment = (dialogValues = false, targetSuspensionDecision = "ask", challengedBody = null) => {
        const confirmDompeurOverride = Boolean(
          dompeurOverrideRequirement
          && readPlanningMutationCheckboxValue(dialogValues, DOMPEUR_CONFIRM_FIELD_KEY, {
            usePrimaryFallback: onlyDompeurCheckbox || dompeurIsConfirmCheckbox,
          }),
        );
        const body = challengedBody ? { ...challengedBody } : buildEndOfDayLoadAssignmentRequestBody({
          deliveryMode,
          deliveryId,
          deliveryIds,
          dispatchDate,
          groupStepIds,
          markerDispatchDate,
          markerKey,
          sourceGroupKey,
          sourceDispatchDate,
          sourceTruckId,
          scope,
          targetPeriod,
          targetDispatchDate,
          targetTruckId,
        });
        if (!body) {
          dismissConfirmation();
          showToast({
            type: "error",
            title: "Chargé en PM impossible",
            message: "Les informations de livraison ou la date cible sont incomplètes. Réouvrez le segment et réessayez.",
          });
          return;
        }
        body.target_suspension_decision = targetSuspensionDecision;

        const resolvedDeliveryIds = Array.isArray(body.delivery_ids) ? body.delivery_ids : [];
        const primaryDeliveryId = resolvedDeliveryIds[0] || deliveryId || "";
        const targetDateLabel = normalizeInlineText(body.target_dispatch_date);
        const isGroupScope = body.scope === "group";
        const whenLabel = body.delivery_mode === "evening" || body.target_period === "evening"
          ? `le soir du ${formatDispatchDayLabel(targetDateLabel)}`
          : `le ${formatDispatchDayLabel(targetDateLabel)}`;
        const subjectLabel = isGroupScope
          ? `Le groupe de ${resolvedDeliveryIds.length} commande${resolvedDeliveryIds.length > 1 ? "s" : ""}`
          : "La commande";
        const successMessage = `${subjectLabel} sera ${isGroupScope ? "livré" : "livrée"} ${whenLabel} sur ${targetTruckLabel}.`;
        const requestedDompeurDetailItems = buildRequestedDompeurOverrideDetailItems({
          confirmDompeurOverride,
          targetCompatibility,
        });
        const returnedToDayDetailItems = returnedToDayTruckLabel
          ? [{ key: "returned-to-day", label: "Commande", value: `Restée au jour sur ${returnedToDayTruckLabel}` }]
          : [];

        // Compléter un compartiment, puis commandes sorties du camion cible : deux défis successifs.
        const handleCompartmentSharingChallenge = createCompartmentSharingChallengeHandler({
          dismissConfirmation,
          retryMutation: ({ requestBody }) => commitEndOfDayLoadAssignment(dialogValues, requestBody.target_suspension_decision, requestBody),
          setConfirmationDialog,
          targetTruckLabel,
        });

        dismissConfirmation();
        applyPlanningMutation({
          url: END_OF_DAY_LOAD_ASSIGNMENT_API_URL,
          body,
          deliveryId: primaryDeliveryId,
          preferredAssignmentId: body.target_truck_id,
          actionName: "end_of_day_load_assignment",
          onMutationChallenge: (challengeContext) => {
            if (handleCompartmentSharingChallenge(challengeContext)) {
              return true;
            }
            const { payload, requestBody } = challengeContext;
            if (payload?.code !== END_OF_DAY_LOAD_TARGET_SUSPENSION_CODE) {
              return false;
            }
            setConfirmationDialog(buildEndOfDayLoadTargetSuspensionDialog(payload, (decision) => {
              dismissConfirmation();
              if (decision) {
                commitEndOfDayLoadAssignment(dialogValues, decision, requestBody);
              }
            }));
            return true;
          },
          loadingToastContent: {
            errorTitle: "Chargé en PM interrompu",
            title: "Chargé en PM en cours",
            message: "Nous reportons la livraison et ajoutons la bulle de fin de journée.",
            successTitle: "Chargé en PM assigné",
            successMessage,
          },
          statusNoticeContent: {
            eyebrow: "Planning manuel",
            errorDetailItems: [...requestedDompeurDetailItems, ...returnedToDayDetailItems],
            errorTitle: "Chargé en PM interrompu",
            runningMessage: "Chargé PM en cours d'enregistrement…",
            runningStatusLabel: "Traitement en cours",
            runningTitle: "Chargé en PM en cours",
            successDetailItems: requestedDompeurDetailItems,
            successMessage,
            successStatusLabel: "Prêt",
            successTitle: "Chargé en PM assigné",
          },
        });
      };

      if (dompeurOverrideRequirement) {
        setConfirmationDialog({
          eyebrow: "Planning manuel",
          title: "Confirmer l'override Dompeur ?",
          description: "Cette action Chargé en PM vise un camion qui contourne la règle Dompeur.",
          checkboxFields: checkboxFields.slice(1),
          confirmCheckbox: checkboxFields[0] || null,
          confirmLabel: "Confirmer",
          isOpen: true,
          onCancel: dismissConfirmation,
          onClose: dismissConfirmation,
          onConfirm: commitEndOfDayLoadAssignment,
          summaryItems: [
            { label: "Action", value: "Chargé PM" },
            { label: "Camion", value: targetTruckLabel },
            { label: "Commandes", value: String(requestedDeliveryCount || 1) },
          ],
          warningMessage: dompeurOverrideRequirement.description,
        });
        return;
      }

      commitEndOfDayLoadAssignment();
    },
    requestEveningStandardTransfer({
      destinationKind = "truck",
      deliveryId = "",
      deliveryIds = [],
      dispatchDate = "",
      expectedRuntimeRevision = "",
      groupStepIds = [],
      onMutationSuccess = null,
      orderIds = [],
      rebuildMode = "",
      scope = "delivery",
      sourceDispatchPeriod = "",
      sourceGroupKey = "",
      sourceTruckId = "",
      targetCompatibility = null,
      targetPeriod = "",
      targetTruckId = "",
      targetTruckName = "",
    } = {}) {
      const state = workspaceStore.getState();
      if (!state) {
        return;
      }
      const capturedRebuildMode = normalizeDispatchReassignmentMode(
        rebuildMode || resolvedReassignmentRebuildMode,
      );
      const capturedProtectAfterReassignment = resolvedProtectAfterReassignment;

      const targetTruckLabel = normalizeInlineText(targetTruckName) || normalizeInlineText(targetTruckId) || "le camion cible";
      const normalizedSourceDispatchPeriod = normalizeInlineText(sourceDispatchPeriod)
        ? normalizePlanningDispatchPeriod(sourceDispatchPeriod)
        : "";
      const normalizedTargetPeriod = normalizeInlineText(targetPeriod)
        ? normalizePlanningDispatchPeriod(targetPeriod)
        : normalizedSourceDispatchPeriod === "evening" ? "day" : "evening";
      const targetPeriodLabel = normalizedTargetPeriod === "day" ? "Jour" : "Soir";
      const targetPeriodLabelLower = targetPeriodLabel.toLowerCase();
      const isSuspendedDestination = destinationKind === "suspended";
      const dompeurOverrideRequirement = isSuspendedDestination ? null : resolveDompeurOverrideRequirementState(targetCompatibility);
      const checkboxFields = buildPlanningMutationCheckboxFields({ dompeurOverrideRequirement });
      const dompeurIsConfirmCheckbox = checkboxFields[0]?.key === DOMPEUR_CONFIRM_FIELD_KEY;
      const onlyDompeurCheckbox = checkboxFields.length === 1 && dompeurIsConfirmCheckbox;
      const requestedDeliveryCount = Math.max(
        (Array.isArray(deliveryIds) ? deliveryIds.filter((candidate) => normalizeInlineText(candidate)).length : 0),
        deliveryId ? 1 : 0,
      );
      const resolvedOrderIds = (Array.isArray(orderIds) ? orderIds : [])
        .map((orderId) => normalizeInlineText(orderId))
        .filter(Boolean)
        .filter((orderId, index, allOrderIds) => allOrderIds.indexOf(orderId) === index);

      const commitEveningStandardTransfer = (dialogValues = false) => {
        const confirmDompeurOverride = Boolean(
          dompeurOverrideRequirement
          && readPlanningMutationCheckboxValue(dialogValues, DOMPEUR_CONFIRM_FIELD_KEY, {
            usePrimaryFallback: onlyDompeurCheckbox || dompeurIsConfirmCheckbox,
          }),
        );
        const body = buildEveningStandardTransferRequestBody({
          confirmDompeurOverride,
          destinationKind,
          deliveryId,
          deliveryIds,
          dispatchDate,
          groupStepIds,
          protectAfterReassignment: capturedProtectAfterReassignment,
          rebuildMode: capturedRebuildMode,
          scope,
          sourceGroupKey,
          sourceTruckId,
          targetPeriod: normalizedTargetPeriod,
          targetTruckId,
        });
        if (!body) {
          dismissConfirmation();
          showToast({
            type: "error",
            title: `Transfert ${targetPeriodLabelLower} impossible`,
            message: "Les informations de livraison, de date ou de camion cible sont incomplètes.",
          });
          return;
        }

        const resolvedDeliveryIds = Array.isArray(body.delivery_ids) ? body.delivery_ids : [];
        const primaryDeliveryId = resolvedDeliveryIds[0] || deliveryId || "";
        const targetDateLabel = normalizeInlineText(body.dispatch_date);
        const isGroupScope = body.scope === "group";
        const subjectLabel = isGroupScope
          ? `Le groupe de ${resolvedDeliveryIds.length} commande${resolvedDeliveryIds.length > 1 ? "s" : ""}`
          : "La commande";
        const successMessage = isSuspendedDestination
          ? `${subjectLabel} a été ${isGroupScope ? "mis" : "mise"} dans Suspendues du ${targetPeriodLabel} du ${formatDispatchDayLabel(targetDateLabel)}.`
          : `${subjectLabel} a été ${isGroupScope ? "transféré" : "transférée"} au ${targetPeriodLabel} du ${formatDispatchDayLabel(targetDateLabel)}, sur ${targetTruckLabel}.`;
        if (isSuspendedDestination && expectedRuntimeRevision) {
          body.expected_runtime_revision_period = normalizedSourceDispatchPeriod;
        }
        const runningMessage = isSuspendedDestination
          ? `Déplacement vers les suspendues du ${targetPeriodLabelLower} en cours.`
          : `Transfert vers ${targetPeriodLabel} standard en cours. Le camion source et le camion cible sont recalculés.`;
        const requestedDompeurDetailItems = buildRequestedDompeurOverrideDetailItems({
          confirmDompeurOverride,
          targetCompatibility,
        });

        dismissConfirmation();
        applyPlanningMutation({
          url: EVENING_STANDARD_TRANSFER_API_URL,
          body,
          expectedRuntimeRevision,
          deliveryId: primaryDeliveryId,
          dispatchPeriod: normalizedTargetPeriod,
          resultDispatchPeriod: "day",
          highlightSurface: isSuspendedDestination ? "suspended" : "timeline",
          highlightedStepKeys: [
            ...resolvedDeliveryIds.map((resolvedDeliveryId) => getDeliveryHighlightKey(resolvedDeliveryId)),
            ...resolvedOrderIds.map((orderId) => getLoadingOrderHighlightKey(orderId)),
          ],
          preferredAssignmentId: body.source_truck_id || body.target_truck_id,
          actionName: "evening_standard_transfer",
          loadingToastContent: {
            errorTitle: `Transfert ${targetPeriodLabelLower} interrompu`,
            title: `Transfert ${targetPeriodLabelLower} en cours`,
            message: runningMessage,
            successTitle: `Transfert ${targetPeriodLabelLower} terminé`,
            successMessage,
          },
          onMutationSuccess,
          resolveServerErrorMessage({ errorMessage }) {
            return normalizeInlineText(errorMessage) || `Impossible de transférer la commande vers le dispatch ${targetPeriodLabelLower}.`;
          },
          statusNoticeContent: {
            eyebrow: "Planning manuel",
            errorDetailItems: requestedDompeurDetailItems,
            errorTitle: `Transfert ${targetPeriodLabelLower} interrompu`,
            runningMessage,
            runningStatusLabel: "Traitement en cours",
            runningTitle: `Transfert ${targetPeriodLabelLower} en cours`,
            successDetailItems: requestedDompeurDetailItems,
            successMessage,
            successStatusLabel: "Prêt",
            successTitle: `Transfert ${targetPeriodLabelLower} terminé`,
          },
        });
      };

      if (dompeurOverrideRequirement) {
        setConfirmationDialog({
          eyebrow: "Planning manuel",
          title: "Confirmer l'override Dompeur ?",
          description: `Ce transfert ${targetPeriodLabelLower} vise un camion qui contourne la règle Dompeur.`,
          checkboxFields: checkboxFields.slice(1),
          confirmCheckbox: checkboxFields[0] || null,
          confirmLabel: "Confirmer",
          isOpen: true,
          onCancel: dismissConfirmation,
          onClose: dismissConfirmation,
          onConfirm: commitEveningStandardTransfer,
          summaryItems: [
            { label: "Action", value: `${targetPeriodLabel} standard` },
            { label: "Camion", value: targetTruckLabel },
            { label: "Commandes", value: String(requestedDeliveryCount || 1) },
          ],
          warningMessage: dompeurOverrideRequirement.description,
        });
        return;
      }

      commitEveningStandardTransfer();
    },
    requestEndOfDayLoadRemoval({
      dispatchDate = "",
      isSourcePreload = false,
      markerDispatchDate = "",
      markerKey = "",
      removalMode = "",
      sourceDispatchDate = "",
      targetDispatchDate = "",
    } = {}) {
      const state = workspaceStore.getState();
      if (!state) {
        return;
      }

      const body = buildEndOfDayLoadRemovalRequestBody({
        dispatchDate,
        isSourcePreload,
        markerDispatchDate,
        markerKey,
        removalMode,
        sourceDispatchDate,
        targetDispatchDate,
      });
      if (!body) {
        return;
      }

      applyPlanningMutation({
        url: END_OF_DAY_LOAD_REMOVAL_API_URL,
        body,
        actionName: "end_of_day_load_removal",
        loadingToastContent: {
          errorTitle: "Déchargement PM interrompu",
          title: "Déchargement PM en cours",
          message: "Nous retirons la bulle Chargé en PM et le verrou source associé.",
          successTitle: ({ payload }) => resolveEndOfDayLoadRemovalFeedback(payload).title,
          successMessage: ({ payload }) => resolveEndOfDayLoadRemovalFeedback(payload).message,
        },
        statusNoticeContent: {
          eyebrow: "Planning manuel",
          errorTitle: "Déchargement PM interrompu",
          runningMessage: "Retrait du chargement de fin de journée en cours.",
          runningStatusLabel: "Traitement en cours",
          runningTitle: "Déchargement PM en cours",
          successMessage: ({ payload }) => resolveEndOfDayLoadRemovalFeedback(payload).message,
          successStatusLabel: "Prêt",
          successTitle: ({ payload }) => resolveEndOfDayLoadRemovalFeedback(payload).title,
        },
      });
    },
    requestSegmentReorder(payload = {}) {
      const state = workspaceStore.getState();
      if (!state) {
        return;
      }

      const expectedRuntimeRevision = normalizeInlineText(payload.expectedRuntimeRevision);
      const isUndo = payload.isUndo === true;
      const onMutationError = typeof payload.onMutationError === "function" ? payload.onMutationError : null;
      const onMutationStart = typeof payload.onMutationStart === "function" ? payload.onMutationStart : null;
      const onMutationSuccess = typeof payload.onMutationSuccess === "function" ? payload.onMutationSuccess : null;

      const request = buildSegmentReorderMutationRequest({
        ...payload,
        rebuildMode: resolvedReassignmentRebuildMode,
      });
      if (!request) {
        return;
      }

      const preferredAssignmentId = request.truckId || null;
      // Le serveur répond sans rien changer : on affiche son message, pas une réussite.
      const isUnchanged = ({ payload: responsePayload }) => /ne change pas|déjà chargés à la suite|n'est pas chargé à la suite/.test(responsePayload?.message || "");
      const resolveSuccessMessage = (context) => (isUnchanged(context) ? context.payload.message : request.successMessage);

      const commitSegmentReorder = () => {
        dismissConfirmation();
        applyPlanningMutation({
          url: "/api/planning/segment-reorder",
          body: request.body,
          actionName: "segment_reorder",
          dispatchPeriod: request.dispatchPeriod,
          expectedRuntimeRevision,
          highlightedStepKeys: request.highlightedStepKeys,
          isUndo,
          loadingStepId: request.loadingStepId,
          onMutationError,
          onMutationStart,
          onMutationSuccess,
          preferredAssignmentId,
          loadingToastContent: {
            errorTitle: "Réordonnancement interrompu",
            title: "Réordonnancement local en cours",
            message: `Nous mettons à jour l'ordre manuel des ${request.scopeLabel} sur le camion courant et recalculons uniquement ce périmètre local.`,
            successTitle: (context) => (isUnchanged(context) ? "Aucun changement" : "Réordonnancement terminé"),
            successMessage: resolveSuccessMessage,
          },
          statusNoticeContent: {
            eyebrow: "Planning manuel",
            errorTitle: "Réordonnancement interrompu",
            runningMessage: `Réordonnancement local des ${request.scopeLabel} en cours. Aucun déplacement inter-camion ne sera appliqué.`,
            runningStatusLabel: "Traitement en cours",
            runningTitle: "Réordonnancement local en cours",
            successMessage: resolveSuccessMessage,
            successStatusLabel: "Prêt",
            successTitle: (context) => (isUnchanged(context)
              ? "Aucun changement"
              : request.isLoadScope
                ? "Réordonnancement des chargements terminé"
                : "Réordonnancement des livraisons terminé"),
          },
        });
      };

      if (!request.confirmationDialog) {
        commitSegmentReorder();
        return;
      }

      setConfirmationDialog({
        ...request.confirmationDialog,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: commitSegmentReorder,
      });
    },
    requestPlanningReset(options = {}) {
      const state = workspaceStore.getState();
      if (!state) {
        return;
      }

      const resetOptimizationMode = normalizeInlineText(options?.optimizationMode).toLowerCase();
      const launchPlanningReset = async () => {
        const resetOperationId = createPerformanceCorrelationId();
        const resetScope = {
          dispatchDate: getCurrentDispatchDateFromLocation() || normalizeInlineText(state.resultsData?.dispatch_date),
          dispatchPeriod: getCurrentDispatchPeriodFromLocation(),
        };
        setPlanningResetLaunchScope(resetScope);
        clearAppStatusNotice();
        try {
          const launchState = await launchOptimizationViaApi({
            ...resetScope,
            includeSuspendedDeliveries: options?.includeSuspendedDeliveries === true,
            optimizationMode: resetOptimizationMode || null,
            requestHeaders: {
              ...(state.planningDiagnostics?.enabled ? { "X-Planning-Diagnostics": "1" } : {}),
              ...buildPlanningPerformanceActionHeaders("reset_planning"),
            },
            requestUrl: "/api/planning/reset",
          });
          trackOptimizationLaunch({
            ...launchState,
            operationKind: "planning_reset",
            message: normalizeInlineText(launchState?.message)
              || "Réinitialisation en cours.",
          });
        } catch (error) {
          const errorMessage = error instanceof Error && error.message
            ? error.message
            : "Le lancement de la réinitialisation du planning a échoué.";
          const captureContext = buildStatusNoticeAlertCaptureContext({
            action: "planning_reset",
            actionLabel: formatPlanningDiagnosticActionLabel("planning_reset"),
            captureContext: {
              action: "planning_reset",
              action_label: formatPlanningDiagnosticActionLabel("planning_reset"),
              dispatch: {
                target_date: getCurrentDispatchDateFromLocation(),
                target_period: getCurrentDispatchPeriodFromLocation(),
              },
              interaction_origin: "direct",
              operation_id: resetOperationId,
              source: "optimization",
              technical: {
                correlation_id: error?.correlationId || resetOperationId,
                failed_stage: error?.failedStage || "",
                failure_kind: Number.isInteger(Number(error?.status)) ? "http" : "network",
                http_status: error?.status,
                method: "POST",
                path: error?.requestPath || "/api/planning/reset",
                reason_code: error?.reasonCode || "",
              },
            },
            operationId: resetOperationId,
            status: "failed",
            title: "Réinitialisation du planning interrompue",
          });
          trackOptimizationLaunch({
            captureContext,
            error: errorMessage,
            finishedAt: new Date().toISOString(),
            message: errorMessage,
            operationKind: "planning_reset",
            status: "failed",
            ...resetScope,
          });
        } finally {
          setPlanningResetLaunchScope((currentScope) => currentScope === resetScope ? null : currentScope);
        }
      };
      const content = buildPlanningResetConfirmationContent(state.planningSummary);
      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          void launchPlanningReset();
        },
      });
    },
    createDispatchSnapshotSave(options = {}) {
      const dispatchDate = normalizeInlineText(options?.dispatchDate);
      const mutationUrl = buildDispatchSnapshotCreateUrl(dispatchDate);
      if (!mutationUrl) {
        return Promise.resolve();
      }
      const label = normalizeInlineText(options?.label);
      const onMutationSuccess = typeof options?.onMutationSuccess === "function"
        ? options.onMutationSuccess
        : null;
      const rethrowOnError = options?.rethrowOnError === true;
      return applyPlanningMutation({
        url: mutationUrl,
        body: label ? { label } : null,
        actionName: "create_dispatch_snapshot_save",
        allowMissingResultPayload: true,
        loadingToastContent: {
          errorTitle: "Sauvegarde interrompue",
          title: "Sauvegarde du dispatch",
          message: "Nous sauvegardons le dispatch courant.",
          successTitle: "Dispatch sauvegardé",
          successMessage: "Le dispatch courant est disponible dans les sauvegardes de la journée.",
        },
        statusNoticeContent: {
          eyebrow: "Sauvegarde dispatch",
          errorTitle: "Sauvegarde interrompue",
          runningMessage: "Sauvegarde du dispatch courant en cours.",
          runningStatusLabel: "Traitement en cours",
          runningTitle: "Sauvegarde du dispatch",
          successMessage: "Le dispatch courant est sauvegardé.",
          successStatusLabel: "Prêt",
          successTitle: "Dispatch sauvegardé",
        },
        onMutationSuccess(context = {}) {
          onMutationSuccess?.(context);
        },
        rethrowOnError,
      });
    },
    restoreDispatchSnapshot(options = {}) {
      const dispatchDate = normalizeInlineText(options?.dispatchDate);
      const snapshotId = normalizeInlineText(options?.snapshotId);
      const onMutationSuccess = typeof options?.onMutationSuccess === "function"
        ? options.onMutationSuccess
        : null;
      const rethrowOnError = options?.rethrowOnError === true;
      const restore = (restoreOptions) => {
        const mutationUrl = appendDispatchSnapshotRestoreConfirmationParams(
          buildDispatchSnapshotRestoreUrl(snapshotId, dispatchDate),
          restoreOptions,
        );
        if (!mutationUrl) {
          return Promise.resolve();
        }
        return applyPlanningMutation({
          url: mutationUrl,
          actionName: "restore_dispatch_snapshot",
          // Tournées commencées dont la sauvegarde change le chauffeur ou le camion : rien n'est écrit sans confirmation.
          onMutationChallenge({ payload, response }) {
            const challenge = startedTourRestoreChallenge(payload, response?.status);
            if (!challenge) return false;
            let submitted = false;
            setConfirmationDialog({ ...startedTourRestoreConfirmation(challenge), isOpen: true,
              onConfirm: () => {
                if (submitted) return;
                submitted = true;
                dismissConfirmation();
                void restore({ ...restoreOptions, confirmStartedTours: true }).catch(() => {});
              },
              onCancel: dismissConfirmation, onClose: dismissConfirmation, onDismiss: dismissConfirmation });
            return true;
          },
          loadingToastContent: {
            errorTitle: "Restauration interrompue",
            title: "Restauration de sauvegarde",
            message: "Nous restaurons la sauvegarde sélectionnée comme dispatch courant.",
            successTitle: "Sauvegarde restaurée",
            successMessage: "La sauvegarde sélectionnée est redevenue le dispatch courant.",
          },
          statusNoticeContent: {
            eyebrow: "Sauvegarde dispatch",
            errorTitle: "Restauration interrompue",
            runningMessage: "Restauration de la sauvegarde en cours.",
            runningStatusLabel: "Traitement en cours",
            runningTitle: "Restauration de sauvegarde",
            successMessage: "La sauvegarde sélectionnée est redevenue la base active.",
            successStatusLabel: "Prêt",
            successTitle: "Sauvegarde restaurée",
          },
          onMutationSuccess(context = {}) {
            onMutationSuccess?.(context);
          },
          rethrowOnError,
        });
      };
      return restore(options);
    },
    deleteDispatchSnapshot(options = {}) {
      const dispatchDate = normalizeInlineText(options?.dispatchDate);
      const snapshotId = normalizeInlineText(options?.snapshotId);
      const mutationUrl = buildDispatchSnapshotDeleteUrl(snapshotId, dispatchDate);
      if (!mutationUrl) {
        return Promise.resolve();
      }
      const onMutationSuccess = typeof options?.onMutationSuccess === "function"
        ? options.onMutationSuccess
        : null;
      const rethrowOnError = options?.rethrowOnError === true;
      return applyPlanningMutation({
        url: mutationUrl,
        method: "DELETE",
        actionName: "delete_dispatch_snapshot",
        allowMissingResultPayload: true,
        loadingToastContent: {
          errorTitle: "Suppression de la sauvegarde interrompue",
          title: "Suppression de sauvegarde",
          message: "Nous supprimons la sauvegarde sélectionnée.",
          successTitle: "Sauvegarde supprimée",
          successMessage: "La sauvegarde a été supprimée.",
        },
        statusNoticeContent: {
          eyebrow: "Sauvegarde dispatch",
          errorTitle: "Suppression de la sauvegarde interrompue",
          runningMessage: "Suppression de la sauvegarde en cours.",
          runningStatusLabel: "Traitement en cours",
          runningTitle: "Suppression de sauvegarde",
          successMessage: "La sauvegarde a été supprimée.",
          successStatusLabel: "Prêt",
          successTitle: "Sauvegarde supprimée",
        },
        onMutationSuccess(context = {}) {
          onMutationSuccess?.(context);
        },
        rethrowOnError,
      });
    },
    setSegmentBuffer({
      manualBufferMinutes = null,
      manualBufferNote = "",
      manualBufferReason = "",
      orderId = "",
      segmentBufferKey = "",
    } = {}) {
      const state = workspaceStore.getState();
      const safeSegmentBufferKey = normalizeInlineText(segmentBufferKey);
      if (!state || !safeSegmentBufferKey) {
        return;
      }

      const parsedManualBufferMinutes = manualBufferMinutes == null || manualBufferMinutes === ""
        ? null
        : Number.parseInt(String(manualBufferMinutes), 10);
      const hasManualBuffer = Number.isInteger(parsedManualBufferMinutes) && parsedManualBufferMinutes > 0;
      const safeManualBufferNote = normalizeInlineText(manualBufferNote);
      const safeManualBufferReason = normalizeInlineText(manualBufferReason);

      applyPlanningMutation({
        url: "/api/planning/segment-buffers",
        actionName: "segment_buffer",
        body: {
          segment_buffer_key: safeSegmentBufferKey,
          manual_buffer_minutes: hasManualBuffer ? parsedManualBufferMinutes : null,
          manual_buffer_reason: hasManualBuffer ? safeManualBufferReason : null,
          manual_buffer_note: safeManualBufferNote || null,
        },
        loadingToastContent: {
          errorTitle: hasManualBuffer ? "Enregistrement du buffer interrompu" : "Retrait du buffer interrompu",
          title: hasManualBuffer ? "Buffer manuel en cours" : "Retrait du buffer en cours",
          message: hasManualBuffer
            ? "Nous ajoutons ce temps et recalculons le camion."
            : "Nous retirons ce temps et recalculons le camion.",
          successTitle: hasManualBuffer ? "Buffer manuel enregistré" : "Buffer manuel retiré",
          successMessage: hasManualBuffer
            ? "Le temps ajouté apparaît dans le Gantt."
            : "Le temps ajouté est retiré du Gantt.",
        },
        orderId: normalizeInlineText(orderId) || null,
        resolveServerErrorMessage({ errorMessage }) {
          return normalizeInlineText(errorMessage)
            || (hasManualBuffer
              ? "Impossible d'enregistrer ce buffer manuel."
              : "Impossible de retirer ce buffer manuel.");
        },
        statusNoticeContent: {
          eyebrow: "Planning manuel",
          errorTitle: hasManualBuffer ? "Enregistrement du buffer interrompu" : "Retrait du buffer interrompu",
          runningMessage: hasManualBuffer
            ? "Ajout du temps en cours. Le Gantt sera mis à jour, sans relancer « Optimiser »."
            : "Retrait du temps en cours. Le Gantt sera mis à jour, sans relancer « Optimiser ».",
          runningStatusLabel: "Traitement en cours",
          runningTitle: hasManualBuffer ? "Buffer manuel en cours" : "Retrait du buffer en cours",
          successMessage: hasManualBuffer
            ? "Le temps ajouté apparaît dans le Gantt."
            : "Le temps ajouté est retiré du Gantt.",
          successStatusLabel: "Prêt",
          successTitle: hasManualBuffer ? "Buffer manuel enregistré" : "Buffer manuel retiré",
        },
      });
    },
    applyRouteAlternative({
      alternativeId = SOREL_TRACY_ROUTE_ALTERNATIVE_ID,
      routeSegmentIndex = null,
      routeSegmentIndexes = [],
      tripId = "",
    } = {}) {
      const state = workspaceStore.getState();
      const safeTripId = normalizeInlineText(tripId);
      const parsedRouteSegmentIndexes = normalizeRouteAlternativeSegmentIndexes(routeSegmentIndex, routeSegmentIndexes);
      const parsedRouteSegmentIndex = parsedRouteSegmentIndexes[0] ?? null;
      const safeAlternativeId = normalizeInlineText(alternativeId) || SOREL_TRACY_ROUTE_ALTERNATIVE_ID;
      if (!state || !safeTripId || !Number.isInteger(parsedRouteSegmentIndex) || parsedRouteSegmentIndex <= 0) {
        return;
      }

      const routeSegmentSummaryLabel = parsedRouteSegmentIndexes.join(", ");
      setConfirmationDialog({
        ...ROUTE_ALTERNATIVE_APPLY_CONFIRMATION,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          applyPlanningMutation({
            url: "/api/planning/route-alternatives/apply",
            actionName: "route_alternative_apply",
            body: {
              alternative_id: safeAlternativeId,
              route_segment_index: parsedRouteSegmentIndex,
              route_segment_indexes: parsedRouteSegmentIndexes,
              trip_id: safeTripId,
            },
            loadingToastContent: {
              errorTitle: "Application de la traverse interrompue",
              title: "Traverse Sorel-Tracy en cours",
              message: "Nous appliquons la traverse et recalculons ce trajet.",
              successTitle: "Traverse appliquée",
              successMessage: "Le trajet passe maintenant par la traverse Sorel-Tracy.",
            },
            resolveServerErrorMessage({ errorMessage }) {
              return normalizeInlineText(errorMessage) || "Impossible d'appliquer la traverse Sorel-Tracy sur ce segment.";
            },
            statusNoticeContent: {
              eyebrow: "Planning manuel",
              errorTitle: "Application de la traverse interrompue",
              runningMessage: "Application de la traverse Sorel-Tracy en cours. Le Gantt sera mis à jour, sans relancer « Optimiser ».",
              runningStatusLabel: "Traitement en cours",
              runningTitle: "Traverse Sorel-Tracy en cours",
              successMessage: "La traverse Sorel-Tracy apparaît dans le Gantt et sur la carte.",
              successStatusLabel: "Prêt",
              successTitle: "Traverse Sorel-Tracy appliquée",
            },
          });
        },
        summaryItems: [
          { label: "Tournée", value: safeTripId },
          { label: parsedRouteSegmentIndexes.length > 1 ? "Segments" : "Segment", value: routeSegmentSummaryLabel },
        ],
      });
    },
    removeRouteAlternative({
      alternativeId = SOREL_TRACY_ROUTE_ALTERNATIVE_ID,
      routeSegmentIndex = null,
      routeSegmentIndexes = [],
      tripId = "",
    } = {}) {
      const state = workspaceStore.getState();
      const safeTripId = normalizeInlineText(tripId);
      const parsedRouteSegmentIndexes = normalizeRouteAlternativeSegmentIndexes(routeSegmentIndex, routeSegmentIndexes);
      const parsedRouteSegmentIndex = parsedRouteSegmentIndexes[0] ?? null;
      const safeAlternativeId = normalizeInlineText(alternativeId) || SOREL_TRACY_ROUTE_ALTERNATIVE_ID;
      if (!state || !safeTripId || !Number.isInteger(parsedRouteSegmentIndex) || parsedRouteSegmentIndex <= 0) {
        return;
      }

      const routeSegmentSummaryLabel = parsedRouteSegmentIndexes.join(", ");
      setConfirmationDialog({
        ...ROUTE_ALTERNATIVE_REMOVE_CONFIRMATION,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          applyPlanningMutation({
            url: "/api/planning/route-alternatives/remove",
            actionName: "route_alternative_remove",
            body: {
              alternative_id: safeAlternativeId,
              route_segment_index: parsedRouteSegmentIndex,
              route_segment_indexes: parsedRouteSegmentIndexes,
              trip_id: safeTripId,
            },
            loadingToastContent: {
              errorTitle: "Retrait de la traverse interrompu",
              title: "Retrait de la traverse en cours",
              message: "Nous retirons la traverse et recalculons ce trajet.",
              successTitle: "Traverse retirée",
              successMessage: "Le trajet reprend sa route actuelle.",
            },
            resolveServerErrorMessage({ errorMessage }) {
              return normalizeInlineText(errorMessage) || "Impossible de retirer la traverse Sorel-Tracy sur ce segment.";
            },
            statusNoticeContent: {
              eyebrow: "Planning manuel",
              errorTitle: "Retrait de la traverse interrompu",
              runningMessage: "Retrait de la traverse Sorel-Tracy en cours. Le Gantt sera mis à jour, sans relancer « Optimiser ».",
              runningStatusLabel: "Traitement en cours",
              runningTitle: "Retrait de la traverse en cours",
              successMessage: "La traverse Sorel-Tracy a été retirée du Gantt et de la carte.",
              successStatusLabel: "Prêt",
              successTitle: "Traverse Sorel-Tracy retirée",
            },
          });
        },
        summaryItems: [
          { label: "Tournée", value: safeTripId },
          { label: parsedRouteSegmentIndexes.length > 1 ? "Segments" : "Segment", value: routeSegmentSummaryLabel },
        ],
      });
    },
    updateOrderDetails({ orderId = "", payload = null, deliveryId = null, loadingStepId = null, orderEditSubmission = null } = {}) {
      const state = resolveWorkspaceState();
      const safeOrderId = normalizeInlineText(orderId);
      const normalizedPayload = payload && typeof payload === "object" && !Array.isArray(payload)
        ? payload
        : null;
      if (!state || !safeOrderId || !normalizedPayload) {
        return;
      }

      if (!orderEditSubmission && hasPendingPlanningMutation(state, { orderId: safeOrderId })) {
        showPendingOrderMutationToast();
        return;
      }

      const highlightedStepKeys = [
        deliveryId ? getDeliveryHighlightKey(deliveryId) : "",
        loadingStepId ? getLoadingHighlightKey(loadingStepId) : "",
      ].filter(Boolean);

      applyPlanningMutation({
        url: `/api/planning/orders/${encodeURIComponent(safeOrderId)}/gantt-source-backed`,
        actionName: "gantt_source_backed_save",
        orderEditSubmission,
        deliveryId,
        loadingStepId,
        body: normalizedPayload,
        orderId: safeOrderId,
        highlightedStepKeys: highlightedStepKeys.length > 0 ? highlightedStepKeys : undefined,
        loadingToastContent: {
          errorTitle: "Échec de la modification depuis le segment Gantt",
          title: "Modification depuis le segment Gantt en cours",
          message: "Nous enregistrons la commande et recalculons ce camion.",
          successTitle: "Modification depuis le segment Gantt enregistrée",
          // Message du serveur : il dit si la commande est protégée et si le temps supplémentaire a été ouvert.
          successMessage: ({ payload }) => normalizeInlineText(payload?.message)
            || "La commande est enregistrée. Le camion courant a été recalculé localement.",
        },
        statusNoticeContent: {
          eyebrow: "Planning manuel",
          errorTitle: "Échec de la modification depuis le segment Gantt",
          runningMessage: "Enregistrement en cours. Seul ce camion est recalculé.",
          runningStatusLabel: "Traitement en cours",
          runningTitle: "Modification depuis le segment Gantt en cours",
          successMessage: ({ payload }) => normalizeInlineText(payload?.message)
            || "La commande est enregistrée. Le camion courant a été recalculé localement.",
          successStatusLabel: "Prêt",
          successTitle: "Modification depuis le segment Gantt enregistrée",
        },
        resolveServerErrorMessage({ errorMessage }) {
          return normalizeInlineText(errorMessage) || "Impossible d'enregistrer la commande depuis le Gantt.";
        },
      });
    },
    invalidateDispatchDay(dispatchDate, options = {}) {
      const safeDispatchDate = normalizeInlineText(dispatchDate);
      if (!safeDispatchDate) {
        return;
      }

      const onDispatchDayMutationSuccess = typeof options?.onMutationSuccess === "function"
        ? options.onMutationSuccess
        : null;
      const state = workspaceStore.getState();
      const dispatchDayRecord = resolveDispatchDayRecordFromState(state, safeDispatchDate) || { dispatch_date: safeDispatchDate };
      const content = buildDispatchDayTransitionConfirmationContent(dispatchDayRecord, "invalidate");

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          applyPlanningMutation({
            url: `/api/dispatch-days/${encodeURIComponent(safeDispatchDate)}/invalidate`,
            actionName: "invalidate_dispatch_day",
            loadingToastContent: {
              errorTitle: "Invalidation interrompue",
              title: "Invalidation du dispatch en cours",
              message: "Nous marquons le snapshot courant comme obsolète pour exiger une nouvelle validation métier.",
              successTitle: "Dispatch invalidé",
              successMessage: `La journée du ${formatDispatchDayLabel(safeDispatchDate)} est maintenant à revalider.`,
            },
            statusNoticeContent: {
              eyebrow: "Dispatch admin",
              errorTitle: "Invalidation interrompue",
              runningMessage: "Invalidation en cours. Les tournées visibles restent consultables pendant la mise à jour du statut.",
              runningStatusLabel: "Traitement en cours",
              runningTitle: "Invalidation du dispatch en cours",
              successMessage: `La journée du ${formatDispatchDayLabel(safeDispatchDate)} est à revalider jusqu'au prochain calcul.`,
              successStatusLabel: "Prêt",
              successTitle: "Dispatch invalidé",
            },
            onMutationSuccess(context = {}) {
              publishOrderDataChange("dispatch");
              onDispatchDayMutationSuccess?.({
                ...context,
                dispatchDate: safeDispatchDate,
              });
            },
            resolveServerErrorMessage({ errorMessage }) {
              return normalizeInlineText(errorMessage)
                || `Impossible d'invalider le dispatch du ${formatDispatchDayLabel(safeDispatchDate)}.`;
            },
          });
        },
      });
    },
    resetDispatchDayRevalidation(dispatchDate, options = {}) {
      const safeDispatchDate = normalizeInlineText(dispatchDate);
      if (!safeDispatchDate) {
        return;
      }

      const onDispatchDayMutationSuccess = typeof options?.onMutationSuccess === "function"
        ? options.onMutationSuccess
        : null;

      setConfirmationDialog({
        eyebrow: "Dispatch admin",
        title: "Acquitter la journée ?",
        description: "La journée redevient « Courante », sans relancer « Optimiser ».",
        impactItems: [
          "Le « ! » « Pourquoi réoptimiser ? » disparaîtra.",
          "Les tournées, affectations et protections visibles resteront inchangées.",
          "Une nouvelle invalidation pourra être demandée ultérieurement si nécessaire.",
        ],
        confirmLabel: "Acquitter",
        cancelLabel: "Annuler",
        tone: "warning",
        warningMessage: "Rien n'est recalculé. Refusé si « Mise à jour » a encore des changements.",
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          applyPlanningMutation({
            url: `/api/dispatch-days/${encodeURIComponent(safeDispatchDate)}/reset-revalidation`,
            actionName: "reset_dispatch_day_revalidation",
            loadingToastContent: {
              errorTitle: "Acquittement interrompu",
              title: "Acquittement en cours",
              message: "Tournées inchangées.",
              successTitle: "Journée acquittée",
              successMessage: "La journée est maintenant « Courante ».",
            },
            statusNoticeContent: {
              eyebrow: "Dispatch admin",
              errorTitle: "Acquittement interrompu",
              runningMessage: "Tournées inchangées.",
              runningStatusLabel: "Traitement en cours",
              runningTitle: "Acquittement en cours",
              successMessage: "La journée est maintenant « Courante ».",
              successStatusLabel: "Prêt",
              successTitle: "Journée acquittée",
            },
            onMutationSuccess(context = {}) {
              onDispatchDayMutationSuccess?.({
                ...context,
                dispatchDate: safeDispatchDate,
              });
            },
            resolveServerErrorMessage({ errorMessage }) {
              return normalizeInlineText(errorMessage)
                || "Impossible d'acquitter cette journée.";
            },
          });
        },
      });
    },
    deleteDispatchDaySnapshot(dispatchDate, options = {}) {
      const safeDispatchDate = normalizeInlineText(dispatchDate);
      if (!safeDispatchDate) {
        return;
      }

      const onMutationSuccess = typeof options?.onMutationSuccess === "function"
        ? options.onMutationSuccess
        : null;

      setConfirmationDialog({
        eyebrow: "Suppression Dispatch",
        title: "Supprimer le snapshot Dispatch — Jour + Soir ?",
        description: `Tout le Dispatch du ${formatDispatchDayLabel(safeDispatchDate)} sera supprimé. Cette action est irréversible et la prochaine ouverture se comportera comme une première optimisation.`,
        impactItems: [
          "Jour et Soir sont supprimés ensemble, quelle que soit la période actuellement affichée.",
          "Les affectations, arrêts, protections, états runtime, historiques et sauvegardes de cette date seront supprimés.",
          "Les commandes, clients et camions du Data Manager resteront intacts.",
          "Les publications du portail conducteur seront retirées automatiquement pour Jour et Soir.",
        ],
        confirmCheckbox: {
          key: "confirm-dispatch-snapshot-deletion",
          label: "Je comprends que la suppression du snapshot Dispatch est irréversible.",
          required: true,
        },
        confirmLabel: "Supprimer le snapshot",
        cancelLabel: "Annuler",
        tone: "danger",
        warningMessage: "Action irréversible : aucune sauvegarde Dispatch de cette date ne pourra être restaurée après la suppression.",
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          void applyPlanningMutation({
            url: `/api/planning/dispatch-days/${encodeURIComponent(safeDispatchDate)}/snapshot`,
            method: "DELETE",
            actionName: "delete_dispatch_day_snapshot",
            allowMissingResultPayload: true,
            refreshResultsAfterMissingPayload: true,
            loadingToastContent: {
              errorTitle: "Suppression du dispatch interrompue",
              title: "Suppression du snapshot Dispatch",
              message: "Nous supprimons les états Jour et Soir et retirons les publications portail de cette date.",
              successTitle: "Snapshot Dispatch supprimé",
              successMessage: "La journée est revenue à l'état aucun snapshot / jamais optimisé.",
            },
            statusNoticeContent: {
              eyebrow: "Suppression Dispatch",
              errorTitle: "Suppression du dispatch interrompue",
              runningMessage: "Suppression du dispatch Jour et Soir en cours…",
              runningStatusLabel: "Traitement en cours",
              runningTitle: "Suppression du snapshot Dispatch",
              successMessage: "Le snapshot Jour + Soir et ses sauvegardes ont été supprimés; les commandes Data Manager sont intactes.",
              successStatusLabel: "Prêt",
              successTitle: "Snapshot Dispatch supprimé",
            },
            onMutationSuccess(context = {}) {
              onMutationSuccess?.(context);
            },
            resolveServerErrorMessage({ errorMessage }) {
              return normalizeInlineText(errorMessage)
                || `Impossible de supprimer le snapshot Dispatch de la journée ${safeDispatchDate}.`;
            },
          });
        },
      });
    },
    resetOptimizationRuntimeCache(options = {}) {
      const onMutationSuccess = typeof options?.onMutationSuccess === "function"
        ? options.onMutationSuccess
        : null;

      setConfirmationDialog({
        eyebrow: "Cache runtime",
        title: "Confirmer le reset cache ?",
        description: "Cette action vide les caches runtime d'optimisation côté backend sans toucher aux caches HERE ni au dispatch visible.",
        impactItems: [
          "Le prochain clic Optimiser repartira des caches runtime backend vides.",
          "Le dispatch visible reste consultable et n'est pas invalidé par cette action.",
          "Les caches HERE restent inchangés : utilisez HERE séparément si nécessaire.",
        ],
        confirmLabel: "Cache",
        cancelLabel: "Annuler",
        tone: "warning",
        warningMessage: "Le prochain rerun peut être plus lent le temps de reconstruire les caches runtime d'optimisation.",
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          applyPlanningMutation({
            url: "/api/admin/optimization-runtime-cache/reset",
            actionName: "reset_optimization_runtime_cache",
            allowMissingResultPayload: true,
            loadingToastContent: {
              errorTitle: "Reset cache interrompu",
              title: "Reset cache en cours",
              message: "Nous vidons les caches runtime d'optimisation pour forcer un rerun à froid.",
              successTitle: "Caches runtime réinitialisés",
              successMessage: "Le prochain rerun repartira d'un état froid côté caches runtime d'optimisation.",
            },
            statusNoticeContent: {
              eyebrow: "Cache runtime",
              errorTitle: "Reset cache interrompu",
              runningMessage: "Reset cache en cours. Le dispatch visible reste consultable pendant la purge technique.",
              runningStatusLabel: "Traitement en cours",
              runningTitle: "Reset cache en cours",
              successMessage: "Les caches runtime d'optimisation ont été vidés. Le prochain rerun repartira à froid.",
              successStatusLabel: "Prêt",
              successTitle: "Caches runtime réinitialisés",
              successDetailItems: [
                {
                  key: "optimization-runtime-cache-scope",
                  label: "Portée",
                  value: "Caches runtime d'optimisation",
                },
                {
                  key: "optimization-runtime-cache-here",
                  label: "HERE",
                  value: "Inchangé",
                },
              ],
            },
            onMutationSuccess(context = {}) {
              onMutationSuccess?.(context);
            },
            resolveServerErrorMessage({ errorMessage }) {
              return normalizeInlineText(errorMessage)
                || "Impossible de réinitialiser les caches runtime d'optimisation.";
            },
          });
        },
      });
    },
    resetHereRouteCache(options = {}) {
      const onMutationSuccess = typeof options?.onMutationSuccess === "function"
        ? options.onMutationSuccess
        : null;

      setConfirmationDialog({
        eyebrow: "Cache HERE",
        title: "Confirmer le reset HERE ?",
        description: "Cette action vide le cache routier HERE persistant ainsi que le cache mémoire de calcul. Les prochains calculs de trajet redemanderont HERE.",
        impactItems: [
          "Les prochains calculs de route repartiront d'un cache vide côté HERE.",
          "Les durées et distances routières seront recalculées lors des prochaines sollicitations.",
          "Aucune tournée visible n'est supprimée: seuls les caches techniques de routage sont vidés.",
        ],
        confirmLabel: "HERE",
        cancelLabel: "Annuler",
        tone: "warning",
        warningMessage: "Cette action peut rallonger le prochain calcul de route le temps de reconstituer le cache HERE.",
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          applyPlanningMutation({
            url: "/api/admin/here-route-cache/reset",
            actionName: "reset_here_route_cache",
            loadingToastContent: {
              errorTitle: "Reset HERE interrompu",
              title: "Reset HERE en cours",
              message: "Nous vidons les caches techniques HERE pour forcer un nouveau calcul routier au prochain appel.",
              successTitle: "Cache HERE réinitialisé",
              successMessage: "Le prochain calcul de route sollicitera HERE à nouveau.",
            },
            statusNoticeContent: {
              eyebrow: "Cache HERE",
              errorTitle: "Reset HERE interrompu",
              runningMessage: "Reset HERE en cours. Les tournées visibles restent consultables pendant la purge des caches.",
              runningStatusLabel: "Traitement en cours",
              runningTitle: "Reset HERE en cours",
              successMessage: "Les caches HERE ont été vidés. Le prochain calcul de route repartira d'un cache vide.",
              successStatusLabel: "Prêt",
              successTitle: "Cache HERE réinitialisé",
            },
            onMutationSuccess(context = {}) {
              onMutationSuccess?.(context);
            },
            resolveServerErrorMessage({ errorMessage }) {
              return normalizeInlineText(errorMessage)
                || "Impossible de réinitialiser le cache HERE.";
            },
          });
        },
      });
    },
    closeDispatchDay(dispatchDate, options = {}) {
      const safeDispatchDate = normalizeInlineText(dispatchDate);
      if (!safeDispatchDate) {
        return;
      }

      const onDispatchDayMutationSuccess = typeof options?.onMutationSuccess === "function"
        ? options.onMutationSuccess
        : null;
      const state = workspaceStore.getState();
      const dispatchDayRecord = resolveDispatchDayRecordFromState(state, safeDispatchDate) || { dispatch_date: safeDispatchDate };
      const confirm = () => {
        dismissConfirmation();
        applyPlanningMutation({
          url: `/api/dispatch-days/${encodeURIComponent(safeDispatchDate)}/close`,
          actionName: "close_dispatch_day",
          loadingToastContent: {
            errorTitle: "Clôture de la journée interrompue",
            title: "Fermeture de journée en cours",
            message: "Nous verrouillons la journée de dispatch active pour empêcher toute nouvelle mutation manuelle.",
            successTitle: "Journée fermée",
            successMessage: `La journée ${safeDispatchDate} est maintenant fermée.`,
          },
          statusNoticeContent: {
            eyebrow: "Journée dispatch",
            errorTitle: "Clôture de la journée interrompue",
            runningMessage: "Fermeture en cours. Les mutations manuelles seront bloquées dès validation.",
            runningStatusLabel: "Traitement en cours",
            runningTitle: "Fermeture de journée en cours",
            successMessage: `La journée ${safeDispatchDate} est fermée et n'accepte plus de mutation.`,
            successStatusLabel: "Prêt",
            successTitle: "Journée dispatch fermée",
          },
          onMutationSuccess(context = {}) {
            publishOrderDataChange("dispatch");
            onDispatchDayMutationSuccess?.({
              ...context,
              dispatchDate: safeDispatchDate,
            });
          },
        });
      };
      // Écarts Geotab : la fenêtre Geotab de la journée tient lieu de confirmation.
      const content = withDispatchGeotabCheck(buildDispatchDayTransitionConfirmationContent(dispatchDayRecord, "close"), safeDispatchDate, { confirm, cancel: dismissConfirmation });
      if (!content) {
        return;
      }

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: confirm,
      });
    },
    reopenDispatchDay(dispatchDate, options = {}) {
      const safeDispatchDate = normalizeInlineText(dispatchDate);
      if (!safeDispatchDate) {
        return;
      }

      const onDispatchDayMutationSuccess = typeof options?.onMutationSuccess === "function"
        ? options.onMutationSuccess
        : null;
      const state = workspaceStore.getState();
      const dispatchDayRecord = resolveDispatchDayRecordFromState(state, safeDispatchDate) || { dispatch_date: safeDispatchDate };
      const content = buildDispatchDayTransitionConfirmationContent(dispatchDayRecord, "reopen");

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          applyPlanningMutation({
            url: `/api/dispatch-days/${encodeURIComponent(safeDispatchDate)}/reopen`,
            actionName: "reopen_dispatch_day",
            loadingToastContent: {
              errorTitle: "Réouverture de la journée interrompue",
              title: "Réouverture de journée en cours",
              message: "Nous réactivons la journée de dispatch pour autoriser de nouvelles mutations manuelles.",
              successTitle: "Journée réouverte",
              successMessage: `La journée ${safeDispatchDate} est de nouveau modifiable.`,
            },
            statusNoticeContent: {
              eyebrow: "Journée dispatch",
              errorTitle: "Réouverture de la journée interrompue",
              runningMessage: "Réouverture en cours. Les mutations manuelles seront de nouveau autorisées une fois validées.",
              runningStatusLabel: "Traitement en cours",
              runningTitle: "Réouverture de journée en cours",
              successMessage: `La journée ${safeDispatchDate} accepte de nouveau les mutations manuelles.`,
              successStatusLabel: "Prêt",
              successTitle: "Journée dispatch réouverte",
            },
            beforeSuccessFeedback(context = {}) {
              publishOrderDataChange("dispatch");
              return onDispatchDayMutationSuccess?.({
                ...context,
                dispatchDate: safeDispatchDate,
              });
            },
          });
        },
      });
    },
    suspendDelivery(deliveryId, {
      dispatchPeriod = "day",
      expectedRuntimeRevision = "",
      isUndo = false,
      onMutationError = null,
      onMutationStart = null,
      onMutationSuccess = null,
      skipConfirmation = false,
    } = {}) {
      const state = workspaceStore.getState();
      if (!state || !deliveryId || hasPendingPlanningMutation(state, { deliveryId })) {
        return;
      }

      const content = buildDeliverySuspendConfirmationContent();

      const commitDeliverySuspension = () => {
        if (isDeliveryMutationPending(deliveryId)) {
          dismissConfirmation();
          showPendingDeliveryMutationToast();
          return;
        }

        const deliverySubject = resolveDeliveryNoticeSubject(workspaceStore.getState(), deliveryId);
        dismissConfirmation();
        applyPlanningMutation({
          url: `/api/planning/deliveries/${encodeURIComponent(deliveryId)}/suspend`,
          body: buildDispatchReassignmentMutationModeFields(resolvedReassignmentRebuildMode),
          deliveryId,
          dispatchPeriod,
          expectedRuntimeRevision,
          highlightedStepKeys: [getDeliveryHighlightKey(deliveryId)],
          highlightSurface: "suspended",
          actionName: "suspend",
          isUndo,
          loadingToastContent: {
            errorTitle: "Commande non suspendue",
            title: "Suspension en cours",
            message: "Nous retirons toute la commande de la flotte active et recalculons le planning courant.",
            successTitle: "Suspension terminée",
            successMessage: "La commande a été suspendue et retirée des tournées actives.",
          },
          onMutationError,
          onMutationStart,
          onMutationSuccess,
          resolveServerErrorMessage({ errorMessage }) {
            return resolveDeliverySuspendRejectedMessage(errorMessage);
          },
          statusNoticeContent: {
            eyebrow: "Planning manuel",
            errorTitle: "Commande non suspendue",
            runningMessage: "Suspension en cours. Vous pouvez continuer à naviguer pendant le recalcul.",
            runningStatusLabel: "Traitement en cours",
            runningTitle: "Suspension de commande en cours",
            successMessage: deliverySubject
              ? `La commande ${deliverySubject} a été suspendue et retirée des tournées actives. Le planning mis à jour est prêt.`
              : "La commande a été suspendue et retirée des tournées actives. Le planning mis à jour est prêt.",
            successStatusLabel: "Prêt",
            successTitle: appendNoticeSubject("Suspension terminée", deliverySubject),
          },
        });
      };

      if (skipConfirmation === true) {
        commitDeliverySuspension();
        return;
      }

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: commitDeliverySuspension,
      });
    },
    suspendDeliveryGroup({
      deliveryIds = [],
      dispatchPeriod = "day",
      expectedRuntimeRevision = "",
      groupStepIds = [],
      isUndo = false,
      onMutationError = null,
      onMutationStart = null,
      onMutationSuccess = null,
      sourceGroupKey = "",
    } = {}) {
      const state = workspaceStore.getState();
      const resolvedDeliveryIds = (Array.isArray(deliveryIds) ? deliveryIds : [])
        .filter((deliveryId) => typeof deliveryId === "string" && deliveryId.trim())
        .map((deliveryId) => deliveryId.trim())
        .filter((deliveryId, index, items) => items.indexOf(deliveryId) === index);
      const resolvedGroupStepIds = (Array.isArray(groupStepIds) ? groupStepIds : [])
        .filter((stepId) => typeof stepId === "string" && stepId.trim())
        .map((stepId) => stepId.trim())
        .filter((stepId, index, items) => items.indexOf(stepId) === index);
      const resolvedSourceGroupKey = normalizeInlineText(sourceGroupKey);
      const primaryDeliveryId = resolvedDeliveryIds[0] || "";
      if (!state || resolvedDeliveryIds.length < 2 || resolvedDeliveryIds.some((deliveryId) => isDeliveryMutationPending(deliveryId))) {
        return;
      }

      const groupCount = resolvedDeliveryIds.length;
      applyPlanningMutation({
        url: "/api/planning/delivery-groups/suspend",
        body: {
          delivery_ids: resolvedDeliveryIds,
          ...buildDispatchReassignmentMutationModeFields(resolvedReassignmentRebuildMode),
          ...(resolvedSourceGroupKey ? { source_group_key: resolvedSourceGroupKey } : {}),
          ...(resolvedGroupStepIds.length > 0 ? { group_step_ids: resolvedGroupStepIds } : {}),
        },
        deliveryId: primaryDeliveryId,
        dispatchPeriod,
        expectedRuntimeRevision,
        highlightedStepKeys: resolvedDeliveryIds.map((resolvedDeliveryId) => (
          getDeliveryHighlightKey(resolvedDeliveryId)
        )),
        highlightSurface: "suspended",
        actionName: "suspend_delivery_group",
        isUndo,
        loadingToastContent: {
          errorTitle: "Commande non suspendue",
          title: "Suspension en cours",
          message: `Nous retirons les ${groupCount} commandes du groupe de la flotte active.`,
          successTitle: "Suspension terminée",
          successMessage: `Les ${groupCount} commandes du groupe sont maintenant visibles dans Suspendues.`,
        },
        onMutationError,
        onMutationStart,
        onMutationSuccess,
        resolveServerErrorMessage({ errorMessage }) {
          return resolveDeliverySuspendRejectedMessage(errorMessage);
        },
        statusNoticeContent: {
          eyebrow: "Planning manuel",
          errorTitle: "Commande non suspendue",
          runningMessage: `Suspension groupée en cours. Les ${groupCount} commandes seront retirées ensemble de la tournée active.`,
          runningStatusLabel: "Traitement en cours",
          runningTitle: "Suspension de groupe en cours",
          successMessage: `Les ${groupCount} commandes du groupe ont été suspendues et retirées des tournées actives. Le planning mis à jour est prêt.`,
          successStatusLabel: "Prêt",
          successTitle: "Suspension groupée terminée",
        },
      });
    },
    freezeDelivery(deliveryId, { skipConfirmation = false } = {}) {
      const state = workspaceStore.getState();
      if (!state || !deliveryId || hasPendingPlanningMutation(state, { deliveryId })) {
        return;
      }

      const content = buildDeliveryFreezeConfirmationContent();

      const commitDeliveryFreeze = () => {
        if (isDeliveryMutationPending(deliveryId)) {
          dismissConfirmation();
          showPendingDeliveryMutationToast();
          return;
        }

        const deliverySubject = resolveDeliveryNoticeSubject(workspaceStore.getState(), deliveryId);
        dismissConfirmation();
        applyPlanningMutation({
          url: `/api/planning/deliveries/${encodeURIComponent(deliveryId)}/freeze`,
          deliveryId,
          actionName: "freeze",
          loadingToastContent: {
            errorTitle: "Gel interrompu",
            title: "Gel en cours",
            message: "Nous gelons la commande et celles chargées au même endroit sur ce camion.",
            successTitle: "Commande gelée",
            successMessage: "La commande est gelée ; ses autres actions sont masquées.",
          },
          statusNoticeContent: {
            eyebrow: "Gel",
            errorTitle: "Gel interrompu",
            runningMessage: "Gel en cours. La commande reste en place et aucune optimisation globale n'est relancée.",
            runningStatusLabel: "Traitement en cours",
            runningTitle: "Gel de commande en cours",
            successMessage: deliverySubject
              ? `La commande ${deliverySubject} est maintenant gelée.`
              : "La commande est maintenant gelée.",
            successStatusLabel: "Prêt",
            successTitle: appendNoticeSubject("Commande gelée", deliverySubject),
          },
        });
      };

      if (skipConfirmation === true) {
        commitDeliveryFreeze();
        return;
      }

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: commitDeliveryFreeze,
      });
    },
    suspendOrderFromError(entry = {}, { skipConfirmation = false } = {}) {
      const state = workspaceStore.getState();
      const { deliveryId, errorId, orderId } = resolveErrorSuspendMutationContext(entry);
      if (!state || !orderId || !deliveryId || !errorId || hasPendingPlanningMutation(state, { orderId, deliveryId })) {
        return null;
      }

      const content = buildErrorSuspendConfirmationContent(entry);
      const commitErrorSuspension = () => {
        if (hasPendingPlanningMutation(workspaceStore.getState(), { orderId, deliveryId })) {
          dismissConfirmation();
          showPendingDeliveryMutationToast();
          return null;
        }

        dismissConfirmation();
        return runSuspendOrderFromErrorMutation(entry);
      };

      if (skipConfirmation === true) {
        return commitErrorSuspension();
      }

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          void commitErrorSuspension();
        },
      });
      return null;
    },
    suspendErrorGroupFromErrors(groupRequest = []) {
      const state = workspaceStore.getState();
      const rawEntries = Array.isArray(groupRequest)
        ? groupRequest
        : Array.isArray(groupRequest?.entries)
          ? groupRequest.entries
          : [];
      const seenOrderIds = new Set();
      const groupEntries = rawEntries
        .filter((entry) => entry?.canSuspend === true)
        .filter((entry) => {
          const { deliveryId, errorId, orderId } = resolveErrorSuspendMutationContext(entry);
          if (!orderId || !deliveryId || !errorId || seenOrderIds.has(orderId)) {
            return false;
          }
          seenOrderIds.add(orderId);
          return true;
        });

      if (!state || groupEntries.length === 0) {
        return null;
      }

      const content = buildErrorSuspendGroupConfirmationContent(groupEntries);
      const groupMemberDeliveryIds = Array.from(new Set(
        groupEntries
          .map((entry) => resolveErrorSuspendMutationContext(entry).deliveryId)
          .filter(Boolean),
      ));
      const groupMetadata = {
        groupKey: normalizeInlineText(groupRequest?.groupKey || groupRequest?.rowId),
        groupLabel: normalizeInlineText(groupRequest?.groupLabel || groupRequest?.title),
        memberDeliveryIds: groupMemberDeliveryIds,
        sourceKey: "non_assignable_errors",
      };
      const commitErrorGroupSuspension = async () => {
        dismissConfirmation();
        const totalCount = groupEntries.length;

        try {
          for (let index = 0; index < groupEntries.length; index += 1) {
            const entry = groupEntries[index];
            const currentIndex = index + 1;
            const result = await runSuspendOrderFromErrorMutation(entry, {
              groupMetadata,
              rethrowOnError: true,
              loadingToastContent: {
                errorTitle: "Transfert de groupe interrompu",
                title: totalCount > 1 ? `Transfert ${currentIndex}/${totalCount}` : "Transfert en cours",
                message: "Nous transférons les commandes du groupe dans Suspendues une par une.",
                successTitle: currentIndex === totalCount ? "Groupe transféré" : "Commande transférée",
                successMessage: currentIndex === totalCount
                  ? `${totalCount} commande${totalCount > 1 ? "s" : ""} transférée${totalCount > 1 ? "s" : ""} dans Suspendues.`
                  : `Commande ${currentIndex}/${totalCount} transférée dans Suspendues.`,
                successDuration: currentIndex === totalCount ? 5000 : 1800,
              },
              statusNoticeContent: {
                eyebrow: "Centre des alertes",
                errorTitle: "Transfert de groupe interrompu",
                runningMessage: `Transfert ${currentIndex}/${totalCount} dans Suspendues en cours.`,
                runningStatusLabel: "Traitement en cours",
                runningTitle: "Transfert de groupe en cours",
                successMessage: currentIndex === totalCount
                  ? `${totalCount} commande${totalCount > 1 ? "s ont" : " a"} été transférée${totalCount > 1 ? "s" : ""} dans Suspendues.`
                  : `Commande ${currentIndex}/${totalCount} transférée dans Suspendues.`,
                successStatusLabel: "Prêt",
                successTitle: currentIndex === totalCount ? "Groupe transféré" : "Commande transférée",
              },
            });

            if (!result) {
              break;
            }
          }
        } catch (_error) {
          // applyPlanningMutation a déjà publié le toast et le statut d'échec.
        }
      };

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          void commitErrorGroupSuspension();
        },
      });
      return null;
    },
    unfreezeDelivery(deliveryId, { skipConfirmation = false } = {}) {
      const state = workspaceStore.getState();
      if (!state || !deliveryId || hasPendingPlanningMutation(state, { deliveryId })) {
        return;
      }

      const content = buildDeliveryUnfreezeConfirmationContent();

      const commitDeliveryUnfreeze = () => {
        if (isDeliveryMutationPending(deliveryId)) {
          dismissConfirmation();
          showPendingDeliveryMutationToast();
          return;
        }

        const deliverySubject = resolveDeliveryNoticeSubject(workspaceStore.getState(), deliveryId);
        dismissConfirmation();
        applyPlanningMutation({
          url: `/api/planning/deliveries/${encodeURIComponent(deliveryId)}/unfreeze`,
          deliveryId,
          actionName: "unfreeze",
          loadingToastContent: {
            errorTitle: "Dégel interrompu",
            title: "Dégel en cours",
            message: "Nous dégelons la commande.",
            successTitle: "Commande dégelée",
            successMessage: "La commande est dégelée ; ses actions sont de nouveau disponibles.",
          },
          statusNoticeContent: {
            eyebrow: "Gel",
            errorTitle: "Dégel interrompu",
            runningMessage: "Dégel en cours. La commande reste en place et les autres actions vont redevenir disponibles.",
            runningStatusLabel: "Traitement en cours",
            runningTitle: "Dégel de commande en cours",
            successMessage: deliverySubject
              ? `La commande ${deliverySubject} est de nouveau planifiable.`
              : "La commande est de nouveau planifiable.",
            successStatusLabel: "Prêt",
            successTitle: appendNoticeSubject("Commande dégelée", deliverySubject),
          },
        });
      };

      if (skipConfirmation === true) {
        commitDeliveryUnfreeze();
        return;
      }

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: commitDeliveryUnfreeze,
      });
    },
    releaseDelivery(deliveryId, { dispatchPeriod = "" } = {}) {
      const state = workspaceStore.getState();
      if (!state || !deliveryId || hasPendingPlanningMutation(state, { deliveryId })) {
        return;
      }

      const content = buildDeliveryReleaseConfirmationContent(state.planningSummary);

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          if (isDeliveryMutationPending(deliveryId)) {
            dismissConfirmation();
            showPendingDeliveryMutationToast();
            return;
          }

          const deliverySubject = resolveDeliveryNoticeSubject(workspaceStore.getState(), deliveryId);
          dismissConfirmation();
          applyPlanningMutation({
            url: `/api/planning/deliveries/${encodeURIComponent(deliveryId)}/release`,
            deliveryId,
            actionName: "release",
            dispatchPeriod,
            loadingToastContent: {
              errorTitle: "Libération interrompue",
              title: "Libération en cours",
              message: "Nous retirons le verrou manuel de réaffectation sans relancer une optimisation globale.",
              successTitle: "Libération terminée",
              successMessage: "La commande est de nouveau optimisable et restera en place jusqu'à une prochaine optimisation.",
            },
            statusNoticeContent: {
              eyebrow: "Planning manuel",
              errorTitle: "Libération interrompue",
              runningMessage: "Libération en cours. La commande redeviendra optimisable sans recalcul global implicite.",
              runningStatusLabel: "Traitement en cours",
              runningTitle: "Libération de commande en cours",
              successMessage: deliverySubject
                ? `La commande ${deliverySubject} est de nouveau optimisable.`
                : "La commande est de nouveau optimisable.",
              successStatusLabel: "Prêt",
              successTitle: appendNoticeSubject("Libération terminée", deliverySubject),
            },
          });
        },
      });
    },
    releaseDeliveryGroup({
      deliveryIds = [],
      dispatchPeriod = "",
      groupStepIds = [],
      sourceGroupKey = "",
    }) {
      const state = workspaceStore.getState();
      const resolvedDeliveryIds = (Array.isArray(deliveryIds) ? deliveryIds : [])
        .filter((deliveryId) => typeof deliveryId === "string" && deliveryId.trim())
        .map((deliveryId) => deliveryId.trim());
      if (!state || resolvedDeliveryIds.length < 2 || resolvedDeliveryIds.some((deliveryId) => isDeliveryMutationPending(deliveryId))) {
        return;
      }

      const content = buildDeliveryReleaseConfirmationContent(state.planningSummary, {
        scope: "group",
        count: resolvedDeliveryIds.length,
      });

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          const currentState = workspaceStore.getState();
          if (!currentState || resolvedDeliveryIds.length < 2) {
            dismissConfirmation();
            return;
          }

          if (resolvedDeliveryIds.some((deliveryId) => isDeliveryMutationPending(deliveryId))) {
            dismissConfirmation();
            showPendingDeliveryMutationToast();
            return;
          }

          const groupCount = resolvedDeliveryIds.length;
          dismissConfirmation();
          applyPlanningMutation({
            url: "/api/planning/delivery-groups/release",
            body: {
              delivery_ids: resolvedDeliveryIds,
              ...(sourceGroupKey ? { source_group_key: sourceGroupKey } : {}),
              ...(Array.isArray(groupStepIds) && groupStepIds.length > 0 ? { group_step_ids: groupStepIds } : {}),
            },
            actionName: "release_delivery_group",
            dispatchPeriod,
            loadingToastContent: {
              errorTitle: "Libération interrompue",
              title: "Libération en cours",
              message: `Nous retirons les verrous manuels de réaffectation des ${groupCount} commandes du groupe sans relancer une optimisation globale.`,
              successTitle: "Libération terminée",
              successMessage: `Les ${groupCount} commandes du groupe sont de nouveau optimisables.`,
            },
            statusNoticeContent: {
              eyebrow: "Planning manuel",
              errorTitle: "Libération interrompue",
              runningMessage: `Libération groupée en cours. Les ${groupCount} commandes du groupe redeviendront optimisables sans recalcul global implicite.`,
              runningStatusLabel: "Traitement en cours",
              runningTitle: "Libération de groupe en cours",
              successMessage: `Les ${groupCount} commandes du groupe sont de nouveau optimisables.`,
              successStatusLabel: "Prêt",
              successTitle: "Libération groupée terminée",
            },
          });
        },
      });
    },
    confirmDeliveryGroup({
      deliveryIds = [],
      groupStepIds = [],
      sourceGroupKey = "",
    } = {}) {
      const state = workspaceStore.getState();
      const resolvedDeliveryIds = (Array.isArray(deliveryIds) ? deliveryIds : [])
        .filter((deliveryId) => typeof deliveryId === "string" && deliveryId.trim())
        .map((deliveryId) => deliveryId.trim())
        .filter((deliveryId, index, items) => items.indexOf(deliveryId) === index);
      const resolvedGroupStepIds = (Array.isArray(groupStepIds) ? groupStepIds : [])
        .filter((stepId) => typeof stepId === "string" && stepId.trim())
        .map((stepId) => stepId.trim())
        .filter((stepId, index, items) => items.indexOf(stepId) === index);
      const resolvedSourceGroupKey = normalizeInlineText(sourceGroupKey);
      if (!state || resolvedDeliveryIds.length < 2 || resolvedDeliveryIds.some((deliveryId) => isDeliveryMutationPending(deliveryId))) {
        return;
      }

      const groupCount = resolvedDeliveryIds.length;
      const content = buildOrderProtectionConfirmationContent("delivery");
      setConfirmationDialog({
        ...content,
        title: "Confirmer la protection du groupe ?",
        description: `Les ${groupCount} commandes du groupe seront protégées ensemble.`,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          if (resolvedDeliveryIds.some((deliveryId) => isDeliveryMutationPending(deliveryId))) {
            dismissConfirmation();
            showPendingDeliveryMutationToast();
            return;
          }

          dismissConfirmation();
          applyPlanningMutation({
            url: "/api/planning/delivery-groups/confirm",
            body: {
              delivery_ids: resolvedDeliveryIds,
              ...(resolvedSourceGroupKey ? { source_group_key: resolvedSourceGroupKey } : {}),
              ...(resolvedGroupStepIds.length > 0 ? { group_step_ids: resolvedGroupStepIds } : {}),
            },
            deliveryId: resolvedDeliveryIds[0],
            highlightedStepKeys: resolvedDeliveryIds.map(getDeliveryHighlightKey),
            actionName: "confirm_delivery_group",
            loadingToastContent: {
              errorTitle: "Protection du groupe interrompue",
              title: "Protection du groupe en cours",
              message: `Nous protégeons ensemble les ${groupCount} commandes du groupe.`,
              successTitle: "Groupe protégé",
              successMessage: `Les ${groupCount} commandes du groupe sont maintenant protégées.`,
            },
          });
        },
        summaryItems: [{ label: "Commandes", value: String(groupCount) }],
      });
    },
    freezeDeliveryGroup({
      deliveryIds = [],
      groupStepIds = [],
      sourceGroupKey = "",
    } = {}) {
      const state = workspaceStore.getState();
      const resolvedDeliveryIds = (Array.isArray(deliveryIds) ? deliveryIds : [])
        .filter((deliveryId) => typeof deliveryId === "string" && deliveryId.trim())
        .map((deliveryId) => deliveryId.trim())
        .filter((deliveryId, index, items) => items.indexOf(deliveryId) === index);
      const resolvedGroupStepIds = (Array.isArray(groupStepIds) ? groupStepIds : [])
        .filter((stepId) => typeof stepId === "string" && stepId.trim())
        .map((stepId) => stepId.trim())
        .filter((stepId, index, items) => items.indexOf(stepId) === index);
      const resolvedSourceGroupKey = normalizeInlineText(sourceGroupKey);
      if (!state || resolvedDeliveryIds.length < 2 || resolvedDeliveryIds.some((deliveryId) => isDeliveryMutationPending(deliveryId))) {
        return;
      }

      const groupCount = resolvedDeliveryIds.length;
      const content = buildDeliveryFreezeConfirmationContent();
      setConfirmationDialog({
        ...content,
        title: "Confirmer le gel du groupe ?",
        description: `Les ${groupCount} commandes du groupe seront gelées ensemble.`,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          if (resolvedDeliveryIds.some((deliveryId) => isDeliveryMutationPending(deliveryId))) {
            dismissConfirmation();
            showPendingDeliveryMutationToast();
            return;
          }

          dismissConfirmation();
          applyPlanningMutation({
            url: "/api/planning/delivery-groups/freeze",
            body: {
              delivery_ids: resolvedDeliveryIds,
              ...(resolvedSourceGroupKey ? { source_group_key: resolvedSourceGroupKey } : {}),
              ...(resolvedGroupStepIds.length > 0 ? { group_step_ids: resolvedGroupStepIds } : {}),
            },
            deliveryId: resolvedDeliveryIds[0],
            highlightedStepKeys: resolvedDeliveryIds.map(getDeliveryHighlightKey),
            actionName: "freeze_delivery_group",
            loadingToastContent: {
              errorTitle: "Gel du groupe interrompu",
              title: "Gel du groupe en cours",
              message: `Nous gelons ensemble les ${groupCount} commandes du groupe.`,
              successTitle: "Groupe gelé",
              successMessage: `Les ${groupCount} commandes du groupe sont maintenant gelées.`,
            },
          });
        },
        summaryItems: [{ label: "Commandes", value: String(groupCount) }],
      });
    },
    unfreezeDeliveryGroup({
      deliveryIds = [],
      groupStepIds = [],
      sourceGroupKey = "",
    } = {}) {
      const state = workspaceStore.getState();
      const resolvedDeliveryIds = (Array.isArray(deliveryIds) ? deliveryIds : [])
        .filter((deliveryId) => typeof deliveryId === "string" && deliveryId.trim())
        .map((deliveryId) => deliveryId.trim())
        .filter((deliveryId, index, items) => items.indexOf(deliveryId) === index);
      const resolvedGroupStepIds = (Array.isArray(groupStepIds) ? groupStepIds : [])
        .filter((stepId) => typeof stepId === "string" && stepId.trim())
        .map((stepId) => stepId.trim())
        .filter((stepId, index, items) => items.indexOf(stepId) === index);
      const resolvedSourceGroupKey = normalizeInlineText(sourceGroupKey);
      if (!state || resolvedDeliveryIds.length < 2 || resolvedDeliveryIds.some((deliveryId) => isDeliveryMutationPending(deliveryId))) {
        return;
      }

      const groupCount = resolvedDeliveryIds.length;
      const content = buildDeliveryUnfreezeConfirmationContent();
      setConfirmationDialog({
        ...content,
        title: "Confirmer le dégel du groupe ?",
        description: `Les ${groupCount} commandes du groupe seront dégelées ensemble.`,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          if (resolvedDeliveryIds.some((deliveryId) => isDeliveryMutationPending(deliveryId))) {
            dismissConfirmation();
            showPendingDeliveryMutationToast();
            return;
          }

          dismissConfirmation();
          applyPlanningMutation({
            url: "/api/planning/delivery-groups/unfreeze",
            body: {
              delivery_ids: resolvedDeliveryIds,
              ...(resolvedSourceGroupKey ? { source_group_key: resolvedSourceGroupKey } : {}),
              ...(resolvedGroupStepIds.length > 0 ? { group_step_ids: resolvedGroupStepIds } : {}),
            },
            deliveryId: resolvedDeliveryIds[0],
            highlightedStepKeys: resolvedDeliveryIds.map(getDeliveryHighlightKey),
            actionName: "unfreeze_delivery_group",
            loadingToastContent: {
              errorTitle: "Dégel du groupe interrompu",
              title: "Dégel du groupe en cours",
              message: `Nous retirons le gel des ${groupCount} commandes du groupe.`,
              successTitle: "Groupe dégelé",
              successMessage: `Les ${groupCount} commandes du groupe sont de nouveau planifiables.`,
            },
          });
        },
        summaryItems: [{ label: "Commandes", value: String(groupCount) }],
      });
    },
    unconfirmDeliveryGroup({
      deliveryIds = [],
      groupStepIds = [],
      sourceGroupKey = "",
    }) {
      const state = workspaceStore.getState();
      const resolvedDeliveryIds = (Array.isArray(deliveryIds) ? deliveryIds : [])
        .filter((deliveryId) => typeof deliveryId === "string" && deliveryId.trim())
        .map((deliveryId) => deliveryId.trim());
      if (!state || resolvedDeliveryIds.length < 2 || resolvedDeliveryIds.some((deliveryId) => isDeliveryMutationPending(deliveryId))) {
        return;
      }

      const content = buildDeliveryUnconfirmConfirmationContent(state.planningSummary, {
        scope: "group",
        count: resolvedDeliveryIds.length,
      });

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          const currentState = workspaceStore.getState();
          if (!currentState || resolvedDeliveryIds.length < 2) {
            dismissConfirmation();
            return;
          }

          if (resolvedDeliveryIds.some((deliveryId) => isDeliveryMutationPending(deliveryId))) {
            dismissConfirmation();
            showPendingDeliveryMutationToast();
            return;
          }

          const groupCount = resolvedDeliveryIds.length;
          dismissConfirmation();
          applyPlanningMutation({
            url: "/api/planning/delivery-groups/unconfirm",
            body: {
              delivery_ids: resolvedDeliveryIds,
              ...(sourceGroupKey ? { source_group_key: sourceGroupKey } : {}),
              ...(Array.isArray(groupStepIds) && groupStepIds.length > 0 ? { group_step_ids: groupStepIds } : {}),
            },
            actionName: "unconfirm_delivery_group",
            loadingToastContent: {
              errorTitle: "Retrait de protection interrompu",
              title: "Retrait de protection en cours",
              message: `Nous retirons la protection explicite des ${groupCount} commandes du groupe.`,
              successTitle: "Protection retirée",
              successMessage: `La protection a été retirée pour les ${groupCount} commandes du groupe.`,
            },
            statusNoticeContent: {
              eyebrow: "Planning manuel",
              errorTitle: "Retrait de protection interrompu",
              runningMessage: `Retrait de protection groupé en cours pour les ${groupCount} commandes du groupe.`,
              runningStatusLabel: "Traitement en cours",
              runningTitle: "Retrait de protection de groupe en cours",
              successMessage: `La protection a été retirée pour les ${groupCount} commandes du groupe.`,
              successStatusLabel: "Prêt",
              successTitle: "Retrait de protection de groupe terminé",
            },
          });
        },
      });
    },
    toggleDeliveryConfirm({ deliveryId, isConfirmed }) {
      const state = workspaceStore.getState();
      if (!state || !deliveryId || hasPendingPlanningMutation(state, { deliveryId })) {
        return;
      }

      if (!isConfirmed) {
        requestProtectionConfirmation({
          actionName: "confirm",
          content: buildOrderProtectionConfirmationContent("delivery"),
          deliveryId,
          url: `/api/planning/deliveries/${encodeURIComponent(deliveryId)}/confirm`,
        });
        return;
      }

      applyPlanningMutation({
        url: isConfirmed
          ? `/api/planning/deliveries/${encodeURIComponent(deliveryId)}/unconfirm`
          : `/api/planning/deliveries/${encodeURIComponent(deliveryId)}/confirm`,
        deliveryId,
        actionName: isConfirmed ? "unconfirm" : "confirm",
      });
    },
    toggleLoadingConfirm({ isConfirmed, loadingStepId }) {
      const state = workspaceStore.getState();
      if (!state || !loadingStepId || hasPendingPlanningMutation(state, { loadingStepId })) {
        return;
      }

      if (!isConfirmed) {
        requestProtectionConfirmation({
          actionName: "confirm_loading",
          content: buildOrderProtectionConfirmationContent("loading"),
          loadingStepId,
          url: `/api/planning/loadings/${encodeURIComponent(loadingStepId)}/confirm`,
        });
        return;
      }

      applyPlanningMutation({
        url: isConfirmed
          ? `/api/planning/loadings/${encodeURIComponent(loadingStepId)}/unconfirm`
          : `/api/planning/loadings/${encodeURIComponent(loadingStepId)}/confirm`,
        loadingStepId,
        actionName: isConfirmed ? "unconfirm_loading" : "confirm_loading",
      });
    },
    async refreshTruckDataPreserveOrderAuto({
      truckId,
      truckName = "",
      warningId,
      warningVersion,
    } = {}) {
      const state = workspaceStore.getState();
      const safeTruckId = normalizeInlineText(truckId);
      const safeTruckName = normalizeInlineText(truckName) || safeTruckId;
      const safeWarningId = normalizeInlineText(warningId);
      const safeWarningVersion = normalizeInlineText(warningVersion);
      if (
        !state
        || !safeTruckId
        || !safeWarningId
        || !safeWarningVersion
        || isTruckMutationPending(safeTruckId)
      ) {
        return null;
      }

      return applyTruckScopedMutation(safeTruckId, () => applyPlanningMutation({
        url: `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/refresh-data-preserve-order`,
        body: {
          warning_id: safeWarningId,
          warning_version: safeWarningVersion,
        },
        preferredAssignmentId: safeTruckId,
        actionName: "refresh_truck_data_preserve_order_auto",
        loadingToastContent: {
          errorTitle: "Actualisation automatique du camion interrompue",
          title: "Mise à jour automatique du camion",
          message: `Nous actualisons ${safeTruckName} avec Data Manager et les routes, sans modifier son ordre protégé.`,
          successTitle: "Camion mis à jour",
          successMessage: ({ payload }) => payload?.auto_refresh?.status === "already_resolved"
            ? `${safeTruckName} était déjà à jour; le diagnostic a été resynchronisé.`
            : `${safeTruckName} a été actualisé sans modifier son ordre protégé.`,
        },
        resolveServerErrorMessage({ errorMessage }) {
          return normalizeInlineText(errorMessage)
            || `Impossible d'actualiser automatiquement ${safeTruckName} sans modifier son ordre protégé.`;
        },
      }));
    },
    // Bulle du triangle de route : routes HERE seulement, l'ordre du camion ne change pas.
    recalculateTruckRoute({ truckId, truckName = "" } = {}) {
      const safeTruckId = normalizeInlineText(truckId);
      const safeTruckName = normalizeInlineText(truckName) || safeTruckId;
      if (!workspaceStore.getState() || !safeTruckId || isTruckMutationPending(safeTruckId)) {
        return null;
      }

      return applyTruckScopedMutation(safeTruckId, () => applyPlanningMutation({
        url: `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/recalculate-route`,
        actionName: "recalculate_truck_route",
        loadingToastContent: {
          errorTitle: "Route non recalculée",
          title: "Recalcul de la route",
          message: `Nous recalculons les distances HERE de ${safeTruckName}, sans changer son ordre.`,
          successTitle: "Route recalculée",
          successMessage: ({ payload }) => normalizeInlineText(payload?.message)
            || `La route de ${safeTruckName} a été recalculée; l'ordre n'a pas changé.`,
        },
        resolveServerErrorMessage({ errorMessage }) {
          return normalizeInlineText(errorMessage)
            || `Impossible de recalculer la route de ${safeTruckName}.`;
        },
      }));
    },
    optimizeTruckRoutes({ truckId, truckName = "", assignment = null } = {}) {
      const state = workspaceStore.getState();
      const safeTruckId = normalizeInlineText(truckId);
      const safeTruckName = normalizeInlineText(truckName) || safeTruckId;
      if (!state || !safeTruckId || isTruckMutationPending(safeTruckId)) {
        return;
      }

      const resolvedAssignment = assignment && typeof assignment === "object"
        ? assignment
        : state.assignmentsById?.get?.(safeTruckId) || null;
      const content = buildTruckRouteOptimizationConfirmationContent(safeTruckName, {
        assignment: resolvedAssignment,
        resultsData: state.resultsData,
      });

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          void applyTruckScopedMutation(safeTruckId, () => submitTruckRouteOptimization({
            truckId: safeTruckId,
            truckName: safeTruckName,
          }));
        },
      });
    },
    updateTruckStartTime({
      hasOrders = false,
      startTime = "",
      truckId,
      truckName = "",
    } = {}) {
      const state = workspaceStore.getState();
      const safeTruckId = normalizeInlineText(truckId);
      const safeTruckName = normalizeInlineText(truckName) || safeTruckId;
      const currentStartTime = normalizeTruckStartTimeValue(startTime);
      const hasTruckOrders = hasOrders === true;
      if (!state || !safeTruckId || isTruckMutationPending(safeTruckId)) {
        return;
      }

      const content = buildTruckStartTimeConfirmationContent(safeTruckName, {
        currentStartTime,
        hasOrders: hasTruckOrders,
      });

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: (dialogValues = null) => {
          const nextStartTime = normalizeTruckStartTimeValue(
            readPlanningMutationTextPromptValue(dialogValues),
          );
          if (!nextStartTime) {
            return;
          }
          if (nextStartTime === currentStartTime) {
            dismissConfirmation();
            return;
          }

          dismissConfirmation();
          void applyTruckScopedMutation(safeTruckId, async () => {
            const saveLoadingToastContent = {
              errorTitle: "Enregistrement de l'heure de début interrompu",
              title: "Enregistrement de l'heure de début",
              message: `Nous enregistrons l'heure de début de ${safeTruckName} dans Data Manager.`,
              successTitle: "Heure de début enregistrée",
              successMessage: `${safeTruckName} démarre maintenant à ${formatTruckPreloadTimeLabel(nextStartTime)} dans Data Manager.`,
            };
            const chainedLoadingToastId = hasTruckOrders
              ? showToast({
                duration: null,
                message: saveLoadingToastContent.message,
                title: saveLoadingToastContent.title,
                type: "loading",
              })
              : null;
            const synchronization = hasTruckOrders
              ? createSavedTruckSettingsSynchronization(safeTruckId, safeTruckName, chainedLoadingToastId)
              : null;
            let saveResult = null;
            try {
              saveResult = await applyPlanningMutation({
                url: `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/start-time`,
                method: "PATCH",
                body: {
                  dispatch_period: "day",
                  start_time: nextStartTime,
                },
                preferredAssignmentId: safeTruckId,
                actionName: "update_truck_start_time",
                allowMissingResultPayload: hasTruckOrders,
                existingLoadingToastId: chainedLoadingToastId,
                preserveLoadingToastOnSuccess: hasTruckOrders,
                requestHeaders: hasTruckOrders ? { Prefer: "return=minimal" } : null,
                rethrowOnError: true,
                loadingToastContent: saveLoadingToastContent,
                beforeSuccessFeedback: synchronization?.beforeSuccessFeedback,
                onMutationSuccess: hasTruckOrders
                  ? () => {
                    if (synchronization.isCurrent()) rememberTruckDataManagerStartTime(safeTruckId, nextStartTime);
                  }
                  : null,
                statusNoticeContent: hasTruckOrders ? null : {
                  eyebrow: "Data Manager",
                  errorTitle: "Enregistrement de l'heure de début interrompu",
                  runningMessage: `Enregistrement de l'heure de début de ${safeTruckName}.`,
                  runningStatusLabel: "Traitement en cours",
                  runningTitle: "Enregistrement de l'heure de début",
                  successMessage: `L'heure de début de ${safeTruckName} est maintenant ${formatTruckPreloadTimeLabel(nextStartTime)}.`,
                  successStatusLabel: "Prêt",
                  successTitle: `Heure de début de ${safeTruckName} enregistrée`,
                },
                resolveServerErrorMessage({ errorMessage }) {
                  return normalizeInlineText(errorMessage)
                    || `Impossible d'enregistrer l'heure de début de ${safeTruckName} dans Data Manager.`;
                },
              });
            } catch {
              return null;
            }

            if (!saveResult || !hasTruckOrders) {
              if (!saveResult && chainedLoadingToastId) {
                dismissToast(chainedLoadingToastId);
              }
              return saveResult;
            }
            const expectedRuntimeRevision = synchronization.getRevisionForOptimization();
            if (!expectedRuntimeRevision) return saveResult;
            return submitTruckRouteOptimization({
              existingLoadingToastId: chainedLoadingToastId,
              expectedRuntimeRevision,
              requiredStartTime: nextStartTime,
              truckId: safeTruckId,
              truckName: safeTruckName,
            });
          });
        },
        textPrompt: buildTruckStartTimeTextPrompt(currentStartTime),
      });
    },
    updateTruckTimeSettings({
      capacityTons = null,
      dispatchPeriod = "day",
      driverName = "",
      eveningRadiusKm = 40,
      hasOrders = false,
      hoursDisabledReason = "",
      maxWorkHours = null,
      startTime = "",
      trailerName = "",
      trailerSettingsDisabledReason = "",
      truckId,
      truckName = "",
    } = {}) {
      const isEvening = dispatchPeriod === "evening";
      const state = workspaceStore.getState();
      const safeTruckId = normalizeInlineText(truckId);
      const safeTruckName = normalizeInlineText(truckName) || safeTruckId;
      const currentStartTime = normalizeTruckStartTimeValue(startTime);
      const currentMaxWorkHours = normalizeTruckMaxWorkHoursValue(maxWorkHours);
      const currentCapacityTons = normalizeTruckCapacityTonsValue(capacityTons);
      // Colonne Soir du Dispatch Jour : enregistrement seul, le recalcul d'un camion ne vise que la période affichée.
      const isOtherPeriod = dispatchPeriod !== getCurrentDispatchPeriodFromLocation();
      const hasTruckOrders = hasOrders === true && !isOtherPeriod;
      if (!state || !safeTruckId || isTruckMutationPending(safeTruckId)) {
        return;
      }

      const content = buildTruckTimeSettingsConfirmationContent(safeTruckName, {
        hasOrders: hasTruckOrders,
        savesOnly: isOtherPeriod && hasOrders === true,
      });

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: (dialogValues = null) => {
          const textPromptValues = readPlanningMutationTextPromptValues(dialogValues);
          const nextStartTime = normalizeTruckStartTimeValue(
            textPromptValues[TRUCK_TIME_SETTINGS_START_FIELD_KEY]
              ?? textPromptValues[TRUCK_START_TIME_FIELD_KEY]
              ?? "",
          );
          const nextMaxWorkHours = normalizeTruckMaxWorkHoursValue(
            textPromptValues[TRUCK_TIME_SETTINGS_MAX_FIELD_KEY],
          );
          const nextCapacityTons = normalizeTruckCapacityTonsValue(
            textPromptValues[TRUCK_TIME_SETTINGS_CAPACITY_FIELD_KEY],
          );
          const nextRadius = isEvening ? normalizeTruckMaxWorkHoursValue(textPromptValues.eveningRadiusKm) : null;
          const radiusChanged = isEvening && nextRadius !== eveningRadiusKm;
          if (nextMaxWorkHours == null || (isEvening ? nextRadius == null : nextCapacityTons == null)) {
            return;
          }

          const startTimeChanged = Boolean(nextStartTime) && nextStartTime !== currentStartTime;
          const maxWorkHoursChanged = nextMaxWorkHours !== currentMaxWorkHours;
          const capacityTonsChanged = !isEvening && nextCapacityTons !== currentCapacityTons;
          if (!startTimeChanged && !maxWorkHoursChanged && !capacityTonsChanged && !radiusChanged) {
            dismissConfirmation();
            return;
          }

          const changedSettingsBody = {
            dispatch_period: dispatchPeriod,
            ...(radiusChanged ? { evening_radius_km: nextRadius } : {}),
            ...(startTimeChanged ? { start_time: nextStartTime } : {}),
            ...(maxWorkHoursChanged ? { max_work_hours: nextMaxWorkHours } : {}),
            ...(capacityTonsChanged ? { capacity_tons: nextCapacityTons } : {}),
          };
          const nextStartTimeLabel = formatTruckPreloadTimeLabel(nextStartTime);
          const nextMaxWorkHoursLabel = formatTruckMaxWorkHoursLabel(nextMaxWorkHours);
          const nextCapacityTonsLabel = formatTruckCapacityTonsLabel(nextCapacityTons);
          const savedSettingsSummary = isEvening
            ? `${safeTruckName} : maximum ${nextMaxWorkHoursLabel}, rayon ${nextRadius} km au Soir`
            : nextStartTime
            ? `${safeTruckName} démarre à ${nextStartTimeLabel}, maximum de ${nextMaxWorkHoursLabel}, capacité de ${nextCapacityTonsLabel}`
            : `${safeTruckName} a un maximum de ${nextMaxWorkHoursLabel} et une capacité de ${nextCapacityTonsLabel}`;
          // Le serveur précise la fiche qui a reçu chaque valeur (chauffeur, tracteur) quand ce n'est pas la ligne.
          const describeSavedSettings = (suffix) => ({ payload }) => {
            const destination = normalizeInlineText(payload?.settings_destination_message);
            return destination ? `${savedSettingsSummary}. ${destination}` : `${savedSettingsSummary}${suffix}`;
          };
          dismissConfirmation();
          void applyTruckScopedMutation(safeTruckId, async () => {
            const saveLoadingToastContent = {
              errorTitle: "Enregistrement paramètres chauffeur et camion interrompu",
              title: "Enregistrement · paramètres chauffeur et camion",
              message: `Enregistrement des paramètres de ${safeTruckName} dans Data Manager.`,
              successTitle: "Paramètres chauffeur et camion enregistrés",
              successMessage: describeSavedSettings(" dans Data Manager."),
            };
            const chainedLoadingToastId = hasTruckOrders
              ? showToast({
                duration: null,
                message: saveLoadingToastContent.message,
                title: saveLoadingToastContent.title,
                type: "loading",
              })
              : null;
            const synchronization = hasTruckOrders
              ? createSavedTruckSettingsSynchronization(safeTruckId, safeTruckName, chainedLoadingToastId)
              : null;
            let saveResult = null;
            try {
              saveResult = await applyPlanningMutation({
                url: `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/time-settings`,
                method: "PATCH",
                body: changedSettingsBody,
                ...(isOtherPeriod ? { dispatchPeriod } : {}),
                preferredAssignmentId: safeTruckId,
                actionName: "update_truck_time_settings",
                allowMissingResultPayload: hasTruckOrders,
                existingLoadingToastId: chainedLoadingToastId,
                preserveLoadingToastOnSuccess: hasTruckOrders,
                requestHeaders: hasTruckOrders ? { Prefer: "return=minimal" } : null,
                rethrowOnError: true,
                loadingToastContent: saveLoadingToastContent,
                beforeSuccessFeedback: synchronization?.beforeSuccessFeedback,
                onMutationSuccess: () => {
                  if (synchronization && !synchronization.isCurrent()) return;
                  rememberTruckDataManagerTimeSettings(safeTruckId, {
                    eveningRadiusKm: radiusChanged ? nextRadius : null,
                    capacityTons: capacityTonsChanged ? nextCapacityTons : null,
                    maxWorkHours: maxWorkHoursChanged ? nextMaxWorkHours : null,
                    startTime: startTimeChanged ? nextStartTime : "",
                  });
                },
                statusNoticeContent: hasTruckOrders ? null : {
                  eyebrow: "Data Manager",
                  errorTitle: "Enregistrement paramètres chauffeur et camion interrompu",
                  runningMessage: `Enregistrement des paramètres de ${safeTruckName}.`,
                  runningStatusLabel: "Traitement en cours",
                  runningTitle: "Enregistrement · paramètres chauffeur et camion",
                  successMessage: describeSavedSettings("."),
                  successStatusLabel: "Prêt",
                  successTitle: `Paramètres chauffeur et camion de ${safeTruckName} enregistrés`,
                },
                resolveServerErrorMessage({ errorMessage }) {
                  return normalizeInlineText(errorMessage)
                    || `Impossible d'enregistrer les paramètres chauffeur et camion de ${safeTruckName} dans Data Manager.`;
                },
              });
            } catch {
              return null;
            }

            if (!saveResult || !hasTruckOrders) {
              if (!saveResult && chainedLoadingToastId) {
                dismissToast(chainedLoadingToastId);
              }
              return saveResult;
            }
            const destinationMessage = normalizeInlineText(saveResult?.settings_destination_message);
            if (destinationMessage) {
              showToast({ type: "info", title: "Paramètres enregistrés", message: destinationMessage, duration: 8000 });
            }

            const expectedRuntimeRevision = synchronization.getRevisionForOptimization();
            if (!expectedRuntimeRevision) return saveResult;
            return submitTruckRouteOptimization({
              existingLoadingToastId: chainedLoadingToastId,
              expectedRuntimeRevision,
              requiredCapacityTons: capacityTonsChanged ? nextCapacityTons : null,
              requiredMaxWorkHours: (isEvening || maxWorkHoursChanged) ? nextMaxWorkHours : null,
              requiredStartTime: startTimeChanged ? nextStartTime : "",
              truckId: safeTruckId,
              truckName: safeTruckName,
            });
          });
        },
        textPromptFields: isEvening ? [
          { key: TRUCK_TIME_SETTINGS_MAX_FIELD_KEY, label: "Heures max Soir", defaultValue: currentMaxWorkHours, description: currentMaxWorkHours == null ? undefined : formatTruckSettingsSourceValue(formatTruckSettingsSourceLabel(driverName), formatTruckMaxWorkHoursLabel(currentMaxWorkHours)), min: 0.1, positive: true, required: true, step: 0.1, type: "number", ...disabledTextPromptField(hoursDisabledReason) },
          { key: "eveningRadiusKm", label: "Rayon Soir (km)", defaultValue: eveningRadiusKm, description: "À vol d’oiseau depuis le départ, pour le chargement et les livraisons des commandes ajoutées du Jour.", min: 0.1, positive: true, required: true, step: 0.1, type: "number", ...disabledTextPromptField(trailerSettingsDisabledReason) },
        ] : buildTruckTimeSettingsTextPromptFields({
          currentCapacityTons,
          currentMaxWorkHours,
          currentStartTime,
          hoursDisabledReason,
          trailerSettingsDisabledReason,
          driverName,
          trailerName,
        }),
      });
    },
    setTruckPreloadTime({
      clearPreloadTime = false,
      hasPreloadedPreviousDayOrder = false,
      preloadTime = "",
      truckId,
      truckName = "",
    }) {
      const state = workspaceStore.getState();
      const safeTruckId = normalizeInlineText(truckId);
      const safeTruckName = normalizeInlineText(truckName) || safeTruckId;
      const safeCurrentPreloadTime = normalizeTruckPreloadTimeValue(preloadTime);
      const isClearRequest = clearPreloadTime === true;
      if (
        !state
        || !safeTruckId
        || isTruckMutationPending(safeTruckId)
        || (isClearRequest ? !safeCurrentPreloadTime : !hasPreloadedPreviousDayOrder)
      ) {
        return;
      }

      const submitPreloadTime = (nextPreloadTimeValue = null) => {
        const mutationRequest = buildTruckPreloadTimeMutationRequest(safeTruckId, nextPreloadTimeValue);
        const nextPreloadTime = mutationRequest.body.preload_time || "";
        const isClearing = mutationRequest.body.preload_time === null;
        const detailItems = buildTruckPreloadTimeDetailItems(nextPreloadTime);

        void applyTruckScopedMutation(safeTruckId, () => applyPlanningMutation({
          ...mutationRequest,
          loadingToastContent: {
            errorTitle: isClearing ? "Retrait de l'heure de préchargement interrompu" : "Enregistrement de l'heure de préchargement interrompu",
            title: isClearing ? "Suppression de l'heure preload" : "Enregistrement de l'heure preload",
            message: isClearing
              ? `Nous retirons l'heure manuelle de préchargement de ${safeTruckName} pour le dispatch courant.`
              : `Nous enregistrons l'heure manuelle de préchargement de ${safeTruckName} pour le dispatch courant.`,
            successTitle: isClearing ? "Heure preload retirée" : "Heure preload enregistrée",
            successMessage: isClearing
              ? `L'heure manuelle de préchargement de ${safeTruckName} a été retirée.`
              : `L'heure manuelle de préchargement de ${safeTruckName} est fixée à ${formatTruckPreloadTimeLabel(nextPreloadTime)}.`,
          },
          statusNoticeContent: {
            eyebrow: "Planning manuel",
            errorDetailItems: detailItems,
            errorTitle: isClearing ? "Retrait de l'heure de préchargement interrompu" : "Enregistrement de l'heure de préchargement interrompu",
            runningMessage: isClearing
              ? `Suppression en cours de l'heure manuelle de préchargement de ${safeTruckName}.`
              : `Enregistrement en cours de l'heure manuelle de préchargement de ${safeTruckName}.`,
            runningStatusLabel: "Traitement en cours",
            runningTitle: isClearing ? "Suppression de l'heure preload" : "Enregistrement de l'heure preload",
            successDetailItems: detailItems,
            successMessage: isClearing
              ? `L'heure manuelle de préchargement de ${safeTruckName} a été retirée.`
              : `L'heure manuelle de préchargement de ${safeTruckName} est maintenant ${formatTruckPreloadTimeLabel(nextPreloadTime)}.`,
            successStatusLabel: "Prêt",
            successTitle: isClearing
              ? `Heure de préchargement de ${safeTruckName} retirée`
              : `Heure de préchargement de ${safeTruckName} enregistrée`,
          },
          resolveServerErrorMessage({ errorMessage }) {
            return normalizeInlineText(errorMessage)
              || (isClearing
                ? `Impossible de retirer l'heure de préchargement de ${safeTruckName}.`
                : `Impossible d'enregistrer l'heure de préchargement de ${safeTruckName}.`);
          },
        }));
      };

      if (isClearRequest) {
        const content = buildClearTruckPreloadTimeConfirmationContent(safeTruckName, safeCurrentPreloadTime);
        setConfirmationDialog(createTruckPreloadTimeClearConfirmationDialog({
          content,
          dismissConfirmation,
          submitPreloadTime,
        }));
        return;
      }

      const content = buildTruckPreloadTimeConfirmationContent(safeTruckName, {
        isEligible: true,
        preloadTime: safeCurrentPreloadTime,
      });

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: (dialogValues = null) => {
          const nextPreloadTime = normalizeTruckPreloadTimeValue(readPlanningMutationTextPromptValue(dialogValues));

          if (nextPreloadTime === safeCurrentPreloadTime) {
            dismissConfirmation();
            return;
          }

          dismissConfirmation();
          submitPreloadTime(nextPreloadTime);
        },
        textPrompt: buildTruckPreloadTimeTextPrompt(safeCurrentPreloadTime),
      });
    },
    createTruckIncident({
      description = "",
      dispatchDate = "",
      dispatchPeriod = "day",
      endTime = "",
      expectedRuntimeRevision = "",
      startTime = "",
      truckId,
      truckName = "",
    } = {}) {
      const safeTruckId = normalizeInlineText(truckId);
      const safeTruckName = normalizeInlineText(truckName) || safeTruckId;
      const safeDispatchDate = normalizeInlineText(dispatchDate) || getCurrentDispatchDateFromLocation();
      const resolvedDispatchPeriod = normalizePlanningDispatchPeriod(dispatchPeriod);
      if (!safeTruckId || !safeDispatchDate || isTruckMutationPending(safeTruckId)) {
        return null;
      }

      return applyTruckScopedMutation(safeTruckId, () => applyPlanningMutation({
        url: buildTruckIncidentMutationUrl({
          dispatchDate: safeDispatchDate,
          dispatchPeriod: resolvedDispatchPeriod,
        }),
        method: "POST",
        body: {
          description: normalizeInlineText(description),
          end_time: normalizeInlineText(endTime),
          start_time: normalizeInlineText(startTime),
          truck_id: safeTruckId,
        },
        actionName: "create_truck_incident",
        dispatchPeriod: resolvedDispatchPeriod,
        expectedRuntimeRevision,
        preferredAssignmentId: safeTruckId,
        rethrowOnError: true,
        loadingToastContent: {
          errorTitle: "Ajout du bris interrompu",
          title: "Ajout du bris ou contretemps",
          message: `Nous ajoutons la plage d'incident de ${safeTruckName}.`,
          successTitle: "Incident ajouté",
          successMessage: ({ payload }) => withSuspendedOrdersNote(`Le bris mécanique ou contretemps de ${safeTruckName} a été ajouté.`, payload),
        },
        resolveServerErrorMessage({ errorMessage }) {
          return normalizeInlineText(errorMessage)
            || `Impossible d'ajouter le bris mécanique ou contretemps de ${safeTruckName}.`;
        },
      }));
    },
    updateTruckIncident({
      description = "",
      dispatchDate = "",
      dispatchPeriod = "day",
      endTime = "",
      expectedRuntimeRevision = "",
      incidentId = "",
      startTime = "",
      truckId,
      truckName = "",
    } = {}) {
      const safeIncidentId = normalizeInlineText(incidentId);
      const safeTruckId = normalizeInlineText(truckId);
      const safeTruckName = normalizeInlineText(truckName) || safeTruckId;
      const safeDispatchDate = normalizeInlineText(dispatchDate) || getCurrentDispatchDateFromLocation();
      const resolvedDispatchPeriod = normalizePlanningDispatchPeriod(dispatchPeriod);
      if (!safeIncidentId || !safeTruckId || !safeDispatchDate || isTruckMutationPending(safeTruckId)) {
        return null;
      }

      return applyTruckScopedMutation(safeTruckId, () => applyPlanningMutation({
        url: buildTruckIncidentMutationUrl({
          dispatchDate: safeDispatchDate,
          dispatchPeriod: resolvedDispatchPeriod,
          incidentId: safeIncidentId,
        }),
        method: "PATCH",
        body: {
          description: normalizeInlineText(description),
          end_time: normalizeInlineText(endTime),
          start_time: normalizeInlineText(startTime),
        },
        actionName: "update_truck_incident",
        dispatchPeriod: resolvedDispatchPeriod,
        expectedRuntimeRevision,
        preferredAssignmentId: safeTruckId,
        rethrowOnError: true,
        loadingToastContent: {
          errorTitle: "Modification du bris interrompue",
          title: "Modification du bris ou contretemps",
          message: `Nous mettons à jour la plage d'incident de ${safeTruckName}.`,
          successTitle: "Incident modifié",
          successMessage: ({ payload }) => withSuspendedOrdersNote(`Le bris mécanique ou contretemps de ${safeTruckName} a été modifié.`, payload),
        },
        resolveServerErrorMessage({ errorMessage }) {
          return normalizeInlineText(errorMessage)
            || `Impossible de modifier le bris mécanique ou contretemps de ${safeTruckName}.`;
        },
      }));
    },
    deleteTruckIncident({
      dispatchDate = "",
      dispatchPeriod = "day",
      expectedRuntimeRevision = "",
      incidentId = "",
      truckId,
      truckName = "",
    } = {}) {
      const safeIncidentId = normalizeInlineText(incidentId);
      const safeTruckId = normalizeInlineText(truckId);
      const safeTruckName = normalizeInlineText(truckName) || safeTruckId;
      const safeDispatchDate = normalizeInlineText(dispatchDate) || getCurrentDispatchDateFromLocation();
      const resolvedDispatchPeriod = normalizePlanningDispatchPeriod(dispatchPeriod);
      if (!safeIncidentId || !safeTruckId || !safeDispatchDate || isTruckMutationPending(safeTruckId)) {
        return null;
      }

      return applyTruckScopedMutation(safeTruckId, () => applyPlanningMutation({
        url: buildTruckIncidentMutationUrl({
          dispatchDate: safeDispatchDate,
          dispatchPeriod: resolvedDispatchPeriod,
          incidentId: safeIncidentId,
        }),
        method: "DELETE",
        body: {},
        actionName: "delete_truck_incident",
        dispatchPeriod: resolvedDispatchPeriod,
        expectedRuntimeRevision,
        preferredAssignmentId: safeTruckId,
        rethrowOnError: true,
        loadingToastContent: {
          errorTitle: "Suppression du bris interrompue",
          title: "Suppression du bris ou contretemps",
          message: `Nous retirons la plage d'incident de ${safeTruckName}.`,
          successTitle: "Incident supprimé",
          successMessage: `Le bris mécanique ou contretemps de ${safeTruckName} a été supprimé.`,
        },
        resolveServerErrorMessage({ errorMessage }) {
          return normalizeInlineText(errorMessage)
            || `Impossible de supprimer le bris mécanique ou contretemps de ${safeTruckName}.`;
        },
      }));
    },
    updateTruckDriverName({ dispatchPeriod = "day", employeeId = null, expectedAssignmentVersion, expectedRuntimeRevision = "", truckId, truckName = "" } = {}) {
      const safeTruckId = normalizeInlineText(truckId);
      if (!workspaceStore.getState() || !safeTruckId || isTruckMutationPending(safeTruckId)) return null;
      const period = normalizePlanningDispatchPeriod(dispatchPeriod);
      const dispatchDate = getCurrentDispatchDateFromLocation();
      const submit = (body) => applyTruckScopedMutation(safeTruckId, () => applyPlanningMutation({
        url: `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/driver-assignment`, method: "PATCH", body,
        dispatchPeriod: period, expectedRuntimeRevision: body.expected_runtime_revision || "",
        preferredAssignmentId: safeTruckId, rethrowOnError: true,
        actionName: "update_truck_driver_name", allowMissingResultPayload: true, refreshResultsAfterMissingPayload: true,
        handOffLoadingToast: true,
        loadingToastContent: { title: "Actualisation chauffeur", message: `${truckName || safeTruckId} — Actualisation en cours.`,
          successTitle: "Actualisation chauffeur", successMessage: `${truckName || safeTruckId} — Actualisation terminée.`, errorTitle: "Affectation interrompue" },
        resolveServerErrorMessage({ payload, errorMessage }) {
          return payload?.detail?.message || (Array.isArray(payload?.detail)
            ? "L'affectation n'a pas pu être validée. Rechargez le Dispatch."
            : errorMessage);
        },
        // Tournée commencée : confirmer avant de changer le chauffeur ; les départs déjà faits restent au précédent.
        onMutationChallenge({ payload, response, requestBody }) {
          const challenge = driverTourStartedChallenge(payload, response?.status);
          if (!challenge) return false;
          let submitted = false;
          setConfirmationDialog({ ...driverTourStartedConfirmation(challenge), isOpen: true,
            onConfirm: () => {
              if (submitted) return;
              submitted = true;
              dismissConfirmation();
              void submit({ ...requestBody, confirm_started_tour: true }).catch(() => {});
            },
            onCancel: dismissConfirmation, onClose: dismissConfirmation, onDismiss: dismissConfirmation });
          return true;
        },
      }));
      return submit({ employee_id: employeeId, expected_assignment_version: expectedAssignmentVersion,
        ...(expectedRuntimeRevision ? { expected_runtime_revision: expectedRuntimeRevision } : {}),
        dispatch_date: dispatchDate, dispatch_period: period });
    },
    updateTruckTractor({ dispatchPeriod = "day", expectedRuntimeRevision = "", expectedVersion, holderTruckId = "", mode = "tractor",
      tractorTruckId = null, truckId, truckName = "" } = {}) {
      const safeTruckId = normalizeInlineText(truckId);
      // « Changer de remorque » : le camion de la remorque du menu (holder) part vers la remorque choisie (truckId).
      const trailerMode = mode === "trailer";
      const safeHolderId = trailerMode ? normalizeInlineText(holderTruckId) : "";
      if (!workspaceStore.getState() || !safeTruckId || isTruckMutationPending(safeTruckId)
        || (trailerMode && (!safeHolderId || isTruckMutationPending(safeHolderId)))) return null;
      const period = normalizePlanningDispatchPeriod(dispatchPeriod);
      const dispatchDate = getCurrentDispatchDateFromLocation();
      const originPath = window.location.pathname;
      // Le rail « Afficher le soir » vise le Soir depuis la page Jour : comparer à la période affichée au départ.
      const originPeriod = getCurrentDispatchPeriodFromLocation();
      const isCurrent = () => isMountedRef.current && getCurrentDispatchDateFromLocation() === dispatchDate
        && getCurrentDispatchPeriodFromLocation() === originPeriod && window.location.pathname === originPath;
      const menuTruckId = trailerMode ? safeHolderId : safeTruckId;
      const toastContent = trailerMode
        ? { title: "Actualisation de la remorque", message: `${truckName || safeHolderId} — Actualisation en cours.`, successTitle: "Actualisation de la remorque", errorTitle: "Changement de remorque interrompu" }
        : { title: "Actualisation camion", message: `${truckName || safeTruckId} — Actualisation en cours.`, successTitle: "Actualisation camion", errorTitle: "Changement de camion interrompu" };
      const submit = (body) => applyTruckScopedMutation(menuTruckId, () => applyPlanningMutation({
        url: `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/tractor-assignment`, method: "PATCH", body,
        dispatchPeriod: period, expectedRuntimeRevision: body.expected_runtime_revision || "",
        preferredAssignmentId: menuTruckId, rethrowOnError: true,
        actionName: "update_truck_tractor", allowMissingResultPayload: true, refreshResultsAfterMissingPayload: true,
        handOffLoadingToast: true, loadingToastContent: toastContent,
        resolveServerErrorMessage({ payload, errorMessage }) {
          return payload?.detail?.message || errorMessage;
        },
        // Camion déjà attelé : « Échanger » par défaut (chacun avec son chauffeur), ou laisser l'autre remorque sans camion.
        onMutationChallenge({ payload, response, requestBody }) {
          const challenge = tractorAssignmentChallenge(payload, response?.status);
          if (!challenge) return false;
          let submitted = false;
          setConfirmationDialog({ ...(trailerMode ? trailerChangeConfirmation(challenge) : tractorAssignmentConfirmation(challenge)), isOpen: true,
            onConfirm: (dialogValues) => {
              if (submitted) return;
              submitted = true;
              dismissConfirmation();
              if (!isCurrent()) {
                showToast({ title: toastContent.errorTitle, message: "Le contexte a changé. Recommencez dans le Dispatch souhaité.", type: "warning" });
                return;
              }
              // Une seule case : la fenêtre transmet sa valeur directement (repli sur la case principale).
              void submit(confirmedTractorAssignment(requestBody, challenge,
                readPlanningMutationCheckboxValue(dialogValues, "leave_missing", { usePrimaryFallback: true }))).catch(() => {});
            },
            onCancel: dismissConfirmation, onClose: dismissConfirmation, onDismiss: dismissConfirmation });
          return true;
        },
      }));
      return submit({ tractor_truck_id: tractorTruckId || null, expected_version: expectedVersion,
        ...(expectedRuntimeRevision ? { expected_runtime_revision: expectedRuntimeRevision } : {}),
        ...(trailerMode ? { expected_holder_truck_id: safeHolderId } : {}),
        dispatch_date: dispatchDate, dispatch_period: period });
    },
    toggleTruckDispatchDisabled({
      truckId,
      truckName = "",
      isDispatchDisabled = false,
      hasOrders = false,
      isTruckProtected = false,
    }) {
      const state = workspaceStore.getState();
      const safeTruckId = normalizeInlineText(truckId);
      const safeTruckName = normalizeInlineText(truckName) || safeTruckId;
      const hasActiveOrders = hasOrders === true;
      if (!state || !safeTruckId || isTruckMutationPending(safeTruckId)) {
        return;
      }

      const content = buildTruckDispatchDisabledConfirmationContent(safeTruckName, isDispatchDisabled, {
        hasOrders: hasActiveOrders,
        isTruckProtected: isTruckProtected === true,
      });

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          void applyTruckScopedMutation(safeTruckId, () => applyPlanningMutation({
            url: isDispatchDisabled
              ? `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/enable`
              : `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/disable`,
            actionName: isDispatchDisabled ? "enable_truck_dispatch" : "disable_truck_dispatch",
            loadingToastContent: {
              errorTitle: isDispatchDisabled ? "Réactivation du camion interrompue" : "Désactivation du camion interrompue",
              title: isDispatchDisabled ? "Réactivation dispatch en cours" : "Désactivation dispatch en cours",
              message: isDispatchDisabled
                ? `Nous réactivons ${safeTruckName} dans le dispatch courant. Aucune commande n'est déplacée immédiatement.`
                : hasActiveOrders
                  ? "Ses commandes vont dans Suspendues."
                  : `Nous sortons ${safeTruckName} du dispatch courant sans toucher à la fiche maître camion.`,
              successTitle: isDispatchDisabled ? "Camion réactivé" : "Camion désactivé",
              successMessage: isDispatchDisabled
                ? `${safeTruckName} est de nouveau disponible dans ce dispatch.`
                : hasActiveOrders
                  ? `${safeTruckName} est désactivé. Ses commandes sont dans Suspendues.`
                  : `${safeTruckName} est maintenant désactivé pour ce dispatch.`,
            },
            statusNoticeContent: {
              eyebrow: "Planning manuel",
              errorTitle: isDispatchDisabled ? "Réactivation du camion interrompue" : "Désactivation du camion interrompue",
              runningMessage: isDispatchDisabled
                ? `Réactivation dispatch en cours de ${safeTruckName}. Le camion redeviendra disponible sans déplacer de commande immédiatement.`
                : hasActiveOrders
                  ? `Désactivation de ${safeTruckName}. Ses commandes vont dans Suspendues.`
                  : `Désactivation dispatch en cours de ${safeTruckName}. Le camion restera visible mais bloqué pour ce dispatch.`,
              runningStatusLabel: "Traitement en cours",
              runningTitle: isDispatchDisabled ? "Réactivation dispatch en cours" : "Désactivation dispatch en cours",
              successMessage: isDispatchDisabled
                ? `${safeTruckName} peut de nouveau servir de cible ou de support dans le dispatch courant.`
                : hasActiveOrders
                  ? `${safeTruckName} est hors dispatch. Ses commandes sont dans Suspendues.`
                  : `${safeTruckName} est maintenant hors dispatch pour la journée courante.`,
              successStatusLabel: "Prêt",
              successTitle: isDispatchDisabled
                ? `Réactivation dispatch de ${safeTruckName} terminée`
                : `Désactivation dispatch de ${safeTruckName} terminée`,
            },
            resolveServerErrorMessage({ errorMessage }) {
              return normalizeInlineText(errorMessage)
                || (isDispatchDisabled
                  ? `Impossible de réactiver ${safeTruckName} dans ce dispatch.`
                  : `Impossible de désactiver ${safeTruckName} pour ce dispatch.`);
            },
          }));
        },
      });
    },
    toggleTruckProtection({ truckId, truckName = "", isProtected = false }) {
      const state = workspaceStore.getState();
      const safeTruckId = normalizeInlineText(truckId);
      const safeTruckName = normalizeInlineText(truckName) || safeTruckId;
      if (!state || !safeTruckId || isTruckMutationPending(safeTruckId)) {
        return;
      }

      const content = buildTruckProtectionConfirmationContent(safeTruckName, isProtected);

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          void applyTruckScopedMutation(safeTruckId, () => applyPlanningMutation({
            url: isProtected
              ? `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/unprotect`
              : `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/protect`,
            actionName: isProtected ? "unprotect_truck" : "protect_truck",
            loadingToastContent: {
              errorTitle: isProtected ? "Déprotection interrompue" : "Protection interrompue",
              title: isProtected ? "Déprotection camion en cours" : "Protection camion en cours",
              message: isProtected
                ? `Nous retirons le gel manuel de ${safeTruckName} sans modifier les protections commandes déjà posées séparément.`
                : `Nous figeons ${safeTruckName} pour empêcher Réaffecter et Optimiser de déplacer son plan courant, sans ajouter le symbole de protection sur ses commandes actuelles.`,
              successTitle: isProtected ? "Camion déprotégé" : "Camion protégé",
              successMessage: isProtected
                ? `${safeTruckName} peut de nouveau participer aux mutations et optimisations.`
                : `${safeTruckName} est maintenant figé pour Réaffecter et Optimiser.`,
            },
            statusNoticeContent: {
              eyebrow: "Planning manuel",
              errorTitle: isProtected ? "Déprotection interrompue" : "Protection interrompue",
              runningMessage: isProtected
                ? `${safeTruckName} redevient disponible pour les prochaines mutations locales et optimisations.`
                : `${safeTruckName} est en cours de gel. Ses commandes actuelles resteront attachées à ce camion sans passer en statut protégé.`,
              runningStatusLabel: "Traitement en cours",
              runningTitle: isProtected ? "Déprotection camion en cours" : "Protection camion en cours",
              successMessage: isProtected
                ? `${safeTruckName} est de nouveau disponible pour Réaffecter et Optimiser.`
                : `${safeTruckName} restera figé jusqu'à retrait explicite de cette protection.`,
              successStatusLabel: "Prêt",
              successTitle: isProtected ? `Déprotection de ${safeTruckName} terminée` : `Protection de ${safeTruckName} terminée`,
            },
          }));
        },
      });
    },
    reassignTruckOrders({ sourceTruckId, sourceTruckName = "", targetTruckId = "", targetTruckName = "" }) {
      const state = workspaceStore.getState();
      const capturedProtectAfterReassignment = resolvedProtectAfterReassignment;
      const safeSourceTruckId = normalizeInlineText(sourceTruckId);
      const safeTargetTruckId = normalizeInlineText(targetTruckId);
      const safeSourceTruckName = normalizeInlineText(sourceTruckName) || safeSourceTruckId;
      const safeTargetTruckName = normalizeInlineText(targetTruckName) || safeTargetTruckId;
      if (
        !state
        || !safeSourceTruckId
        || !safeTargetTruckId
        || safeSourceTruckId === safeTargetTruckId
        || isTruckMutationPending(safeSourceTruckId)
        || isTruckMutationPending(safeTargetTruckId)
      ) {
        return;
      }

      const content = buildTruckBulkReassignmentConfirmationContent(safeSourceTruckName, safeTargetTruckName);
      const detailItems = [
        { key: "bulk-reassignment-source", label: "Source", value: safeSourceTruckName },
        { key: "bulk-reassignment-target", label: "Cible", value: safeTargetTruckName },
      ];

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          void applyTruckScopedMutation([safeSourceTruckId, safeTargetTruckId], () => applyPlanningMutation({
            url: `/api/planning/trucks/${encodeURIComponent(safeSourceTruckId)}/reassign-all`,
            body: buildTruckBulkReassignmentRequestBody({
              protectAfterReassignment: capturedProtectAfterReassignment,
              targetTruckId: safeTargetTruckId,
            }),
            actionName: "reassign_truck_orders",
            loadingToastContent: {
              errorTitle: "Réaffectation groupée interrompue",
              title: "Réaffectation groupée en cours",
              message: `Nous transférons toutes les commandes de ${safeSourceTruckName} vers ${safeTargetTruckName} avec validation finale tout ou rien.`,
              successTitle: "Réaffectation groupée terminée",
              successMessage: `Toutes les commandes de ${safeSourceTruckName} sont maintenant réaffectées vers ${safeTargetTruckName}.`,
            },
            statusNoticeContent: {
              eyebrow: "Planning manuel",
              errorDetailItems: detailItems,
              errorTitle: "Réaffectation groupée interrompue",
              runningMessage: `Réaffectation groupée en cours de ${safeSourceTruckName} vers ${safeTargetTruckName}. L'opération échouera entièrement si une seule commande ne peut pas être placée sur la cible.`,
              runningStatusLabel: "Traitement en cours",
              runningTitle: "Réaffectation groupée en cours",
              successDetailItems: detailItems,
              successMessage: `Toutes les commandes de ${safeSourceTruckName} ont été transférées vers ${safeTargetTruckName}.`,
              successStatusLabel: "Prêt",
              successTitle: `Réaffectation groupée de ${safeSourceTruckName} terminée`,
            },
          }));
        },
      });
    },
    toggleTruckOrdersProtection({ truckId, truckName = "", areAllOrdersProtected = false }) {
      const state = workspaceStore.getState();
      const safeTruckId = normalizeInlineText(truckId);
      const safeTruckName = normalizeInlineText(truckName) || safeTruckId;
      if (!state || !safeTruckId || isTruckMutationPending(safeTruckId)) {
        return;
      }

      const content = buildTruckOrderProtectionConfirmationContent(safeTruckName, areAllOrdersProtected);

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          void applyTruckScopedMutation(safeTruckId, () => applyPlanningMutation({
            url: areAllOrdersProtected
              ? `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/orders/unprotect`
              : `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/orders/protect`,
            actionName: areAllOrdersProtected ? "unprotect_truck_orders" : "protect_truck_orders",
            loadingToastContent: {
              errorTitle: areAllOrdersProtected ? "Déprotection interrompue" : "Protection interrompue",
              title: areAllOrdersProtected ? "Déprotection des commandes en cours" : "Protection des commandes en cours",
              message: areAllOrdersProtected
                ? `Nous retirons la protection des commandes actuellement affectées à ${safeTruckName}.`
                : `Nous protégeons toutes les commandes actuellement affectées à ${safeTruckName}.`,
              successTitle: areAllOrdersProtected ? "Commandes déprotégées" : "Commandes protégées",
              successMessage: areAllOrdersProtected
                ? `Les commandes de ${safeTruckName} sont de nouveau optimisables.`
                : `Les commandes de ${safeTruckName} sont maintenant protégées.`,
            },
            statusNoticeContent: {
              eyebrow: "Planning manuel",
              errorTitle: areAllOrdersProtected ? "Déprotection interrompue" : "Protection interrompue",
              runningMessage: areAllOrdersProtected
                ? `Déprotection en cours des commandes actuellement présentes sur ${safeTruckName}.`
                : `Protection en cours des commandes actuellement présentes sur ${safeTruckName}.`,
              runningStatusLabel: "Traitement en cours",
              runningTitle: areAllOrdersProtected ? "Déprotection des commandes en cours" : "Protection des commandes en cours",
              successMessage: areAllOrdersProtected
                ? `Les commandes actuellement sur ${safeTruckName} peuvent de nouveau être déplacées.`
                : `Les commandes actuellement sur ${safeTruckName} resteront protégées jusqu'à retrait explicite.`,
              successStatusLabel: "Prêt",
              successTitle: areAllOrdersProtected
                ? `Déprotection des commandes de ${safeTruckName} terminée`
                : `Protection des commandes de ${safeTruckName} terminée`,
            },
          }));
        },
      });
    },
    toggleTruckOvertime({ truckId, truckName = "", isAuthorized = false }) {
      const state = workspaceStore.getState();
      const safeTruckId = normalizeInlineText(truckId);
      const safeTruckName = normalizeInlineText(truckName) || safeTruckId;
      if (!state || !safeTruckId || isTruckMutationPending(safeTruckId)) {
        return;
      }

      const content = buildTruckOvertimeConfirmationContent(safeTruckName, isAuthorized);

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          void applyTruckScopedMutation(safeTruckId, () => applyPlanningMutation({
            url: isAuthorized
              ? `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/overtime/disable`
              : `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/overtime/enable`,
            actionName: isAuthorized ? "disable_truck_overtime" : "enable_truck_overtime",
            loadingToastContent: {
              errorTitle: isAuthorized ? "Fermeture du temps supplémentaire interrompue" : "Ouverture du temps supplémentaire interrompue",
              title: isAuthorized ? "Fermeture du temps supplémentaire" : "Ouverture du temps supplémentaire",
              message: isAuthorized
                ? `Nous refermons le temps supplémentaire de ${safeTruckName} et recalculons ses commandes libres si nécessaire.`
                : `Nous autorisons ${safeTruckName} à dépasser son nombre d'heures maximal lors des prochaines réaffectations.`,
              successTitle: isAuthorized ? "Temps supplémentaire refermé" : "Temps supplémentaire ouvert",
              successMessage: isAuthorized
                ? `Le temps supplémentaire de ${safeTruckName} est refermé. Les commandes libres ont été recalculées si nécessaire.`
                : `${safeTruckName} peut maintenant dépasser son nombre d'heures maximal lors des prochaines réaffectations.`,
            },
            statusNoticeContent: {
              eyebrow: "Planning manuel",
              errorTitle: isAuthorized ? "Fermeture du temps supplémentaire interrompue" : "Ouverture du temps supplémentaire interrompue",
              runningMessage: isAuthorized
                ? `Fermeture en cours du temps supplémentaire de ${safeTruckName}. Les commandes libres pourront être redistribuées par le recalcul.`
                : `Ouverture en cours du temps supplémentaire de ${safeTruckName}. Aucun déplacement de commande n'est lancé immédiatement.`,
              runningStatusLabel: "Traitement en cours",
              runningTitle: isAuthorized ? "Fermeture du temps supplémentaire" : "Ouverture du temps supplémentaire",
              successMessage: isAuthorized
                ? `Le temps supplémentaire de ${safeTruckName} est refermé. Les commandes libres ont été recalculées si nécessaire.`
                : `${safeTruckName} peut dépasser ses heures max. Le temps supplémentaire se referme seul quand le camion repasse sous sa limite.`,
              successStatusLabel: "Prêt",
              successTitle: isAuthorized
                ? `Temps supplémentaire de ${safeTruckName} refermé`
                : `Temps supplémentaire de ${safeTruckName} ouvert`,
            },
            resolveServerErrorMessage({ errorMessage }) {
              return normalizeInlineText(errorMessage)
                || (isAuthorized
                  ? `Impossible de refermer le temps supplémentaire de ${safeTruckName}.`
                  : `Impossible d'ouvrir le temps supplémentaire de ${safeTruckName}.`);
            },
          }));
        },
        warningMessage: isAuthorized
          ? "Le camion sera ramené sous ses heures max. Des commandes pourront être retirées du camion et redistribuées par le recalcul, sauf les protégées, confirmées, gelées ou réaffectées."
          : content.warningMessage,
      });
    },
    toggleTruckDompeurMixedLoad({
      truckId,
      truckName = "",
      isAuthorized = false,
      isDompeurTruck = false,
    }) {
      const state = workspaceStore.getState();
      const safeTruckId = normalizeInlineText(truckId);
      const safeTruckName = normalizeInlineText(truckName) || safeTruckId;
      if (!state || !safeTruckId || isTruckMutationPending(safeTruckId)) {
        return;
      }
      if (!isAuthorized && !isDompeurTruck) {
        return;
      }

      const content = buildTruckDompeurMixedLoadConfirmationContent(safeTruckName, isAuthorized, {
        isDompeurTruck,
      });

      setConfirmationDialog({
        ...content,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: () => {
          dismissConfirmation();
          void applyTruckScopedMutation(safeTruckId, () => applyPlanningMutation({
            url: isAuthorized
              ? `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/dompeur-mixed-load/disable`
              : `/api/planning/trucks/${encodeURIComponent(safeTruckId)}/dompeur-mixed-load/enable`,
            actionName: isAuthorized ? "disable_truck_dompeur_mixed_load" : "enable_truck_dompeur_mixed_load",
            preferredAssignmentId: safeTruckId,
            loadingToastContent: {
              errorTitle: isAuthorized ? "Désactivation du regroupement Dompeur interrompue" : "Activation du regroupement Dompeur interrompue",
              title: isAuthorized ? "Désactivation du regroupement Dompeur" : "Activation du regroupement Dompeur",
              message: isAuthorized
                ? `Nous désactivons le regroupement Dompeur de ${safeTruckName} et recalculons ce camion.`
                : `Nous autorisons ${safeTruckName} à regrouper les commandes compatibles et recalculons ce camion.`,
              successTitle: isAuthorized ? "Regroupement désactivé" : "Regroupement autorisé",
              successMessage: isAuthorized
                ? `${safeTruckName} reprend le comportement standard; les regroupements compatibles déjà présents sont séparés.`
                : `${safeTruckName} peut maintenant regrouper les commandes compatibles déjà présentes ou réaffectées manuellement.`,
            },
            statusNoticeContent: {
              eyebrow: "Planning manuel",
              errorTitle: isAuthorized ? "Désactivation du regroupement Dompeur interrompue" : "Activation du regroupement Dompeur interrompue",
              runningMessage: isAuthorized
                ? `Désactivation du regroupement Dompeur de ${safeTruckName} et reconstruction du camion courant.`
                : `Activation du regroupement Dompeur de ${safeTruckName} et reconstruction du camion courant.`,
              runningStatusLabel: "Traitement en cours",
              runningTitle: isAuthorized ? "Désactivation du regroupement Dompeur" : "Activation du regroupement Dompeur",
              successMessage: isAuthorized
                ? `${safeTruckName} est revenu au comportement standard avec livraisons séparées.`
                : `${safeTruckName} gardera cette autorisation jusqu'à désactivation explicite.`,
              successStatusLabel: "Prêt",
              successTitle: isAuthorized
                ? `Regroupement Dompeur de ${safeTruckName} désactivé`
                : `Regroupement Dompeur de ${safeTruckName} autorisé`,
            },
            resolveServerErrorMessage({ errorMessage }) {
              return normalizeInlineText(errorMessage)
                || (isAuthorized
                  ? `Impossible de désactiver le regroupement Dompeur de ${safeTruckName}.`
                  : `Impossible d'autoriser le regroupement Dompeur de ${safeTruckName}.`);
            },
          }));
        },
      });
    },
    reactivateDelivery(
      deliveryId,
      {
        dispatchPeriod = "day",
        expectedRuntimeRevision = "",
        isUndo = false,
        onMutationError = null,
        onMutationStart = null,
        onMutationSuccess = null,
        orderIds = [],
        rebuildMode = "",
        skipConfirmation = false,
        targetCompatibility = null,
        targetTruckId = "",
        targetTruckName = "",
      } = {},
    ) {
      const state = workspaceStore.getState();
      if (!state || !deliveryId || hasPendingPlanningMutation(state, { deliveryId })) {
        return;
      }
      const capturedRebuildMode = normalizeDispatchReassignmentMode(
        rebuildMode || resolvedReassignmentRebuildMode,
      );
      const capturedProtectAfterReassignment = resolvedProtectAfterReassignment;

      const resolvedTargetTruckId = typeof targetTruckId === "string" ? targetTruckId.trim() : "";
      const resolvedTargetTruckName = typeof targetTruckName === "string" ? targetTruckName.trim() : "";
      const resolvedOrderIds = (Array.isArray(orderIds) ? orderIds : [])
        .map((orderId) => normalizeInlineText(orderId))
        .filter(Boolean)
        .filter((orderId, index, allOrderIds) => allOrderIds.indexOf(orderId) === index);
      const overtimeWarningContext = resolvedTargetTruckId
        ? resolveTargetedAssignmentOvertimeWarningContext(state, resolvedTargetTruckId, targetCompatibility)
        : null;
      const content = buildDeliveryReactivateConfirmationContent(resolvedTargetTruckName, { protect: capturedProtectAfterReassignment !== false });
      const timeWindowRequirement = resolveTimeWindowRequirementState(targetCompatibility);
      const dompeurOverrideRequirement = resolveDompeurOverrideRequirementState(targetCompatibility);
      const checkboxFields = buildPlanningMutationCheckboxFields({
        dompeurOverrideRequirement,
        overtimeWarningContext,
      });
      const timeWindowContent = timeWindowRequirement?.mode === "soft"
        ? buildTimeWindowConfirmationContent(state, {
          confirmLabel: content.confirmLabel,
          deliveryIds: [deliveryId],
          targetCompatibility,
          targetTruckName: resolvedTargetTruckName,
          title: `Réaffecter la commande vers ${resolvedTargetTruckName || "ce camion"} ?`,
        })
        : null;
      const textPrompt = buildPlanningMutationTextPrompt(timeWindowRequirement);
      const summaryItems = [
        ...(dompeurOverrideRequirement ? [{
          label: "Dompeur",
          tone: "warning",
          value: "Override manuel",
        }] : []),
        ...(timeWindowRequirement?.mode === "override_required" ? [{
          label: "Fenêtre",
          tone: "warning",
          value: "Motif requis",
        }] : []),
      ];
      const onlyOvertimeCheckbox = checkboxFields.length === 1 && checkboxFields[0]?.key === OVERTIME_CONFIRM_FIELD_KEY && !textPrompt;
      const shouldAutoApproveOvertime = !timeWindowContent && shouldAutoApproveFastOvertime({
        onlyOvertimeCheckbox,
        overtimeWarningContext,
        rebuildMode: capturedRebuildMode,
      });
      const dompeurIsConfirmCheckbox = checkboxFields[0]?.key === DOMPEUR_CONFIRM_FIELD_KEY;
      const onlyDompeurCheckbox = checkboxFields.length === 1 && dompeurIsConfirmCheckbox && !textPrompt;
      const dialogWarningMessage = [
        dompeurOverrideRequirement?.description,
        overtimeWarningContext?.feedbackMessage
          || (overtimeWarningContext?.shouldShow
            ? "Cette réaffectation pourrait dépasser le nombre d'heures maximal."
            : ""),
      ].filter(Boolean).join(" ") || content.warningMessage;

      const commitDeliveryReactivation = (dialogValues = false) => {
        if (isDeliveryMutationPending(deliveryId)) {
          dismissConfirmation();
          showPendingDeliveryMutationToast();
          return;
        }

        const deliverySubject = resolveDeliveryNoticeSubject(workspaceStore.getState(), deliveryId);
        const shouldAllowOvertime = Boolean(
          overtimeWarningContext?.shouldShow
          && (
            shouldAutoApproveOvertime
            || readPlanningMutationCheckboxValue(dialogValues, OVERTIME_CONFIRM_FIELD_KEY, {
              usePrimaryFallback: onlyOvertimeCheckbox,
            })
          ),
        );
        // « … quand même » vaut accord pour l'écart P2/P3.
        const confirmTimeWindowOverride = timeWindowRequirement?.mode === "soft";
        const confirmDompeurOverride = Boolean(
          dompeurOverrideRequirement
          && readPlanningMutationCheckboxValue(dialogValues, DOMPEUR_CONFIRM_FIELD_KEY, {
            usePrimaryFallback: onlyDompeurCheckbox || dompeurIsConfirmCheckbox,
          }),
        );
        const timeWindowOverrideReason = timeWindowRequirement?.mode === "override_required"
          ? readPlanningMutationTextPromptValue(dialogValues)
          : "";
        const requestedDompeurDetailItems = buildRequestedDompeurOverrideDetailItems({
          confirmDompeurOverride,
          targetCompatibility,
        });
        const requestedTimeWindowDetailItems = buildRequestedTimeWindowDetailItems({
          targetCompatibility,
          timeWindowOverrideReason,
        });
        const mutationBody = buildDeliveryReactivationRequestBody({
          allowOvertime: shouldAllowOvertime,
          confirmDompeurOverride,
          confirmTimeWindowOverride,
          protectAfterReassignment: capturedProtectAfterReassignment,
          rebuildMode: capturedRebuildMode,
          targetTruckId: resolvedTargetTruckId,
          timeWindowOverrideReason,
        });
        const submitDeliveryReactivation = ({
          requestBody,
          requestExpectedRuntimeRevision,
          onMutationChallenge = null,
        }) => applyPlanningMutation({
          url: `/api/planning/deliveries/${encodeURIComponent(deliveryId)}/reactivate`,
          body: requestBody,
          deliveryId,
          dispatchPeriod,
          expectedRuntimeRevision: requestExpectedRuntimeRevision,
          highlightedStepKeys: [
            getDeliveryHighlightKey(deliveryId),
            ...resolvedOrderIds.map((orderId) => getLoadingOrderHighlightKey(orderId)),
          ],
          actionName: "reactivate",
          isUndo,
          loadingToastContent: {
            errorTitle: "Réactivation de la commande interrompue",
            title: "Réactivation en cours",
            message: resolvedTargetTruckName
              ? `Nous retirons la suspension et tentons de replacer la commande directement sur ${resolvedTargetTruckName}.`
              : "Nous retirons la suspension et recalculons la commande dans le planning courant.",
            successTitle: "Réactivation terminée",
            successMessage: resolvedTargetTruckName
              ? `La commande a été réactivée sur ${resolvedTargetTruckName}.`
              : "La commande a été réactivée et recalculée dans le planning courant.",
          },
          onMutationChallenge,
          onMutationError,
          onMutationStart,
          onMutationSuccess,
          statusNoticeContent: {
            eyebrow: "Planning manuel",
            errorDetailItems: [
              ...requestedDompeurDetailItems,
              ...requestedTimeWindowDetailItems,
            ],
            errorTitle: "Réactivation de la commande interrompue",
            runningMessage: resolvedTargetTruckName
              ? `Réactivation ciblée en cours vers ${resolvedTargetTruckName}. Aucune autre commande ne sera déplacée silencieusement.`
              : "Réactivation en cours. La commande est retirée des suspendues puis recalculée dans le planning courant.",
            runningStatusLabel: "Traitement en cours",
            runningTitle: resolvedTargetTruckName ? "Réactivation ciblée en cours" : "Réactivation de commande en cours",
            successDetailItems: [
              ...requestedDompeurDetailItems,
              ...requestedTimeWindowDetailItems,
            ],
            successMessage: resolvedTargetTruckName
              ? (deliverySubject
                ? `La commande ${deliverySubject} a été réactivée sur ${resolvedTargetTruckName}.`
                : `La commande a été réactivée sur ${resolvedTargetTruckName}.`)
              : (deliverySubject
              ? `La commande ${deliverySubject} a été réactivée et recalculée dans le planning courant.`
              : "La commande a été réactivée et recalculée dans le planning courant."),
            successStatusLabel: "Prêt",
            successTitle: appendNoticeSubject(
              resolvedTargetTruckName ? `Réactivation vers ${resolvedTargetTruckName} terminée` : "Réactivation terminée",
              deliverySubject,
            ),
          },
        });
        // Partage de compartiment puis multi-visite : chaque relance garde les deux gestionnaires.
        let handleReactivationChallenge = null;
        const retryDeliveryReactivation = ({ requestBody, requestExpectedRuntimeRevision }) => {
          void submitDeliveryReactivation({
            requestBody,
            requestExpectedRuntimeRevision,
            onMutationChallenge: handleReactivationChallenge,
          });
        };
        const handleCompartmentSharingChallenge = createCompartmentSharingChallengeHandler({
          dismissConfirmation,
          retryMutation: retryDeliveryReactivation,
          setConfirmationDialog,
          targetTruckLabel: resolvedTargetTruckName || resolvedTargetTruckId,
        });
        const handleDeliveryClientRepeatChallenge = createDeliveryClientRepeatMutationChallengeHandler({
          dismissConfirmation,
          expectedDeliveryIds: [deliveryId],
          retryMutation: retryDeliveryReactivation,
          setConfirmationDialog,
          targetTruckId: resolvedTargetTruckId,
          targetTruckLabel: resolvedTargetTruckName || resolvedTargetTruckId,
        });
        handleReactivationChallenge = (challengeContext) => handleCompartmentSharingChallenge(challengeContext)
          || handleDeliveryClientRepeatChallenge(challengeContext);

        dismissConfirmation();
        void submitDeliveryReactivation({
          requestBody: mutationBody,
          requestExpectedRuntimeRevision: expectedRuntimeRevision,
          onMutationChallenge: handleReactivationChallenge,
        });
      };

      if (
        shouldAutoApproveOvertime
        || (skipConfirmation === true && checkboxFields.length === 0 && !textPrompt && !timeWindowContent)
      ) {
        commitDeliveryReactivation();
        return;
      }

      setConfirmationDialog({
        ...content,
        checkboxFields: checkboxFields.slice(1),
        confirmCheckbox: checkboxFields[0] || null,
        isOpen: true,
        onCancel: dismissConfirmation,
        onClose: dismissConfirmation,
        onConfirm: commitDeliveryReactivation,
        summaryItems,
        textPrompt,
        warningMessage: dialogWarningMessage,
        ...timeWindowContent,
      });
    },
  };
}
